export type Language = 'en' | 'fr' | 'ar' | 'es' | 'de' | 'it' | 'pt' | 'ru' | 'zh' | 'ja' | 'nl' | 'tr' | 'ko' | 'el' | 'hi' | 'th' | 'id' | 'pl' | 'sv';

export const SUPPORTED_LANGUAGES: Array<{ code: Language; label: string; nativeLabel: string }> = [
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'fr', label: 'French', nativeLabel: 'Français' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español' },
  { code: 'de', label: 'German', nativeLabel: 'Deutsch' },
  { code: 'it', label: 'Italian', nativeLabel: 'Italiano' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português' },
  { code: 'ru', label: 'Russian', nativeLabel: 'Русский' },
  { code: 'zh', label: 'Chinese', nativeLabel: '中文' },
  { code: 'ja', label: 'Japanese', nativeLabel: '日本語' },
  { code: 'nl', label: 'Dutch', nativeLabel: 'Nederlands' },
  { code: 'tr', label: 'Turkish', nativeLabel: 'Türkçe' },
  { code: 'ko', label: 'Korean', nativeLabel: '한국어' },
  { code: 'el', label: 'Greek', nativeLabel: 'Ελληνικά' },
  { code: 'hi', label: 'Hindi', nativeLabel: 'हिन्दी' },
  { code: 'th', label: 'Thai', nativeLabel: 'ไทย' },
  { code: 'id', label: 'Indonesian', nativeLabel: 'Bahasa Indonesia' },
  { code: 'pl', label: 'Polish', nativeLabel: 'Polski' },
  { code: 'sv', label: 'Swedish', nativeLabel: 'Svenska' },
];

type TranslationDictionary = Partial<Record<Language, Record<string, string>>>;

const guestMenuTranslations: Record<Language, Record<string, string>> = {
  en: {
    'menu.no_items': 'No items found',
    'menu.try_adjusting': 'Try adjusting your search or filters',
    'menu.section_closed_check_back': 'This section is currently closed. Please check back during operating hours or browse our other active menus above.',
    'menu.made_with': 'Made With',
    'menu.rate_item': 'Rate this item',
    'menu.based_reviews': 'Based on {count} guest reviews',
    'menu.add_to_order': 'Add to Order',
    'menu.all': 'All',
    'menu.departments': 'Menu departments',
    'menu.learn_more': 'Learn More',
    'cart.title': 'Your Order',
    'cart.details': 'Delivery Details',
    'cart.confirmed': 'Confirmed!',
    'cart.empty': 'Your cart is empty',
    'cart.continue': 'Continue to Details',
    'cart.back': 'Back',
    'cart.proceed_payment': 'Proceed to Payment',
    'cart.qty': 'Qty',
    'cart.notes_placeholder': 'Customize: no onions, sauce aside, allergy note…',
    'cart.upsell.title': 'Perfect Pairings',
    'cart.upsell.add': 'Add',
    'cart.perfect_match': 'Perfect Match',
    'cart.additional': 'Additional',
    'cart.no_thanks': 'No, Thanks',
    'cart.add_both': 'Add Both',
    'cart.limited_time_offer': 'Limited time offer for this table session',
    'cart.modes.dine_in': 'Dine In',
    'cart.modes.pickup': 'Pickup',
    'cart.modes.delivery': 'Delivery',
    'cart.modes_unavailable': 'Ordering modes are currently unavailable.',
    'cart.form.name': 'Full Name',
    'cart.form.phone': 'Phone Number',
    'cart.form.table': 'Table Number',
    'cart.form.address': 'Delivery Address',
    'cart.form.table_placeholder': 'e.g. 12',
    'cart.delivery_estimate': 'Delivery estimate',
    'cart.busy_estimate': 'busy-time estimate',
    'cart.asap': 'ASAP',
    'cart.schedule': 'Schedule',
    'cart.address_placeholder': 'Street and neighbourhood',
    'cart.apartment': 'Apartment',
    'cart.floor': 'Floor',
    'cart.door_code': 'Door code',
    'cart.map_pin': 'Map pin or location link',
    'cart.delivery_instructions': 'Delivery instructions, allergies, or handoff notes',
    'cart.contactless': 'Leave at the door (contactless)',
    'cart.payment.title': 'Order & Pay',
    'cart.payment.subtitle': 'Secure table-side checkout',
    'cart.checkout_failed': 'Checkout Failed',
    'cart.processing_payment': 'Processing Payment',
    'cart.connecting_gateway': 'Connecting to secure gateway...',
    'cart.payments_unavailable': 'Payments Unavailable',
    'cart.payments_unavailable_desc': 'This venue has not enabled a payment method for guest checkout.',
    'cart.pay_online': 'Pay Online',
    'cart.credit_debit_card': 'Credit or Debit Card',
    'cart.or': 'Or',
    'cart.pay_courier_cash': 'Pay the courier (Cash)',
    'cart.pay_counter_cash': 'Pay at the Counter (Cash)',
    'cart.secure_payment': 'Secure Payment',
    'cart.courier_payment': 'Courier Payment',
    'cart.counter_payment': 'Counter Payment',
    'cart.secure_payment_desc': 'Encrypted card checkout is enabled for this venue.',
    'cart.staff_payment_desc': 'Payment will be collected by staff at the venue.',
    'cart.received.title': 'Order Received!',
    'cart.received.dinein': 'Your items will be brought to your table shortly.',
    'cart.received.pickup': 'We\'ve sent your order to the kitchen. Stay tuned!',
    'cart.received.close': 'Close Window',
    'cart.delivery_confirmed': 'Delivery confirmed for',
    'cart.your_zone': 'your zone',
    'cart.loyalty_earned': 'You earned',
    'cart.loyalty_points': 'Loyalty Points!',
    'cart.how_experience': 'How was your ordering experience?',
    'cart.improve_placeholder': 'What could we improve?',
    'cart.send_feedback': 'Send Private Feedback',
    'cart.google_review': 'Leave a Google Review',
    'cart.thank_feedback': 'Thank you for your feedback!',
    'cart.items_subtotal': 'Items',
    'cart.delivery_fee': 'Delivery',
    'cart.delivery': 'Delivery',
    'cart.no_zone': 'No zone',
    'cart.free': 'Free',
    'cart.packaging': 'Packaging',
    'cart.total_summary': 'Total Summary',
    'tracker.order_received': 'Order Received',
    'tracker.confirming_delivery': 'The venue is confirming your delivery',
    'tracker.waiting_kitchen': 'Waiting for kitchen approval',
    'tracker.chef_cooking': 'Chef is Cooking',
    'tracker.delivery_preparing': 'Your delivery is being prepared',
    'tracker.food_preparing': 'Your food is being prepared',
    'tracker.out_for_delivery': 'Out for Delivery!',
    'tracker.ready_pickup': 'Ready for Pickup!',
    'tracker.courier_has_order': 'Your courier has your order',
    'tracker.come_to_counter': 'Please come to the counter',
    'tracker.delivered': 'Delivered!',
    'tracker.enjoy_meal': 'Enjoy your meal!',
    'tracker.delivery_complete': 'Your delivery is complete',
    'tracker.order_completed': 'Order completed successfully',
    'tracker.updating_status': 'Updating status...',
    'tracker.order_again': 'Order Again',
    'reservation.title': 'Book a Table',
    'reservation.subtitle': 'Join us for an unforgettable experience',
    'reservation.name': 'Full Name',
    'reservation.name_placeholder': 'Your Name',
    'reservation.phone': 'Phone Number',
    'reservation.email': 'Email Address',
    'reservation.guests': 'Guests',
    'reservation.date': 'Date',
    'reservation.time': 'Time',
    'reservation.notes': 'Special Notes',
    'reservation.notes_placeholder': 'Allergies, birthday, window seat preference...',
    'reservation.failed': 'Reservation Failed',
    'reservation.error_generic': 'We could not submit your reservation. Please try again.',
    'reservation.submitting': 'Securing your table...',
    'reservation.confirm': 'Confirm Reservation',
    'reservation.requested': 'Reservation Requested!',
    'reservation.requested_desc': 'We\'ll review your request and send a confirmation to your email shortly.',
    'reservation.done': 'Done',
    'loyalty.title': 'Loyalty Rewards',
    'loyalty.subtitle': 'Earn points on every order',
    'loyalty.welcome_back': 'Welcome Back!',
    'loyalty.welcome_desc': 'Sign in to save points across visits, or check with your phone or email.',
    'loyalty.view': 'View',
    'loyalty.enter_email': 'Enter your email',
    'loyalty.sending_code': 'Sending Code...',
    'loyalty.send_code': 'Send Verification Code',
    'loyalty.enter_code_title': 'Enter Verification Code',
    'loyalty.code_placeholder': '6-digit code',
    'loyalty.verifying': 'Verifying...',
    'loyalty.verify_signin': 'Verify & Sign In',
    'loyalty.back_email': 'Back to Email',
    'loyalty.available_points': 'Available Points',
    'loyalty.saved_to': 'Saved to',
    'loyalty.available_rewards': 'Available Rewards',
    'loyalty.redeem': 'Redeem',
    'loyalty.locked': 'Locked',
    'loyalty.no_rewards': 'No rewards available yet.',
    'loyalty.sign_out': 'Sign Out',
    'loyalty.switch_account': 'Switch Account',
    'footer.live_encryption': 'Live Encryption Active',
    'footer.powered_by': 'Powered by',
    'footer.all_rights': 'All Rights Reserved.',
    'menu.open_now': 'Open Now',
    'menu.closed': 'Closed',
    'menu.delivery_only': 'Delivery Only',
    'menu.book_table': 'Book Table',
    'menu.rewards': 'Rewards',
    'menu.table': 'Table',
    'menu.experience_culinary': 'Experience culinary excellence',
    'menu.search_placeholder': 'Search for items...',
    'menu.dietary_filters': 'Dietary filters',
    'menu.exclude_allergens': 'Exclude allergens',
    'menu.no_allergen': 'No {allergen}',
    'dietary.vegetarian': 'Vegetarian',
    'dietary.vegan': 'Vegan',
    'dietary.spicy': 'Spicy',
    'dietary.gluten-free': 'Gluten-Free',
    'dietary.nut-free': 'Nut-Free',
    'dietary.dairy-free': 'Dairy-Free',
    'dietary.healthy': 'Healthy',
    'dietary.halal': 'Halal',
    'dietary.organic': 'Organic',
    'dietary.keto': 'Keto',
    'allergen.nuts': 'Nuts',
    'allergen.dairy': 'Dairy',
    'allergen.gluten': 'Gluten',
    'allergen.shellfish': 'Shellfish',
    'allergen.soy': 'Soy',
    'allergen.eggs': 'Eggs',
    'allergen.fish': 'Fish',
    'menu.chefs_recommendations': "Chef's Recommendations",
    'menu.chefs': "Chef's",
    'menu.recommendations': 'Recommendations',
    'menu.items': 'Menu items',
    'menu.count': 'Items',
    'menu.section_closed': 'is Closed',
    'menu.dine_in_closed_delivery_available': 'Dine-in service is currently closed. Delivery is available for this section.',
    'menu.section_closed_notice': 'This section is currently closed. Please check back during operating hours or browse our other active menus above.',
    'menu.available': 'AVAILABLE',
    'menu.sold_out': 'SOLD OUT',
    'menu.mins': 'MINS',
    'menu.running_low': 'Running Low',
    'menu.adjusted': '(ADJUSTED)',
    'menu.add_to_cart': 'Add to Cart',
    'menu.visual_composition': 'Visual Composition',
    'menu.small_details': 'Details',
    'menu.guest_sentiment': 'Guest Sentiment',
    'menu.how_was_experience': 'How was your experience?',
    'menu.thank_feedback': 'Thank you for the feedback!',
    'menu.based_on_reviews': 'Based on {count} guest reviews',
    'menu.water': 'Water',
    'menu.bill': 'Bill',
    'menu.cart': 'Cart',
    'menu.checkout': 'Checkout',
    'menu.requested': '{item} Requested',
  },
  es: {
    'menu.no_items': 'No se encontraron artículos',
    'menu.try_adjusting': 'Intenta ajustar tu búsqueda o filtros',
    'menu.section_closed_check_back': 'Esta sección está cerrada actualmente. Vuelve a consultar durante el horario de atención.',
    'menu.made_with': 'Elaborado con',
    'menu.rate_item': 'Valorar este artículo',
    'menu.based_reviews': 'Basado en {count} opiniones',
    'menu.add_to_order': 'Añadir al pedido',
    'menu.all': 'Todos',
    'menu.departments': 'Departamentos del menú',
    'menu.learn_more': 'Más información',
    'cart.title': 'Tu Pedido',
    'cart.details': 'Detalles de Entrega',
    'cart.confirmed': '¡Confirmado!',
    'cart.empty': 'Tu carrito está vacío',
    'cart.continue': 'Continuar a los detalles',
    'cart.back': 'Volver',
    'cart.proceed_payment': 'Proceder al pago',
    'cart.qty': 'Cant.',
    'cart.notes_placeholder': 'Personalizar: sin cebolla, salsa aparte, alergias…',
    'cart.upsell.title': 'Maridajes perfectos',
    'cart.upsell.add': 'Añadir',
    'cart.perfect_match': 'Combinación perfecta',
    'cart.additional': 'Adicional',
    'cart.no_thanks': 'No, gracias',
    'cart.add_both': 'Añadir ambos',
    'cart.limited_time_offer': 'Oferta por tiempo limitado para esta mesa',
    'cart.modes.dine_in': 'En el local',
    'cart.modes.pickup': 'Para llevar',
    'cart.modes.delivery': 'A domicilio',
    'cart.modes_unavailable': 'Modos de pedido no disponibles actualmente.',
    'cart.form.name': 'Nombre completo',
    'cart.form.phone': 'Teléfono',
    'cart.form.table': 'Número de mesa',
    'cart.form.address': 'Dirección de entrega',
    'cart.form.table_placeholder': 'ej. 12',
    'cart.delivery_estimate': 'Tiempo estimado de entrega',
    'cart.busy_estimate': 'estimación en hora punta',
    'cart.asap': 'Lo antes posible',
    'cart.schedule': 'Programar',
    'cart.address_placeholder': 'Calle y barrio',
    'cart.apartment': 'Piso / Depto',
    'cart.floor': 'Planta',
    'cart.door_code': 'Código de puerta',
    'cart.map_pin': 'Enlace o chincheta de mapa',
    'cart.delivery_instructions': 'Instrucciones de entrega o alergias',
    'cart.contactless': 'Dejar en la puerta (sin contacto)',
    'cart.payment.title': 'Pedir y Pagar',
    'cart.payment.subtitle': 'Pago seguro desde la mesa',
    'cart.checkout_failed': 'Error en el pago',
    'cart.processing_payment': 'Procesando pago...',
    'cart.connecting_gateway': 'Conectando con pasarela segura...',
    'cart.payments_unavailable': 'Pagos no disponibles',
    'cart.payments_unavailable_desc': 'Este local no tiene métodos de pago activados.',
    'cart.pay_online': 'Pagar online',
    'cart.credit_debit_card': 'Tarjeta de crédito o débito',
    'cart.or': 'O',
    'cart.pay_courier_cash': 'Pagar al repartidor (Efectivo)',
    'cart.pay_counter_cash': 'Pagar en caja (Efectivo)',
    'cart.secure_payment': 'Pago seguro',
    'cart.courier_payment': 'Pago al mensajero',
    'cart.counter_payment': 'Pago en caja',
    'cart.secure_payment_desc': 'El pago cifrado con tarjeta está activado para este local.',
    'cart.staff_payment_desc': 'El pago será cobrado por el personal del local.',
    'cart.received.title': '¡Pedido recibido!',
    'cart.received.dinein': 'Tus platos se servirán en tu mesa en breve.',
    'cart.received.pickup': 'Hemos enviado tu pedido a cocina. ¡Enseguida estará listo!',
    'cart.received.close': 'Cerrar ventana',
    'cart.delivery_confirmed': 'Entrega confirmada para',
    'cart.your_zone': 'tu zona',
    'cart.loyalty_earned': 'Has ganado',
    'cart.loyalty_points': '¡Puntos de fidelidad!',
    'cart.how_experience': '¿Cómo fue tu experiencia de pedido?',
    'cart.improve_placeholder': '¿Qué podríamos mejorar?',
    'cart.send_feedback': 'Enviar comentario privado',
    'cart.google_review': 'Dejar reseña en Google',
    'cart.thank_feedback': '¡Gracias por tus comentarios!',
    'cart.items_subtotal': 'Artículos',
    'cart.delivery_fee': 'Entrega',
    'cart.delivery': 'Entrega',
    'cart.no_zone': 'Sin zona',
    'cart.free': 'Gratis',
    'cart.packaging': 'Empaque',
    'cart.total_summary': 'Resumen total',
    'tracker.order_received': 'Pedido recibido',
    'tracker.confirming_delivery': 'El restaurante está confirmando tu entrega',
    'tracker.waiting_kitchen': 'Esperando aprobación de cocina',
    'tracker.chef_cooking': 'El chef está cocinando',
    'tracker.delivery_preparing': 'Tu entrega se está preparando',
    'tracker.food_preparing': 'Tu comida se está preparando',
    'tracker.out_for_delivery': '¡En camino de entrega!',
    'tracker.ready_pickup': '¡Listo para recoger!',
    'tracker.courier_has_order': 'El repartidor tiene tu pedido',
    'tracker.come_to_counter': 'Por favor acércate al mostrador',
    'tracker.delivered': '¡Entregado!',
    'tracker.enjoy_meal': '¡Buen provecho!',
    'tracker.delivery_complete': 'Tu entrega ha finalizado',
    'tracker.order_completed': 'Pedido completado con éxito',
    'tracker.updating_status': 'Actualizando estado...',
    'tracker.order_again': 'Pedir de nuevo',
    'reservation.title': 'Reservar una mesa',
    'reservation.subtitle': 'Acompáñanos para una experiencia inolvidable',
    'reservation.name': 'Nombre completo',
    'reservation.name_placeholder': 'Tu nombre',
    'reservation.phone': 'Teléfono',
    'reservation.email': 'Correo electrónico',
    'reservation.guests': 'Personas',
    'reservation.date': 'Fecha',
    'reservation.time': 'Hora',
    'reservation.notes': 'Notas especiales',
    'reservation.notes_placeholder': 'Alergias, cumpleaños, preferencia de mesa...',
    'reservation.failed': 'Error en la reserva',
    'reservation.error_generic': 'No pudimos enviar tu reserva. Inténtalo de nuevo.',
    'reservation.submitting': 'Asegurando tu mesa...',
    'reservation.confirm': 'Confirmar reserva',
    'reservation.requested': '¡Reserva solicitada!',
    'reservation.requested_desc': 'Revisaremos tu solicitud y te enviaremos una confirmación por correo en breve.',
    'reservation.done': 'Hecho',
    'loyalty.title': 'Recompensas de fidelidad',
    'loyalty.subtitle': 'Gana puntos en cada pedido',
    'loyalty.welcome_back': '¡Bienvenido de nuevo!',
    'loyalty.welcome_desc': 'Inicia sesión para guardar tus puntos en cada visita.',
    'loyalty.view': 'Ver',
    'loyalty.enter_email': 'Introduce tu correo',
    'loyalty.sending_code': 'Enviando código...',
    'loyalty.send_code': 'Enviar código de verificación',
    'loyalty.enter_code_title': 'Introduce el código de verificación',
    'loyalty.code_placeholder': 'Código de 6 dígitos',
    'loyalty.verifying': 'Verificando...',
    'loyalty.verify_signin': 'Verificar e iniciar sesión',
    'loyalty.back_email': 'Volver al correo',
    'loyalty.available_points': 'Puntos disponibles',
    'loyalty.saved_to': 'Guardado en',
    'loyalty.available_rewards': 'Recompensas disponibles',
    'loyalty.redeem': 'Canjear',
    'loyalty.locked': 'Bloqueado',
    'loyalty.no_rewards': 'No hay recompensas disponibles todavía.',
    'loyalty.sign_out': 'Cerrar sesión',
    'loyalty.switch_account': 'Cambiar cuenta',
    'footer.live_encryption': 'Cifrado en vivo activo',
    'footer.powered_by': 'Desarrollado por',
    'footer.all_rights': 'Todos los derechos reservados.',
    'menu.open_now': 'Abierto ahora',
    'menu.closed': 'Cerrado',
    'menu.delivery_only': 'Solo entrega',
    'menu.book_table': 'Reservar mesa',
    'menu.rewards': 'Recompensas',
    'menu.table': 'Mesa',
    'menu.experience_culinary': 'Experimenta la excelencia culinaria',
    'menu.search_placeholder': 'Buscar artículos...',
    'menu.dietary_filters': 'Filtros dietéticos',
    'menu.exclude_allergens': 'Excluir alérgenos',
    'menu.no_allergen': 'Sin {allergen}',
    'dietary.vegetarian': 'Vegetariano',
    'dietary.vegan': 'Vegano',
    'dietary.spicy': 'Picante',
    'dietary.gluten-free': 'Sin Gluten',
    'dietary.nut-free': 'Sin Frutos Secos',
    'dietary.dairy-free': 'Sin Lácteos',
    'dietary.healthy': 'Saludable',
    'dietary.halal': 'Halal',
    'dietary.organic': 'Orgánico',
    'dietary.keto': 'Keto',
    'allergen.nuts': 'Frutos secos',
    'allergen.dairy': 'Lácteos',
    'allergen.gluten': 'Gluten',
    'allergen.shellfish': 'Marisco',
    'allergen.soy': 'Soja',
    'allergen.eggs': 'Huevos',
    'allergen.fish': 'Pescado',
    'menu.chefs_recommendations': 'Recomendaciones del Chef',
    'menu.chefs': 'Del Chef',
    'menu.recommendations': 'Recomendaciones',
    'menu.items': 'Artículos del menú',
    'menu.count': 'Artículos',
    'menu.section_closed': 'está cerrado',
    'menu.dine_in_closed_delivery_available': 'El servicio en sala está cerrado. La entrega a domicilio está disponible para esta sección.',
    'menu.section_closed_notice': 'Esta sección está cerrada actualmente. Vuelve a consultar durante el horario de atención.',
    'menu.available': 'DISPONIBLE',
    'menu.sold_out': 'AGOTADO',
    'menu.mins': 'MIN',
    'menu.running_low': 'Pocas unidades',
    'menu.adjusted': '(AJUSTADO)',
    'menu.add_to_cart': 'Añadir al carrito',
    'menu.visual_composition': 'Composición visual',
    'menu.small_details': 'Detalles',
    'menu.guest_sentiment': 'Opiniones de clientes',
    'menu.how_was_experience': '¿Cómo fue tu experiencia?',
    'menu.thank_feedback': '¡Gracias por tu opinión!',
    'menu.based_on_reviews': 'Basado en {count} opiniones',
    'menu.water': 'Agua',
    'menu.bill': 'Cuenta',
    'menu.cart': 'Carrito',
    'menu.checkout': 'Finalizar pedido',
    'menu.requested': '{item} solicitado',
  },
  fr: {
    'menu.no_items': 'Aucun plat trouvé',
    'menu.try_adjusting': 'Essayez de modifier votre recherche ou vos filtres',
    'menu.section_closed_check_back': 'Cette section est actuellement fermée. Veuillez vérifier les heures d\'ouverture ou parcourir nos autres menus.',
    'menu.made_with': 'Préparé avec',
    'menu.rate_item': 'Noter ce plat',
    'menu.based_reviews': 'Basé sur {count} avis clients',
    'menu.add_to_order': 'Ajouter à la commande',
    'menu.all': 'Tous',
    'menu.departments': 'Départements du menu',
    'menu.learn_more': 'En savoir plus',
    'cart.title': 'Votre Commande',
    'cart.details': 'Détails de Livraison',
    'cart.confirmed': 'Confirmé !',
    'cart.empty': 'Votre panier est vide',
    'cart.continue': 'Continuer vers les détails',
    'cart.back': 'Retour',
    'cart.proceed_payment': 'Passer au paiement',
    'cart.qty': 'Qté',
    'cart.notes_placeholder': 'Personnaliser : sans oignons, sauce à part, allergies…',
    'cart.upsell.title': 'Accompagnements parfaits',
    'cart.upsell.add': 'Ajouter',
    'cart.perfect_match': 'Association Parfaite',
    'cart.additional': 'En supplément',
    'cart.no_thanks': 'Non, merci',
    'cart.add_both': 'Ajouter les deux',
    'cart.limited_time_offer': 'Offre limitée pour cette session de table',
    'cart.modes.dine_in': 'Sur place',
    'cart.modes.pickup': 'À emporter',
    'cart.modes.delivery': 'Livraison',
    'cart.modes_unavailable': 'Modes de commande temporairement indisponibles.',
    'cart.form.name': 'Nom complet',
    'cart.form.phone': 'Numéro de téléphone',
    'cart.form.table': 'Numéro de table',
    'cart.form.address': 'Adresse de livraison',
    'cart.form.table_placeholder': 'ex. 12',
    'cart.delivery_estimate': 'Estimation de livraison',
    'cart.busy_estimate': 'estimation période d\'affluence',
    'cart.asap': 'Dès que possible',
    'cart.schedule': 'Planifier',
    'cart.address_placeholder': 'Rue et quartier',
    'cart.apartment': 'Appartement',
    'cart.floor': 'Étage',
    'cart.door_code': 'Code d\'accès',
    'cart.map_pin': 'Lien GPS ou repère',
    'cart.delivery_instructions': 'Instructions de livraison, allergies ou consignes',
    'cart.contactless': 'Déposer à la porte (sans contact)',
    'cart.payment.title': 'Commander et Payer',
    'cart.payment.subtitle': 'Paiement sécurisé à table',
    'cart.checkout_failed': 'Échec du paiement',
    'cart.processing_payment': 'Traitement du paiement',
    'cart.connecting_gateway': 'Connexion sécurisée en cours...',
    'cart.payments_unavailable': 'Paiements indisponibles',
    'cart.payments_unavailable_desc': 'Aucun moyen de paiement activé pour ce local.',
    'cart.pay_online': 'Payer en ligne',
    'cart.credit_debit_card': 'Carte Bancaire',
    'cart.or': 'Ou',
    'cart.pay_courier_cash': 'Payer le livreur (Espèces)',
    'cart.pay_counter_cash': 'Payer au comptoir (Espèces)',
    'cart.secure_payment': 'Paiement sécurisé',
    'cart.courier_payment': 'Paiement coursier',
    'cart.counter_payment': 'Paiement comptoir',
    'cart.secure_payment_desc': 'Paiement chiffré et sécurisé actif pour cet établissement.',
    'cart.staff_payment_desc': 'Le règlement sera effectué auprès du personnel.',
    'cart.received.title': 'Commande reçue !',
    'cart.received.dinein': 'Vos plats vous seront servis à table dans un instant.',
    'cart.received.pickup': 'Votre commande est en cuisine. À très vite !',
    'cart.received.close': 'Fermer la fenêtre',
    'cart.delivery_confirmed': 'Livraison confirmée pour',
    'cart.your_zone': 'votre zone',
    'cart.loyalty_earned': 'Vous avez gagné',
    'cart.loyalty_points': 'Points de fidélité !',
    'cart.how_experience': 'Comment s\'est passée votre commande ?',
    'cart.improve_placeholder': 'Que pouvons-nous améliorer ?',
    'cart.send_feedback': 'Envoyer un retour privé',
    'cart.google_review': 'Laisser un avis Google',
    'cart.thank_feedback': 'Merci pour votre avis !',
    'cart.items_subtotal': 'Articles',
    'cart.delivery_fee': 'Livraison',
    'cart.delivery': 'Livraison',
    'cart.no_zone': 'Hors zone',
    'cart.free': 'Gratuit',
    'cart.packaging': 'Emballage',
    'cart.total_summary': 'Récapitulatif',
    'tracker.order_received': 'Commande reçue',
    'tracker.confirming_delivery': 'L\'établissement confirme votre livraison',
    'tracker.waiting_kitchen': 'En attente de la cuisine',
    'tracker.chef_cooking': 'Le chef cuisine',
    'tracker.delivery_preparing': 'Votre livraison est en préparation',
    'tracker.food_preparing': 'Vos plats sont en cours de préparation',
    'tracker.out_for_delivery': 'En cours de livraison !',
    'tracker.ready_pickup': 'Prêt pour le retrait !',
    'tracker.courier_has_order': 'Le livreur a pris en charge votre commande',
    'tracker.come_to_counter': 'Veuillez vous présenter au comptoir',
    'tracker.delivered': 'Livré !',
    'tracker.enjoy_meal': 'Bon appétit !',
    'tracker.delivery_complete': 'Votre livraison est terminée',
    'tracker.order_completed': 'Commande terminée avec succès',
    'tracker.updating_status': 'Mise à jour...',
    'tracker.order_again': 'Commander à nouveau',
    'reservation.title': 'Réserver une table',
    'reservation.subtitle': 'Rejoignez-nous pour une expérience inoubliable',
    'reservation.name': 'Nom complet',
    'reservation.name_placeholder': 'Votre nom',
    'reservation.phone': 'Numéro de téléphone',
    'reservation.email': 'Adresse e-mail',
    'reservation.guests': 'Personnes',
    'reservation.date': 'Date',
    'reservation.time': 'Heure',
    'reservation.notes': 'Demandes particulières',
    'reservation.notes_placeholder': 'Allergies, anniversaire, préférence de table...',
    'reservation.failed': 'Échec de la réservation',
    'reservation.error_generic': 'Impossible d\'enregistrer votre réservation. Veuillez réessayer.',
    'reservation.submitting': 'Réservation en cours...',
    'reservation.confirm': 'Confirmer la réservation',
    'reservation.requested': 'Réservation demandée !',
    'reservation.requested_desc': 'Nous examinerons votre demande et vous enverrons une confirmation par e-mail sous peu.',
    'reservation.done': 'Terminé',
    'loyalty.title': 'Programme de Fidélité',
    'loyalty.subtitle': 'Gagnez des points à chaque commande',
    'loyalty.welcome_back': 'Ravi de vous revoir !',
    'loyalty.welcome_desc': 'Connectez-vous pour cumuler vos points ou vérifier avec votre e-mail.',
    'loyalty.view': 'Voir',
    'loyalty.enter_email': 'Entrez votre adresse e-mail',
    'loyalty.sending_code': 'Envoi du code...',
    'loyalty.send_code': 'Envoyer le code de vérification',
    'loyalty.enter_code_title': 'Code de vérification',
    'loyalty.code_placeholder': 'Code à 6 chiffres',
    'loyalty.verifying': 'Vérification...',
    'loyalty.verify_signin': 'Valider et se connecter',
    'loyalty.back_email': 'Retour à l\'e-mail',
    'loyalty.available_points': 'Points disponibles',
    'loyalty.saved_to': 'Enregistré pour',
    'loyalty.available_rewards': 'Récompenses disponibles',
    'loyalty.redeem': 'Échanger',
    'loyalty.locked': 'Verrouillé',
    'loyalty.no_rewards': 'Aucune récompense disponible pour le moment.',
    'loyalty.sign_out': 'Se déconnecter',
    'loyalty.switch_account': 'Changer de compte',
    'footer.live_encryption': 'Chiffrement en direct actif',
    'footer.powered_by': 'Propulsé par',
    'footer.all_rights': 'Tous droits réservés.',
    'menu.open_now': 'Ouvert maintenant',
    'menu.closed': 'Fermé',
    'menu.delivery_only': 'Livraison uniquement',
    'menu.book_table': 'Réserver une table',
    'menu.rewards': 'Récompenses',
    'menu.table': 'Table',
    'menu.experience_culinary': "Vivez l'excellence culinaire",
    'menu.search_placeholder': 'Rechercher un plat...',
    'menu.dietary_filters': 'Filtres diététiques',
    'menu.exclude_allergens': 'Exclure les allergènes',
    'menu.no_allergen': 'Sans {allergen}',
    'dietary.vegetarian': 'Végétarien',
    'dietary.vegan': 'Végétalien',
    'dietary.spicy': 'Épicé',
    'dietary.gluten-free': 'Sans Gluten',
    'dietary.nut-free': 'Sans Fruits à Coque',
    'dietary.dairy-free': 'Sans Laitiers',
    'dietary.healthy': 'Sain',
    'dietary.halal': 'Halal',
    'dietary.organic': 'Bio',
    'dietary.keto': 'Keto',
    'allergen.nuts': 'Fruits à coque',
    'allergen.dairy': 'Produits laitiers',
    'allergen.gluten': 'Gluten',
    'allergen.shellfish': 'Crustacés',
    'allergen.soy': 'Soja',
    'allergen.eggs': 'Œufs',
    'allergen.fish': 'Poisson',
    'menu.chefs_recommendations': 'Recommandations du Chef',
    'menu.chefs': 'Du Chef',
    'menu.recommendations': 'Recommandations',
    'menu.items': 'Articles du menu',
    'menu.count': 'Articles',
    'menu.section_closed': 'est fermé',
    'menu.dine_in_closed_delivery_available': 'Le service sur place est fermé. La livraison est disponible pour cette section.',
    'menu.section_closed_notice': "Cette section est actuellement fermée. Veuillez vérifier les heures d'ouverture.",
    'menu.available': 'DISPONIBLE',
    'menu.sold_out': 'ÉPUISÉ',
    'menu.mins': 'MIN',
    'menu.running_low': 'Stock limité',
    'menu.adjusted': '(AJUSTÉ)',
    'menu.add_to_cart': 'Ajouter au panier',
    'menu.visual_composition': 'Composition visuelle',
    'menu.small_details': 'Détails',
    'menu.guest_sentiment': 'Avis des clients',
    'menu.how_was_experience': 'Comment s’est passée votre expérience ?',
    'menu.thank_feedback': 'Merci pour votre avis !',
    'menu.based_on_reviews': 'Basé sur {count} avis',
    'menu.water': 'Eau',
    'menu.bill': 'Addition',
    'menu.cart': 'Panier',
    'menu.checkout': 'Commander',
    'menu.requested': '{item} demandé',
  },
  ar: {
    'menu.no_items': 'لم يتم العثور على أطباق',
    'menu.try_adjusting': 'يرجى تجربة تعديل البحث أو الفلاتر',
    'menu.section_closed_check_back': 'هذا القسم مغلق حالياً. يرجى زيارته خلال أوقات العمل أو تصفح القوائم الأخرى أعلاه.',
    'menu.made_with': 'محضر من',
    'menu.rate_item': 'قيّم هذا الطبق',
    'menu.based_reviews': 'بناءً على {count} تقييم من الضيوف',
    'menu.add_to_order': 'أضف إلى الطلب',
    'menu.all': 'الكل',
    'menu.departments': 'أقسام القائمة',
    'menu.learn_more': 'اعرف المزيد',
    'cart.title': 'طلبك',
    'cart.details': 'تفاصيل التوصيل',
    'cart.confirmed': 'تم التأكيد!',
    'cart.empty': 'سلة الطلبات فارغة',
    'cart.continue': 'متابعة التفاصيل',
    'cart.back': 'رجوع',
    'cart.proceed_payment': 'الانتقال للدفع',
    'cart.qty': 'الكمية',
    'cart.notes_placeholder': 'تخصيص: بدون بصل، الصوص جانبي، ملاحظات حساسية…',
    'cart.upsell.title': 'إضافات مقترحة تناسبك',
    'cart.upsell.add': 'إضافة',
    'cart.perfect_match': 'تناغم مثالي',
    'cart.additional': 'إضافي',
    'cart.no_thanks': 'لا، شكراً',
    'cart.add_both': 'إضافة الاثنين معاً',
    'cart.limited_time_offer': 'عرض خاص لجلسة الطاولة الحالية',
    'cart.modes.dine_in': 'محلي (في المطعم)',
    'cart.modes.pickup': 'استلام',
    'cart.modes.delivery': 'توصيل',
    'cart.modes_unavailable': 'خيارات الطلب غير متاحة حالياً.',
    'cart.form.name': 'الاسم الكامل',
    'cart.form.phone': 'رقم الهاتف',
    'cart.form.table': 'رقم الطاولة',
    'cart.form.address': 'عنوان التوصيل',
    'cart.form.table_placeholder': 'مثال: 12',
    'cart.delivery_estimate': 'وقت التوصيل التقديري',
    'cart.busy_estimate': 'تقدير وقت الذروة',
    'cart.asap': 'في أقرب وقت',
    'cart.schedule': 'جدولة الطلب',
    'cart.address_placeholder': 'الشارع والحي',
    'cart.apartment': 'الشقة',
    'cart.floor': 'الطابق',
    'cart.door_code': 'رمز الباب',
    'cart.map_pin': 'رابط الموقع على الخريطة',
    'cart.delivery_instructions': 'تعليمات التوصيل أو الحساسية أو التسليم',
    'cart.contactless': 'توصيل بدون تلامس (عند الباب)',
    'cart.payment.title': 'الطلب والدفع',
    'cart.payment.subtitle': 'دفع آمن ومباشر من طاولتك',
    'cart.checkout_failed': 'تعذر إتمام عملية الدفع',
    'cart.processing_payment': 'جاري معالجة الدفع...',
    'cart.connecting_gateway': 'جاري الاتصال ببوابة الدفع الآمنة...',
    'cart.payments_unavailable': 'الدفع غير متاح حالياً',
    'cart.payments_unavailable_desc': 'لم يقم هذا المطعم بتفعيل خيارات الدفع الإلكتروني بعد.',
    'cart.pay_online': 'الدفع الإلكتروني',
    'cart.credit_debit_card': 'بطاقة بنكية / ائتمانية',
    'cart.or': 'أو',
    'cart.pay_courier_cash': 'الدفع نقداً عند الاستلام',
    'cart.pay_counter_cash': 'الدفع نقداً عند الصندوق',
    'cart.secure_payment': 'دفع آمن ومشفر',
    'cart.courier_payment': 'الدفع للمندوب',
    'cart.counter_payment': 'الدفع عند الصندوق',
    'cart.secure_payment_desc': 'الدفع المشفر بالبطاقات مفعل وآمن لهذا المطعم.',
    'cart.staff_payment_desc': 'سيتم تحصيل المبلغ بواسطة طاقم المطعم.',
    'cart.received.title': 'تم استلام طلبك بنجاح!',
    'cart.received.dinein': 'سيتم إحضار طلبك إلى طاولتك في أقرب وقت.',
    'cart.received.pickup': 'تم إرسال طلبك إلى المطبخ وسيكون جاهزاً قريباً!',
    'cart.received.close': 'إغلاق النافذة',
    'cart.delivery_confirmed': 'تم تأكيد التوصيل إلى',
    'cart.your_zone': 'منطقتك',
    'cart.loyalty_earned': 'لقد حصلت على',
    'cart.loyalty_points': 'نقاط مكافأة!',
    'cart.how_experience': 'كيف كانت تجربة طلبك؟',
    'cart.improve_placeholder': 'ما الذي يمكننا تحسينه؟',
    'cart.send_feedback': 'إرسال ملاحظة خاصة',
    'cart.google_review': 'كتابة تقييم على Google',
    'cart.thank_feedback': 'شكراً لمشاركتنا رأيك!',
    'cart.items_subtotal': 'الأطباق',
    'cart.delivery_fee': 'التوصيل',
    'cart.delivery': 'توصيل',
    'cart.no_zone': 'خارج النطاق',
    'cart.free': 'مجاني',
    'cart.packaging': 'التغليف',
    'cart.total_summary': 'المجموع الكلي',
    'tracker.order_received': 'تم استلام الطلب',
    'tracker.confirming_delivery': 'المطعم يؤكد طلب التوصيل الخاص بك',
    'tracker.waiting_kitchen': 'في انتظار موافقة المطبخ',
    'tracker.chef_cooking': 'الشيف يقوم بالطهي',
    'tracker.delivery_preparing': 'جاري تجهيز طلب التوصيل',
    'tracker.food_preparing': 'جاري تحضير طعامك بكل عناية',
    'tracker.out_for_delivery': 'الطلب في الطريق إليك!',
    'tracker.ready_pickup': 'الطلب جاهز للاستلام!',
    'tracker.courier_has_order': 'المندوب استلم طلبك وهو في الطريق',
    'tracker.come_to_counter': 'تفضل بالاستلام من الصندوق',
    'tracker.delivered': 'تم التوصيل بنجاح!',
    'tracker.enjoy_meal': 'بالهناء والشفاء!',
    'tracker.delivery_complete': 'اكتمل التوصيل بنجاح',
    'tracker.order_completed': 'اكتمل طلبك بنجاح',
    'tracker.updating_status': 'جاري تحديث الحالة...',
    'tracker.order_again': 'اطلب مرة أخرى',
    'reservation.title': 'حجز طاولة',
    'reservation.subtitle': 'انضم إلينا لتجربة استثنائية لا تُنسى',
    'reservation.name': 'الاسم الكامل',
    'reservation.name_placeholder': 'اسمك الكريم',
    'reservation.phone': 'رقم الهاتف',
    'reservation.email': 'البريد الإلكتروني',
    'reservation.guests': 'عدد الضيوف',
    'reservation.date': 'التاريخ',
    'reservation.time': 'الوقت',
    'reservation.notes': 'ملاحظات خاصة',
    'reservation.notes_placeholder': 'حساسية طعام، مناسبة خاصة، موقع الجلوس المفضل...',
    'reservation.failed': 'تعذر إتمام الحجز',
    'reservation.error_generic': 'تعذر تسجيل حجزك حالياً. يرجى المحاولة مرة أخرى.',
    'reservation.submitting': 'جاري تأكيد حجز طاولتك...',
    'reservation.confirm': 'تأكيد الحجز',
    'reservation.requested': 'تم إرسال طلب الحجز!',
    'reservation.requested_desc': 'سنقوم بمراجعة طلبك وإرسال رسالة تأكيد إلى بريدك قريباً.',
    'reservation.done': 'تم',
    'loyalty.title': 'برنامج المكافآت',
    'loyalty.subtitle': 'اكسب نقاطاً مع كل طلب',
    'loyalty.welcome_back': 'أهلاً بك مجدداً!',
    'loyalty.welcome_desc': 'سجل دخولك لحفظ نقاطك واستبدالها بمكافآت مميزة.',
    'loyalty.view': 'عرض',
    'loyalty.enter_email': 'أدخل بريدك الإلكتروني',
    'loyalty.sending_code': 'جاري إرسال الرمز...',
    'loyalty.send_code': 'إرسال رمز التحقق',
    'loyalty.enter_code_title': 'أدخل رمز التحقق',
    'loyalty.code_placeholder': 'رمز من 6 أرقام',
    'loyalty.verifying': 'جاري التحقق...',
    'loyalty.verify_signin': 'تحقق ودخول',
    'loyalty.back_email': 'الرجوع للبريد الإلكتروني',
    'loyalty.available_points': 'النقاط المتاحة',
    'loyalty.saved_to': 'محفوظة في',
    'loyalty.available_rewards': 'المكافآت المتوفرة',
    'loyalty.redeem': 'استبدال',
    'loyalty.locked': 'غير متاح بعد',
    'loyalty.no_rewards': 'لا توجد مكافآت متاحة حالياً.',
    'loyalty.sign_out': 'تسجيل الخروج',
    'loyalty.switch_account': 'تبديل الحساب',
    'footer.live_encryption': 'تشفير فوري آمن ومفعل',
    'footer.powered_by': 'مشغل بواسطة',
    'footer.all_rights': 'جميع الحقوق محفوظة.',
    'menu.open_now': 'مفتوح الآن',
    'menu.closed': 'مغلق',
    'menu.delivery_only': 'توصيل فقط',
    'menu.book_table': 'حجز طاولة',
    'menu.rewards': 'المكافآت',
    'menu.table': 'طاولة',
    'menu.experience_culinary': 'عش تجربة الطهي الفاخرة والأصيلة',
    'menu.search_placeholder': 'ابحث في قائمة الطعام...',
    'menu.dietary_filters': 'خيارات النظام الغذائي',
    'menu.exclude_allergens': 'استبعاد مسببات الحساسية',
    'menu.no_allergen': 'بدون {allergen}',
    'dietary.vegetarian': 'نباتي',
    'dietary.vegan': 'نباتي صرف',
    'dietary.spicy': 'حار',
    'dietary.gluten-free': 'خالٍ من الغلوتين',
    'dietary.nut-free': 'خالٍ من المكسرات',
    'dietary.dairy-free': 'خالٍ من مشتقات الحليب',
    'dietary.healthy': 'صحي',
    'dietary.halal': 'حلال',
    'dietary.organic': 'عضوي',
    'dietary.keto': 'كيتو',
    'allergen.nuts': 'المكسرات',
    'allergen.dairy': 'مشتقات الحليب',
    'allergen.gluten': 'الغلوتين',
    'allergen.shellfish': 'القشريات',
    'allergen.soy': 'الصويا',
    'allergen.eggs': 'البيض',
    'allergen.fish': 'السمك',
    'menu.chefs_recommendations': 'توصيات الشيف',
    'menu.chefs': 'الشيف',
    'menu.recommendations': 'توصيات مميزة',
    'menu.items': 'عناصر القائمة',
    'menu.count': 'عناصر',
    'menu.section_closed': 'مغلق حالياً',
    'menu.dine_in_closed_delivery_available': 'خدمة الصالة مغلقة حالياً. التوصيل متوفر لهذا القسم.',
    'menu.section_closed_notice': 'هذا القسم مغلق حالياً. يرجى المراجعة خلال ساعات العمل أو تصفح القوائم الأخرى.',
    'menu.available': 'متوفر',
    'menu.sold_out': 'نفد بالكامل',
    'menu.mins': 'دقيقة',
    'menu.running_low': 'كمية محدودة',
    'menu.adjusted': '(معدّل)',
    'menu.add_to_cart': 'أضف إلى السلة',
    'menu.visual_composition': 'المكونات الأساسية',
    'menu.small_details': 'التفاصيل',
    'menu.guest_sentiment': 'تقييمات الضيوف',
    'menu.how_was_experience': 'كيف كانت تجربتك؟',
    'menu.thank_feedback': 'شكراً لتقييمك وملاحظاتك!',
    'menu.based_on_reviews': 'بناءً على {count} تقييم من الضيوف',
    'menu.water': 'ماء',
    'menu.bill': 'الحساب',
    'menu.cart': 'السلة',
    'menu.checkout': 'إتمام الطلب',
    'menu.requested': 'تم طلب {item}',
  },
  de: {
    'menu.no_items': 'Keine Gerichte gefunden',
    'menu.try_adjusting': 'Versuchen Sie, die Suche oder Filter anzupassen',
    'menu.section_closed_check_back': 'Dieser Bereich ist zurzeit geschlossen. Bitte versuchen Sie es während der Öffnungszeiten erneut.',
    'menu.made_with': 'Zubereitet mit',
    'menu.rate_item': 'Dieses Gericht bewerten',
    'menu.based_reviews': 'Basierend auf {count} Bewertungen',
    'menu.add_to_order': 'Zur Bestellung hinzufügen',
    'menu.all': 'Alle',
    'menu.departments': 'Menüabteilungen',
    'menu.learn_more': 'Mehr erfahren',
    'cart.title': 'Ihre Bestellung',
    'cart.details': 'Lieferdetails',
    'cart.confirmed': 'Bestätigt!',
    'cart.empty': 'Ihr Warenkorb ist leer',
    'cart.continue': 'Weiter zu Details',
    'cart.back': 'Zurück',
    'cart.proceed_payment': 'Weiter zur Zahlung',
    'cart.qty': 'Menge',
    'cart.notes_placeholder': 'Anpassen: keine Zwiebeln, Sauce extra, Allergien…',
    'cart.upsell.title': 'Perfekte Ergänzungen',
    'cart.upsell.add': 'Hinzufügen',
    'cart.perfect_match': 'Perfekte Kombination',
    'cart.additional': 'Zusätzlich',
    'cart.no_thanks': 'Nein, danke',
    'cart.add_both': 'Beides hinzufügen',
    'cart.limited_time_offer': 'Zeitlich begrenztes Angebot für diesen Tisch',
    'cart.modes.dine_in': 'Vor Ort',
    'cart.modes.pickup': 'Abholung',
    'cart.modes.delivery': 'Lieferung',
    'cart.modes_unavailable': 'Bestellmodi sind derzeit nicht verfügbar.',
    'cart.form.name': 'Vollständiger Name',
    'cart.form.phone': 'Telefonnummer',
    'cart.form.table': 'Tischnummer',
    'cart.form.address': 'Lieferadresse',
    'cart.form.table_placeholder': 'z. B. 12',
    'cart.delivery_estimate': 'Geschätzte Lieferzeit',
    'cart.busy_estimate': 'Schätzung für Stoßzeiten',
    'cart.asap': 'Schnellstmöglich',
    'cart.schedule': 'Planen',
    'cart.address_placeholder': 'Straße und Hausnummer',
    'cart.apartment': 'Wohnung',
    'cart.floor': 'Etage',
    'cart.door_code': 'Türcode',
    'cart.map_pin': 'Standort-Link / Karten-Pin',
    'cart.delivery_instructions': 'Lieferhinweise, Allergien oder Anmerkungen',
    'cart.contactless': 'Vor der Tür abstellen (kontaktlos)',
    'cart.payment.title': 'Bestellen & Bezahlen',
    'cart.payment.subtitle': 'Sichere Bezahlung am Tisch',
    'cart.checkout_failed': 'Zahlung fehlgeschlagen',
    'cart.processing_payment': 'Zahlung wird verarbeitet...',
    'cart.connecting_gateway': 'Verbindung zum sicheren Gateway...',
    'cart.payments_unavailable': 'Zahlung nicht verfügbar',
    'cart.payments_unavailable_desc': 'Dieser Betrieb hat keine Online-Zahlungsmethode aktiviert.',
    'cart.pay_online': 'Online bezahlen',
    'cart.credit_debit_card': 'Kredit- oder Debitkarte',
    'cart.or': 'Oder',
    'cart.pay_courier_cash': 'Beim Kurier bar bezahlen',
    'cart.pay_counter_cash': 'An der Kasse bar bezahlen',
    'cart.secure_payment': 'Sichere Zahlung',
    'cart.courier_payment': 'Zahlung beim Kurier',
    'cart.counter_payment': 'Zahlung an Kasse',
    'cart.secure_payment_desc': 'Verschlüsselte Kartenzahlung ist für dieses Lokal aktiv.',
    'cart.staff_payment_desc': 'Zahlung wird vom Personal vor Ort entgegengenommen.',
    'cart.received.title': 'Bestellung erhalten!',
    'cart.received.dinein': 'Ihre Speisen werden in Kürze an Ihren Tisch gebracht.',
    'cart.received.pickup': 'Wir haben Ihre Bestellung an die Küche übermittelt.',
    'cart.received.close': 'Fenster schließen',
    'cart.delivery_confirmed': 'Lieferung bestätigt für',
    'cart.your_zone': 'Ihre Zone',
    'cart.loyalty_earned': 'Sie haben erhalten',
    'cart.loyalty_points': 'Treuepunkte!',
    'cart.how_experience': 'Wie war Ihr Bestellerlebnis?',
    'cart.improve_placeholder': 'Was können wir verbessern?',
    'cart.send_feedback': 'Privates Feedback senden',
    'cart.google_review': 'Google-Bewertung schreiben',
    'cart.thank_feedback': 'Vielen Dank für Ihre Bewertung!',
    'cart.items_subtotal': 'Artikel',
    'cart.delivery_fee': 'Lieferung',
    'cart.delivery': 'Lieferung',
    'cart.no_zone': 'Keine Zone',
    'cart.free': 'Kostenlos',
    'cart.packaging': 'Verpackung',
    'cart.total_summary': 'Gesamtsumme',
    'tracker.order_received': 'Bestellung eingegangen',
    'tracker.confirming_delivery': 'Das Lokal bestätigt Ihre Lieferung',
    'tracker.waiting_kitchen': 'Warten auf Bestätigung der Küche',
    'tracker.chef_cooking': 'Der Küchenchef kocht',
    'tracker.delivery_preparing': 'Ihre Lieferung wird zubereitet',
    'tracker.food_preparing': 'Ihr Essen wird frisch zubereitet',
    'tracker.out_for_delivery': 'In Zustellung!',
    'tracker.ready_pickup': 'Bereit zur Abholung!',
    'tracker.courier_has_order': 'Der Kurier hat Ihre Bestellung übernommen',
    'tracker.come_to_counter': 'Bitte kommen Sie zur Kasse',
    'tracker.delivered': 'Geliefert!',
    'tracker.enjoy_meal': 'Guten Appetit!',
    'tracker.delivery_complete': 'Ihre Lieferung ist abgeschlossen',
    'tracker.order_completed': 'Bestellung erfolgreich abgeschlossen',
    'tracker.updating_status': 'Status wird aktualisiert...',
    'tracker.order_again': 'Erneut bestellen',
    'reservation.title': 'Tisch reservieren',
    'reservation.subtitle': 'Erleben Sie einen unvergesslichen Aufenthalt',
    'reservation.name': 'Vollständiger Name',
    'reservation.name_placeholder': 'Ihr Name',
    'reservation.phone': 'Telefonnummer',
    'reservation.email': 'E-Mail-Adresse',
    'reservation.guests': 'Gäste',
    'reservation.date': 'Datum',
    'reservation.time': 'Uhrzeit',
    'reservation.notes': 'Besondere Wünsche',
    'reservation.notes_placeholder': 'Allergien, Geburtstag, Tischpräferenz...',
    'reservation.failed': 'Reservierung fehlgeschlagen',
    'reservation.error_generic': 'Ihre Reservierung konnte nicht übermittelt werden.',
    'reservation.submitting': 'Tisch wird reserviert...',
    'reservation.confirm': 'Reservierung bestätigen',
    'reservation.requested': 'Reservierungsanfrage gesendet!',
    'reservation.requested_desc': 'Wir prüfen Ihre Anfrage und senden Ihnen in Kürze eine Bestätigung.',
    'reservation.done': 'Fertig',
    'loyalty.title': 'Treueprogramm',
    'loyalty.subtitle': 'Punkte bei jeder Bestellung sammeln',
    'loyalty.welcome_back': 'Willkommen zurück!',
    'loyalty.welcome_desc': 'Melden Sie sich an, um Punkte zu speichern.',
    'loyalty.view': 'Ansehen',
    'loyalty.enter_email': 'E-Mail-Adresse eingeben',
    'loyalty.sending_code': 'Code wird gesendet...',
    'loyalty.send_code': 'Bestätigungscode senden',
    'loyalty.enter_code_title': 'Bestätigungscode eingeben',
    'loyalty.code_placeholder': '6-stelliger Code',
    'loyalty.verifying': 'Wird überprüft...',
    'loyalty.verify_signin': 'Bestätigen & Anmelden',
    'loyalty.back_email': 'Zurück zur E-Mail',
    'loyalty.available_points': 'Verfügbare Punkte',
    'loyalty.saved_to': 'Gespeichert für',
    'loyalty.available_rewards': 'Verfügbare Prämien',
    'loyalty.redeem': 'Einlösen',
    'loyalty.locked': 'Gesperrt',
    'loyalty.no_rewards': 'Zurzeit keine Prämien verfügbar.',
    'loyalty.sign_out': 'Abmelden',
    'loyalty.switch_account': 'Konto wechseln',
    'footer.live_encryption': 'Live-Verschlüsselung aktiv',
    'footer.powered_by': 'Bereitgestellt von',
    'footer.all_rights': 'Alle Rechte vorbehalten.',
    'menu.open_now': 'Jetzt geöffnet',
    'menu.closed': 'Geschlossen',
    'menu.delivery_only': 'Nur Lieferung',
    'menu.book_table': 'Tisch reservieren',
    'menu.rewards': 'Prämien',
    'menu.table': 'Tisch',
    'menu.experience_culinary': 'Erleben Sie kulinarische Spitzenklasse',
    'menu.search_placeholder': 'Artikel suchen...',
    'menu.dietary_filters': 'Ernährungsfilter',
    'menu.exclude_allergens': 'Allergene ausschließen',
    'menu.no_allergen': 'Ohne {allergen}',
    'dietary.vegetarian': 'Vegetarisch',
    'dietary.vegan': 'Vegan',
    'dietary.spicy': 'Scharf',
    'dietary.gluten-free': 'Glutenfrei',
    'dietary.nut-free': 'Nussfrei',
    'dietary.dairy-free': 'Laktosefrei',
    'dietary.healthy': 'Gesund',
    'dietary.halal': 'Halal',
    'dietary.organic': 'Bio',
    'dietary.keto': 'Keto',
    'allergen.nuts': 'Nüsse',
    'allergen.dairy': 'Milchprodukte',
    'allergen.gluten': 'Gluten',
    'allergen.shellfish': 'Schalentiere',
    'allergen.soy': 'Soja',
    'allergen.eggs': 'Eier',
    'allergen.fish': 'Fisch',
    'menu.chefs_recommendations': 'Empfehlungen des Küchenchefs',
    'menu.chefs': 'Küchenchef',
    'menu.recommendations': 'Empfehlungen',
    'menu.items': 'Menüartikel',
    'menu.count': 'Artikel',
    'menu.section_closed': 'ist geschlossen',
    'menu.dine_in_closed_delivery_available': 'Der Service vor Ort ist geschlossen. Lieferung ist verfügbar.',
    'menu.section_closed_notice': 'Dieser Bereich ist derzeit geschlossen.',
    'menu.available': 'VERFÜGBAR',
    'menu.sold_out': 'AUSVERKAUFT',
    'menu.mins': 'MIN',
    'menu.running_low': 'Geringer Bestand',
    'menu.adjusted': '(ANGEPASST)',
    'menu.add_to_cart': 'In den Warenkorb',
    'menu.visual_composition': 'Visuelle Zusammensetzung',
    'menu.small_details': 'Details',
    'menu.guest_sentiment': 'Gästebewertungen',
    'menu.how_was_experience': 'Wie war Ihre Erfahrung?',
    'menu.thank_feedback': 'Vielen Dank für Ihr Feedback!',
    'menu.based_on_reviews': 'Basierend auf {count} Bewertungen',
    'menu.water': 'Wasser',
    'menu.bill': 'Rechnung',
    'menu.cart': 'Warenkorb',
    'menu.checkout': 'Kasse',
    'menu.requested': '{item} angefordert',
  },
  it: {
    'menu.no_items': 'Nessun piatto trovato',
    'menu.try_adjusting': 'Prova a modificare i filtri o la ricerca',
    'menu.section_closed_check_back': 'Questa sezione è temporaneamente chiusa. Si prega di verificare gli orari di apertura.',
    'menu.made_with': 'Preparato con',
    'menu.rate_item': 'Valuta questo piatto',
    'menu.based_reviews': 'Basato su {count} recensioni',
    'menu.add_to_order': 'Aggiungi all\'ordine',
    'menu.all': 'Tutti',
    'menu.departments': 'Reparti del menu',
    'menu.learn_more': 'Scopri di più',
    'cart.title': 'Il tuo Ordine',
    'cart.details': 'Dettagli di Consegna',
    'cart.confirmed': 'Confermato!',
    'cart.empty': 'Il carrello è vuoto',
    'cart.continue': 'Continua ai dettagli',
    'cart.back': 'Indietro',
    'cart.proceed_payment': 'Procedi al pagamento',
    'cart.qty': 'Qtà',
    'cart.notes_placeholder': 'Personalizza: senza cipolla, salsa a parte, allergie…',
    'cart.upsell.title': 'Abbinamenti perfetti',
    'cart.upsell.add': 'Aggiungi',
    'cart.perfect_match': 'Abbinamento Perfetto',
    'cart.additional': 'Aggiuntivo',
    'cart.no_thanks': 'No, grazie',
    'cart.add_both': 'Aggiungi entrambi',
    'cart.limited_time_offer': 'Offerta limitata per questa sessione al tavolo',
    'cart.modes.dine_in': 'Al tavolo',
    'cart.modes.pickup': 'Da asporto',
    'cart.modes.delivery': 'A domicilio',
    'cart.modes_unavailable': 'Modalità d\'ordine attualmente non disponibili.',
    'cart.form.name': 'Nome e cognome',
    'cart.form.phone': 'Numero di telefono',
    'cart.form.table': 'Numero tavolo',
    'cart.form.address': 'Indirizzo di consegna',
    'cart.form.table_placeholder': 'es. 12',
    'cart.delivery_estimate': 'Stima di consegna',
    'cart.busy_estimate': 'stima orari di punta',
    'cart.asap': 'Il prima possibile',
    'cart.schedule': 'Programma',
    'cart.address_placeholder': 'Via e quartiere',
    'cart.apartment': 'Appartamento',
    'cart.floor': 'Piano',
    'cart.door_code': 'Codice citofono',
    'cart.map_pin': 'Link posizione GPS',
    'cart.delivery_instructions': 'Istruzioni di consegna o note sulle allergie',
    'cart.contactless': 'Lascia alla porta (senza contatto)',
    'cart.payment.title': 'Ordina e Paga',
    'cart.payment.subtitle': 'Pagamento sicuro al tavolo',
    'cart.checkout_failed': 'Pagamento non riuscito',
    'cart.processing_payment': 'Elaborazione del pagamento...',
    'cart.connecting_gateway': 'Connessione al gateway sicuro...',
    'cart.payments_unavailable': 'Pagamenti non disponibili',
    'cart.payments_unavailable_desc': 'Questo locale non ha configurato pagamenti online.',
    'cart.pay_online': 'Paga online',
    'cart.credit_debit_card': 'Carta di credito o debito',
    'cart.or': 'Oppure',
    'cart.pay_courier_cash': 'Paga al corriere (Contanti)',
    'cart.pay_counter_cash': 'Paga alla cassa (Contanti)',
    'cart.secure_payment': 'Pagamento sicuro',
    'cart.courier_payment': 'Pagamento al corriere',
    'cart.counter_payment': 'Pagamento alla cassa',
    'cart.secure_payment_desc': 'Pagamento crittografato con carta attivo.',
    'cart.staff_payment_desc': 'Il pagamento sarà raccolto dal personale del locale.',
    'cart.received.title': 'Ordine ricevuto!',
    'cart.received.dinein': 'I piatti saranno serviti al tavolo a breve.',
    'cart.received.pickup': 'Abbiamo inviato l\'ordine in cucina. A presto!',
    'cart.received.close': 'Chiudi finestra',
    'cart.delivery_confirmed': 'Consegna confermata per',
    'cart.your_zone': 'la tua zona',
    'cart.loyalty_earned': 'Hai guadagnato',
    'cart.loyalty_points': 'Punti Fedeltà!',
    'cart.how_experience': 'Com\'è stata la tua esperienza?',
    'cart.improve_placeholder': 'Cosa possiamo migliorare?',
    'cart.send_feedback': 'Invia feedback privato',
    'cart.google_review': 'Lascia una recensione Google',
    'cart.thank_feedback': 'Grazie per la tua recensione!',
    'cart.items_subtotal': 'Articoli',
    'cart.delivery_fee': 'Consegna',
    'cart.delivery': 'Consegna',
    'cart.no_zone': 'Fuori zona',
    'cart.free': 'Gratis',
    'cart.packaging': 'Confezionamento',
    'cart.total_summary': 'Riepilogo totale',
    'tracker.order_received': 'Ordine ricevuto',
    'tracker.confirming_delivery': 'Il locale sta confermando la consegna',
    'tracker.waiting_kitchen': 'In attesa di approvazione cucina',
    'tracker.chef_cooking': 'Lo chef sta cucinando',
    'tracker.delivery_preparing': 'La tua consegna è in preparazione',
    'tracker.food_preparing': 'Il tuo cibo è in preparazione',
    'tracker.out_for_delivery': 'In consegna!',
    'tracker.ready_pickup': 'Pronto per il ritiro!',
    'tracker.courier_has_order': 'Il corriere ha preso in carico l\'ordine',
    'tracker.come_to_counter': 'Si prega di recarsi al banco',
    'tracker.delivered': 'Consegnato!',
    'tracker.enjoy_meal': 'Buon appetito!',
    'tracker.delivery_complete': 'Consegna completata',
    'tracker.order_completed': 'Ordine completato con successo',
    'tracker.updating_status': 'Aggiornamento in corso...',
    'tracker.order_again': 'Ordina di nuovo',
    'reservation.title': 'Prenota un tavolo',
    'reservation.subtitle': 'Unisciti a noi per un\'esperienza indimenticabile',
    'reservation.name': 'Nome completo',
    'reservation.name_placeholder': 'Il tuo nome',
    'reservation.phone': 'Numero di telefono',
    'reservation.email': 'Indirizzo e-mail',
    'reservation.guests': 'Ospiti',
    'reservation.date': 'Data',
    'reservation.time': 'Ora',
    'reservation.notes': 'Richieste particolari',
    'reservation.notes_placeholder': 'Allergie, compleanno, preferenze tavolo...',
    'reservation.failed': 'Prenotazione non riuscita',
    'reservation.error_generic': 'Impossibile inviare la prenotazione. Riprova.',
    'reservation.submitting': 'Prenotazione in corso...',
    'reservation.confirm': 'Conferma prenotazione',
    'reservation.requested': 'Richiesta di prenotazione inviata!',
    'reservation.requested_desc': 'Esamineremo la richiesta e invieremo conferma via email.',
    'reservation.done': 'Fatto',
    'loyalty.title': 'Programma Fedeltà',
    'loyalty.subtitle': 'Accumula punti a ogni ordine',
    'loyalty.welcome_back': 'Bentornato!',
    'loyalty.welcome_desc': 'Accedi per salvare i tuoi punti a ogni visita.',
    'loyalty.view': 'Vedi',
    'loyalty.enter_email': 'Inserisci la tua email',
    'loyalty.sending_code': 'Invio codice in corso...',
    'loyalty.send_code': 'Invia codice di verifica',
    'loyalty.enter_code_title': 'Codice di verifica',
    'loyalty.code_placeholder': 'Codice a 6 cifre',
    'loyalty.verifying': 'Verifica in corso...',
    'loyalty.verify_signin': 'Verifica e Accedi',
    'loyalty.back_email': 'Torna all\'email',
    'loyalty.available_points': 'Punti disponibili',
    'loyalty.saved_to': 'Registrato per',
    'loyalty.available_rewards': 'Premi disponibili',
    'loyalty.redeem': 'Riscatta',
    'loyalty.locked': 'Bloccato',
    'loyalty.no_rewards': 'Nessun premio disponibile al momento.',
    'loyalty.sign_out': 'Esci',
    'loyalty.switch_account': 'Cambia account',
    'footer.live_encryption': 'Crittografia in tempo reale attiva',
    'footer.powered_by': 'Offerto da',
    'footer.all_rights': 'Tutti i diritti riservati.',
    'menu.open_now': 'Aperto ora',
    'menu.closed': 'Chiuso',
    'menu.delivery_only': 'Solo consegna',
    'menu.book_table': 'Prenota tavolo',
    'menu.rewards': 'Premi',
    'menu.table': 'Tavolo',
    'menu.experience_culinary': "Vivi l'eccellenza culinaria",
    'menu.search_placeholder': 'Cerca piatti...',
    'menu.dietary_filters': 'Filtri dietetici',
    'menu.exclude_allergens': 'Escludi allergeni',
    'menu.no_allergen': 'Senza {allergen}',
    'dietary.vegetarian': 'Vegetariano',
    'dietary.vegan': 'Vegano',
    'dietary.spicy': 'Piccante',
    'dietary.gluten-free': 'Senza Glutine',
    'dietary.nut-free': 'Senza Frutta a Guscio',
    'dietary.dairy-free': 'Senza Latticini',
    'dietary.healthy': 'Salutare',
    'dietary.halal': 'Halal',
    'dietary.organic': 'Biologico',
    'dietary.keto': 'Keto',
    'allergen.nuts': 'Frutta a guscio',
    'allergen.dairy': 'Latticini',
    'allergen.gluten': 'Glutine',
    'allergen.shellfish': 'Crostacei',
    'allergen.soy': 'Soia',
    'allergen.eggs': 'Uova',
    'allergen.fish': 'Pesce',
    'menu.chefs_recommendations': 'Consigli dello Chef',
    'menu.chefs': 'Dello Chef',
    'menu.recommendations': 'Consigli',
    'menu.items': 'Piatti del menu',
    'menu.count': 'Articoli',
    'menu.section_closed': 'è chiuso',
    'menu.dine_in_closed_delivery_available': 'Il servizio in sala è chiuso. La consegna a domicilio è disponibile.',
    'menu.section_closed_notice': 'Questa sezione è attualmente chiusa.',
    'menu.available': 'DISPONIBILE',
    'menu.sold_out': 'ESAURITO',
    'menu.mins': 'MIN',
    'menu.running_low': 'In esaurimento',
    'menu.adjusted': '(MODIFICATO)',
    'menu.add_to_cart': 'Aggiungi al carrello',
    'menu.visual_composition': 'Composizione visiva',
    'menu.small_details': 'Dettagli',
    'menu.guest_sentiment': 'Opinioni degli ospiti',
    'menu.how_was_experience': "Com'è stata la tua esperienza?",
    'menu.thank_feedback': 'Grazie per il tuo feedback!',
    'menu.based_on_reviews': 'Basato su {count} recensioni',
    'menu.water': 'Acqua',
    'menu.bill': 'Conto',
    'menu.cart': 'Carrello',
    'menu.checkout': 'Cassa',
    'menu.requested': '{item} richiesto',
  },
  pt: {
    'menu.no_items': 'Nenhum item encontrado',
    'menu.try_adjusting': 'Tente ajustar a sua pesquisa ou filtros',
    'menu.section_closed_check_back': 'Esta secção encontra-se encerrada de momento. Por favor verifique o horário de funcionamento.',
    'menu.made_with': 'Confecionado com',
    'menu.rate_item': 'Avaliar este item',
    'menu.based_reviews': 'Com base em {count} avaliações',
    'menu.add_to_order': 'Adicionar ao pedido',
    'menu.all': 'Todos',
    'menu.departments': 'Departamentos do menu',
    'menu.learn_more': 'Saber mais',
    'cart.title': 'O seu Pedido',
    'cart.details': 'Detalhes de Entrega',
    'cart.confirmed': 'Confirmado!',
    'cart.empty': 'O carrinho está vazio',
    'cart.continue': 'Continuar para os detalhes',
    'cart.back': 'Voltar',
    'cart.proceed_payment': 'Seguir para pagamento',
    'cart.qty': 'Qtd',
    'cart.notes_placeholder': 'Personalizar: sem cebola, molho à parte, alergias…',
    'cart.upsell.title': 'Combinações perfeitas',
    'cart.upsell.add': 'Adicionar',
    'cart.perfect_match': 'Combinação Ideal',
    'cart.additional': 'Adicional',
    'cart.no_thanks': 'Não, obrigado',
    'cart.add_both': 'Adicionar ambos',
    'cart.limited_time_offer': 'Oferta exclusiva para esta sessão de mesa',
    'cart.modes.dine_in': 'No restaurante',
    'cart.modes.pickup': 'Takeaway',
    'cart.modes.delivery': 'Entrega',
    'cart.modes_unavailable': 'Modos de pedido indisponíveis de momento.',
    'cart.form.name': 'Nome completo',
    'cart.form.phone': 'Telefone',
    'cart.form.table': 'Número da mesa',
    'cart.form.address': 'Morada de entrega',
    'cart.form.table_placeholder': 'ex. 12',
    'cart.delivery_estimate': 'Estimativa de entrega',
    'cart.busy_estimate': 'estimativa em hora de ponta',
    'cart.asap': 'O mais rápido possível',
    'cart.schedule': 'Agendar',
    'cart.address_placeholder': 'Rua e bairro',
    'cart.apartment': 'Apartamento',
    'cart.floor': 'Andar',
    'cart.door_code': 'Código da porta',
    'cart.map_pin': 'Link GPS da localização',
    'cart.delivery_instructions': 'Instruções de entrega ou notas de alergias',
    'cart.contactless': 'Deixar à porta (sem contacto)',
    'cart.payment.title': 'Pedir e Pagar',
    'cart.payment.subtitle': 'Pagamento seguro à mesa',
    'cart.checkout_failed': 'Falha no pagamento',
    'cart.processing_payment': 'A processar pagamento...',
    'cart.connecting_gateway': 'A ligar ao gateway seguro...',
    'cart.payments_unavailable': 'Pagamentos indisponíveis',
    'cart.payments_unavailable_desc': 'Este local não tem pagamentos online ativos.',
    'cart.pay_online': 'Pagar online',
    'cart.credit_debit_card': 'Cartão de Crédito ou Débito',
    'cart.or': 'Ou',
    'cart.pay_courier_cash': 'Pagar ao estafeta (Dinheiro)',
    'cart.pay_counter_cash': 'Pagar no balcão (Dinheiro)',
    'cart.secure_payment': 'Pagamento seguro',
    'cart.courier_payment': 'Pagamento ao estafeta',
    'cart.counter_payment': 'Pagamento no balcão',
    'cart.secure_payment_desc': 'Pagamento seguro e encriptado ativo.',
    'cart.staff_payment_desc': 'O pagamento será recebido pela equipa do restaurante.',
    'cart.received.title': 'Pedido recebido!',
    'cart.received.dinein': 'Os seus pratos serão entregues na mesa em breve.',
    'cart.received.pickup': 'Enviámos o pedido para a cozinha. Até breve!',
    'cart.received.close': 'Fechar janela',
    'cart.delivery_confirmed': 'Entrega confirmada para',
    'cart.your_zone': 'a sua zona',
    'cart.loyalty_earned': 'Ganhou',
    'cart.loyalty_points': 'Pontos de Fidelidade!',
    'cart.how_experience': 'Como foi a sua experiência?',
    'cart.improve_placeholder': 'O que podemos melhorar?',
    'cart.send_feedback': 'Enviar comentário privado',
    'cart.google_review': 'Deixar avaliação no Google',
    'cart.thank_feedback': 'Obrigado pelo seu comentário!',
    'cart.items_subtotal': 'Itens',
    'cart.delivery_fee': 'Entrega',
    'cart.delivery': 'Entrega',
    'cart.no_zone': 'Fora da zona',
    'cart.free': 'Grátis',
    'cart.packaging': 'Embalagem',
    'cart.total_summary': 'Resumo total',
    'tracker.order_received': 'Pedido recebido',
    'tracker.confirming_delivery': 'O restaurante está a confirmar a sua entrega',
    'tracker.waiting_kitchen': 'A aguardar confirmação da cozinha',
    'tracker.chef_cooking': 'O chef está a cozinhar',
    'tracker.delivery_preparing': 'A sua entrega está em preparação',
    'tracker.food_preparing': 'A sua refeição está a ser confecionada',
    'tracker.out_for_delivery': 'Em rota de entrega!',
    'tracker.ready_pickup': 'Pronto para levantamento!',
    'tracker.courier_has_order': 'O estafeta recolheu o seu pedido',
    'tracker.come_to_counter': 'Por favor dirija-se ao balcão',
    'tracker.delivered': 'Entregue!',
    'tracker.enjoy_meal': 'Bom apetite!',
    'tracker.delivery_complete': 'A sua entrega está concluída',
    'tracker.order_completed': 'Pedido concluído com sucesso',
    'tracker.updating_status': 'A atualizar estado...',
    'tracker.order_again': 'Pedir novamente',
    'reservation.title': 'Reservar uma mesa',
    'reservation.subtitle': 'Junte-se a nós para uma experiência inesquecível',
    'reservation.name': 'Nome completo',
    'reservation.name_placeholder': 'O seu nome',
    'reservation.phone': 'Telefone',
    'reservation.email': 'Email',
    'reservation.guests': 'Pessoas',
    'reservation.date': 'Data',
    'reservation.time': 'Hora',
    'reservation.notes': 'Notas especiais',
    'reservation.notes_placeholder': 'Alergias, aniversário, preferência de mesa...',
    'reservation.failed': 'Falha na reserva',
    'reservation.error_generic': 'Não foi possível enviar a reserva. Tente de novo.',
    'reservation.submitting': 'A confirmar a sua mesa...',
    'reservation.confirm': 'Confirmar reserva',
    'reservation.requested': 'Reserva solicitada!',
    'reservation.requested_desc': 'Iremos analisar o seu pedido e enviar confirmação por email.',
    'reservation.done': 'Concluído',
    'loyalty.title': 'Programa de Fidelidade',
    'loyalty.subtitle': 'Acumule pontos em cada pedido',
    'loyalty.welcome_back': 'Bem-vindo de volta!',
    'loyalty.welcome_desc': 'Inicie sessão para guardar os seus pontos a cada visita.',
    'loyalty.view': 'Ver',
    'loyalty.enter_email': 'Introduza o seu email',
    'loyalty.sending_code': 'A enviar código...',
    'loyalty.send_code': 'Enviar código de verificação',
    'loyalty.enter_code_title': 'Código de verificação',
    'loyalty.code_placeholder': 'Código de 6 dígitos',
    'loyalty.verifying': 'A verificar...',
    'loyalty.verify_signin': 'Verificar e Entrar',
    'loyalty.back_email': 'Voltar ao email',
    'loyalty.available_points': 'Pontos disponíveis',
    'loyalty.saved_to': 'Guardado para',
    'loyalty.available_rewards': 'Recompensas disponíveis',
    'loyalty.redeem': 'Resgatar',
    'loyalty.locked': 'Bloqueado',
    'loyalty.no_rewards': 'Sem recompensas disponíveis de momento.',
    'loyalty.sign_out': 'Terminar sessão',
    'loyalty.switch_account': 'Mudar de conta',
    'footer.live_encryption': 'Encriptação em tempo real ativa',
    'footer.powered_by': 'Desenvolvido por',
    'footer.all_rights': 'Todos os direitos reservados.',
    'menu.open_now': 'Aberto agora',
    'menu.closed': 'Fechado',
    'menu.delivery_only': 'Apenas entrega',
    'menu.book_table': 'Reservar mesa',
    'menu.rewards': 'Recompensas',
    'menu.table': 'Mesa',
    'menu.experience_culinary': 'Experimente a excelência culinária',
    'menu.search_placeholder': 'Pesquisar itens...',
    'menu.dietary_filters': 'Filtros dietéticos',
    'menu.exclude_allergens': 'Excluir alergénios',
    'menu.no_allergen': 'Sem {allergen}',
    'dietary.vegetarian': 'Vegetariano',
    'dietary.vegan': 'Vegano',
    'dietary.spicy': 'Picante',
    'dietary.gluten-free': 'Sem Glúten',
    'dietary.nut-free': 'Sem Nozes',
    'dietary.dairy-free': 'Sem Laticínios',
    'dietary.healthy': 'Saudável',
    'dietary.halal': 'Halal',
    'dietary.organic': 'Biológico',
    'dietary.keto': 'Keto',
    'allergen.nuts': 'Nozes',
    'allergen.dairy': 'Laticínios',
    'allergen.gluten': 'Glúten',
    'allergen.shellfish': 'Marisco',
    'allergen.soy': 'Soja',
    'allergen.eggs': 'Ovos',
    'allergen.fish': 'Peixe',
    'menu.chefs_recommendations': 'Recomendações do Chef',
    'menu.chefs': 'Do Chef',
    'menu.recommendations': 'Recomendações',
    'menu.items': 'Itens do menu',
    'menu.count': 'Itens',
    'menu.section_closed': 'está fechado',
    'menu.dine_in_closed_delivery_available': 'O serviço no local está fechado. A entrega está disponível.',
    'menu.section_closed_notice': 'Esta seção está atualmente fechada.',
    'menu.available': 'DISPONÍVEL',
    'menu.sold_out': 'ESGOTADO',
    'menu.mins': 'MIN',
    'menu.running_low': 'Quase esgotado',
    'menu.adjusted': '(AJUSTADO)',
    'menu.add_to_cart': 'Adicionar ao carrinho',
    'menu.visual_composition': 'Composição visual',
    'menu.small_details': 'Detalhes',
    'menu.guest_sentiment': 'Opiniões dos clientes',
    'menu.how_was_experience': 'Como foi a sua experiência?',
    'menu.thank_feedback': 'Obrigado pela sua avaliação!',
    'menu.based_on_reviews': 'Com base em {count} avaliações',
    'menu.water': 'Água',
    'menu.bill': 'Conta',
    'menu.cart': 'Carrinho',
    'menu.checkout': 'Finalizar',
    'menu.requested': '{item} solicitado',
  },
  ru: {
    'menu.no_items': 'Ничего не найдено',
    'menu.try_adjusting': 'Попробуйте изменить поиск или фильтры',
    'menu.section_closed_check_back': 'Этот раздел сейчас закрыт. Пожалуйста, возвращайтесь в рабочие часы.',
    'menu.made_with': 'Состав',
    'menu.rate_item': 'Оценить это блюдо',
    'menu.based_reviews': 'На основе {count} отзывов',
    'menu.add_to_order': 'Добавить в заказ',
    'menu.all': 'Все',
    'menu.departments': 'Разделы меню',
    'menu.learn_more': 'Подробнее',
    'cart.title': 'Ваш заказ',
    'cart.details': 'Детали доставки',
    'cart.confirmed': 'Подтверждено!',
    'cart.empty': 'Ваша корзина пуста',
    'cart.continue': 'Перейти к деталям',
    'cart.back': 'Назад',
    'cart.proceed_payment': 'Перейти к оплате',
    'cart.qty': 'Кол-во',
    'cart.notes_placeholder': 'Пожелания: без лука, соус отдельно, аллергии…',
    'cart.upsell.title': 'Идеальные сочетания',
    'cart.upsell.add': 'Добавить',
    'cart.perfect_match': 'Идеальная пара',
    'cart.additional': 'Дополнительно',
    'cart.no_thanks': 'Нет, спасибо',
    'cart.add_both': 'Добавить оба',
    'cart.limited_time_offer': 'Специальное предложение для вашего столика',
    'cart.modes.dine_in': 'В заведении',
    'cart.modes.pickup': 'Самовывоз',
    'cart.modes.delivery': 'Доставка',
    'cart.modes_unavailable': 'Способы заказа временно недоступны.',
    'cart.form.name': 'Полное имя',
    'cart.form.phone': 'Номер телефона',
    'cart.form.table': 'Номер столика',
    'cart.form.address': 'Адрес доставки',
    'cart.form.table_placeholder': 'напр. 12',
    'cart.delivery_estimate': 'Ожидаемое время доставки',
    'cart.busy_estimate': 'в часы пик',
    'cart.asap': 'Как можно скорее',
    'cart.schedule': 'Запланировать',
    'cart.address_placeholder': 'Улица и дом',
    'cart.apartment': 'Квартира',
    'cart.floor': 'Этаж',
    'cart.door_code': 'Код домофона',
    'cart.map_pin': 'Ссылка на карту / метка',
    'cart.delivery_instructions': 'Инструкции для курьера или аллергии',
    'cart.contactless': 'Оставить у двери (бесконтактно)',
    'cart.payment.title': 'Заказ и оплата',
    'cart.payment.subtitle': 'Безопасная оплата за столиком',
    'cart.checkout_failed': 'Ошибка оплаты',
    'cart.processing_payment': 'Обработка платежа...',
    'cart.connecting_gateway': 'Подключение к платежному шлюзу...',
    'cart.payments_unavailable': 'Оплата недоступна',
    'cart.payments_unavailable_desc': 'Заведение не настроило онлайн-оплату.',
    'cart.pay_online': 'Оплатить онлайн',
    'cart.credit_debit_card': 'Банковская карта',
    'cart.or': 'Или',
    'cart.pay_courier_cash': 'Наличными курьеру',
    'cart.pay_counter_cash': 'Наличными на кассе',
    'cart.secure_payment': 'Безопасная оплата',
    'cart.courier_payment': 'Оплата курьеру',
    'cart.counter_payment': 'Оплата на кассе',
    'cart.secure_payment_desc': 'Зашифрованная оплата картой активна.',
    'cart.staff_payment_desc': 'Оплата принимается персоналом заведения.',
    'cart.received.title': 'Заказ принят!',
    'cart.received.dinein': 'Ваши блюда скоро будут поданы к столику.',
    'cart.received.pickup': 'Заказ передан на кухню. Скоро будет готово!',
    'cart.received.close': 'Закрыть окно',
    'cart.delivery_confirmed': 'Доставка подтверждена для',
    'cart.your_zone': 'вашей зоны',
    'cart.loyalty_earned': 'Вы получили',
    'cart.loyalty_points': 'баллов лояльности!',
    'cart.how_experience': 'Как прошел ваш заказ?',
    'cart.improve_placeholder': 'Что мы можем улучшить?',
    'cart.send_feedback': 'Отправить отзыв',
    'cart.google_review': 'Оставить отзыв в Google',
    'cart.thank_feedback': 'Спасибо за ваш отзыв!',
    'cart.items_subtotal': 'Блюда',
    'cart.delivery_fee': 'Доставка',
    'cart.delivery': 'Доставка',
    'cart.no_zone': 'Вне зоны',
    'cart.free': 'Бесплатно',
    'cart.packaging': 'Упаковка',
    'cart.total_summary': 'Итого к оплате',
    'tracker.order_received': 'Заказ получен',
    'tracker.confirming_delivery': 'Заведение подтверждает доставку',
    'tracker.waiting_kitchen': 'Ожидание подтверждения кухни',
    'tracker.chef_cooking': 'Шеф готовит блюда',
    'tracker.delivery_preparing': 'Ваш заказ на доставку собирается',
    'tracker.food_preparing': 'Ваши блюда готовятся',
    'tracker.out_for_delivery': 'Курьер в пути!',
    'tracker.ready_pickup': 'Готово к выдаче!',
    'tracker.courier_has_order': 'Курьер забрал ваш заказ',
    'tracker.come_to_counter': 'Пожалуйста, подойдите к стойке',
    'tracker.delivered': 'Доставлено!',
    'tracker.enjoy_meal': 'Приятного аппетита!',
    'tracker.delivery_complete': 'Доставка завершена',
    'tracker.order_completed': 'Заказ успешно выполнен',
    'tracker.updating_status': 'Обновление статуса...',
    'tracker.order_again': 'Заказать снова',
    'reservation.title': 'Забронировать столик',
    'reservation.subtitle': 'Проведите незабываемый вечер с нами',
    'reservation.name': 'Полное имя',
    'reservation.name_placeholder': 'Ваше имя',
    'reservation.phone': 'Номер телефона',
    'reservation.email': 'Электронная почта',
    'reservation.guests': 'Гости',
    'reservation.date': 'Дата',
    'reservation.time': 'Время',
    'reservation.notes': 'Особые пожелания',
    'reservation.notes_placeholder': 'Аллергии, праздник, пожелания по столику...',
    'reservation.failed': 'Ошибка бронирования',
    'reservation.error_generic': 'Не удалось отправить бронирование. Попробуйте снова.',
    'reservation.submitting': 'Бронируем столик...',
    'reservation.confirm': 'Подтвердить бронь',
    'reservation.requested': 'Заявка на бронь отправлена!',
    'reservation.requested_desc': 'Мы проверим заявку и вышлем подтверждение на ваш email.',
    'reservation.done': 'Готово',
    'loyalty.title': 'Программа лояльности',
    'loyalty.subtitle': 'Копите баллы с каждого заказа',
    'loyalty.welcome_back': 'С возвращением!',
    'loyalty.welcome_desc': 'Войдите, чтобы сохранять баллы за каждый визит.',
    'loyalty.view': 'Смотреть',
    'loyalty.enter_email': 'Введите ваш email',
    'loyalty.sending_code': 'Отправка кода...',
    'loyalty.send_code': 'Отправить код подтверждения',
    'loyalty.enter_code_title': 'Введите код подтверждения',
    'loyalty.code_placeholder': '6-значный код',
    'loyalty.verifying': 'Проверка...',
    'loyalty.verify_signin': 'Подтвердить и войти',
    'loyalty.back_email': 'Назад к вводу email',
    'loyalty.available_points': 'Доступные баллы',
    'loyalty.saved_to': 'Привязано к',
    'loyalty.available_rewards': 'Доступные награды',
    'loyalty.redeem': 'Обменять',
    'loyalty.locked': 'Заблокировано',
    'loyalty.no_rewards': 'Награды пока недоступны.',
    'loyalty.sign_out': 'Выйти',
    'loyalty.switch_account': 'Сменить аккаунт',
    'footer.live_encryption': 'Шифрование в реальном времени активно',
    'footer.powered_by': 'Работает на платформе',
    'footer.all_rights': 'Все права защищены.',
    'menu.open_now': 'Открыто сейчас',
    'menu.closed': 'Закрыто',
    'menu.delivery_only': 'Только доставка',
    'menu.book_table': 'Забронировать стол',
    'menu.rewards': 'Награды',
    'menu.table': 'Стол',
    'menu.experience_culinary': 'Почувствуйте кулинарное совершенство',
    'menu.search_placeholder': 'Поиск блюд...',
    'menu.dietary_filters': 'Диетические фильтры',
    'menu.exclude_allergens': 'Исключить аллергены',
    'menu.no_allergen': 'Без {allergen}',
    'dietary.vegetarian': 'Вегетарианское',
    'dietary.vegan': 'Веганское',
    'dietary.spicy': 'Острое',
    'dietary.gluten-free': 'Без глютена',
    'dietary.nut-free': 'Без орехов',
    'dietary.dairy-free': 'Без лактозы',
    'dietary.healthy': 'Здоровое',
    'dietary.halal': 'Халяль',
    'dietary.organic': 'Органическое',
    'dietary.keto': 'Кето',
    'allergen.nuts': 'Орехи',
    'allergen.dairy': 'Молочные продукты',
    'allergen.gluten': 'Глютен',
    'allergen.shellfish': 'Моллюски',
    'allergen.soy': 'Соя',
    'allergen.eggs': 'Яйца',
    'allergen.fish': 'Рыба',
    'menu.chefs_recommendations': 'Рекомендации шефа',
    'menu.chefs': 'Шеф',
    'menu.recommendations': 'Рекомендации',
    'menu.items': 'Блюда меню',
    'menu.count': 'позиций',
    'menu.section_closed': 'закрыт',
    'menu.dine_in_closed_delivery_available': 'Зал закрыт. Доступна доставка для этого раздела.',
    'menu.section_closed_notice': 'Этот раздел сейчас закрыт.',
    'menu.available': 'ДОСТУПНО',
    'menu.sold_out': 'РАСПРОДАНО',
    'menu.mins': 'МИН',
    'menu.running_low': 'Заканчивается',
    'menu.adjusted': '(СКОРРЕКТИРОВАНО)',
    'menu.add_to_cart': 'В корзину',
    'menu.visual_composition': 'Состав блюда',
    'menu.small_details': 'Детали',
    'menu.guest_sentiment': 'Отзывы гостей',
    'menu.how_was_experience': 'Как вам ваши впечатления?',
    'menu.thank_feedback': 'Спасибо за ваш отзыв!',
    'menu.based_on_reviews': 'На основе {count} отзывов',
    'menu.water': 'Вода',
    'menu.bill': 'Счет',
    'menu.cart': 'Корзина',
    'menu.checkout': 'Оформить заказ',
    'menu.requested': '{item} запрошен',
  },
  zh: {
    'menu.no_items': '未找到菜品',
    'menu.try_adjusting': '请尝试调整搜索词或筛选条件',
    'menu.section_closed_check_back': '该专区当前已打烊，请在营业时间内查看，或浏览上方其他营业菜单。',
    'menu.made_with': '食材配料',
    'menu.rate_item': '为此菜品评分',
    'menu.based_reviews': '基于 {count} 条真实评价',
    'menu.add_to_order': '加入订单',
    'menu.all': '全部',
    'menu.departments': '菜单部门',
    'menu.learn_more': '了解更多',
    'cart.title': '您的订单',
    'cart.details': '配送详情',
    'cart.confirmed': '已确认！',
    'cart.empty': '购物车是空的',
    'cart.continue': '继续填写详情',
    'cart.back': '返回',
    'cart.proceed_payment': '前往支付',
    'cart.qty': '数量',
    'cart.notes_placeholder': '口味备注：免葱、酱料另放、过敏原…',
    'cart.upsell.title': '绝佳搭配推荐',
    'cart.upsell.add': '添加',
    'cart.perfect_match': '完美搭配',
    'cart.additional': '额外加点',
    'cart.no_thanks': '不用了，谢谢',
    'cart.add_both': '两样一起加',
    'cart.limited_time_offer': '本桌限时推荐特惠',
    'cart.modes.dine_in': '堂食',
    'cart.modes.pickup': '自提',
    'cart.modes.delivery': '外卖配送',
    'cart.modes_unavailable': '暂不支持点餐模式',
    'cart.form.name': '姓名',
    'cart.form.phone': '联系电话',
    'cart.form.table': '桌号',
    'cart.form.address': '配送地址',
    'cart.form.table_placeholder': '例如：12',
    'cart.delivery_estimate': '预计送达时间',
    'cart.busy_estimate': '高峰期预计',
    'cart.asap': '尽快送达',
    'cart.schedule': '预约时间',
    'cart.address_placeholder': '街道及门牌号',
    'cart.apartment': '公寓/单元',
    'cart.floor': '楼层',
    'cart.door_code': '门禁密码',
    'cart.map_pin': '地图定位链接',
    'cart.delivery_instructions': '配送备注、过敏原或交接说明',
    'cart.contactless': '无接触配送（放在门口）',
    'cart.payment.title': '下单并支付',
    'cart.payment.subtitle': '安全桌边结算',
    'cart.checkout_failed': '支付失败',
    'cart.processing_payment': '正在处理支付...',
    'cart.connecting_gateway': '正在连接安全支付网关...',
    'cart.payments_unavailable': '暂无可用支付方式',
    'cart.payments_unavailable_desc': '该门店暂未开通在线支付功能。',
    'cart.pay_online': '在线支付',
    'cart.credit_debit_card': '信用卡或借记卡',
    'cart.or': '或',
    'cart.pay_courier_cash': '货到付款（现金）',
    'cart.pay_counter_cash': '前台现金支付',
    'cart.secure_payment': '安全加密支付',
    'cart.courier_payment': '配送员收款',
    'cart.counter_payment': '前台收款',
    'cart.secure_payment_desc': '本餐厅已启用全程加密银行卡结算。',
    'cart.staff_payment_desc': '将由餐厅工作人员现场收取款项。',
    'cart.received.title': '订单已收到！',
    'cart.received.dinein': '菜品将很快为您送至餐桌。',
    'cart.received.pickup': '厨房已收到您的订单，请稍候！',
    'cart.received.close': '关闭窗口',
    'cart.delivery_confirmed': '已确认配送至',
    'cart.your_zone': '您的区域',
    'cart.loyalty_earned': '本次获得',
    'cart.loyalty_points': '积分！',
    'cart.how_experience': '您对本次点餐体验满意吗？',
    'cart.improve_placeholder': '我们还有哪些可以改进的地方？',
    'cart.send_feedback': '发送私密反馈',
    'cart.google_review': '在 Google 上留下评价',
    'cart.thank_feedback': '感谢您的宝贵意见！',
    'cart.items_subtotal': '商品小计',
    'cart.delivery_fee': '配送费',
    'cart.delivery': '配送',
    'cart.no_zone': '超出配送范围',
    'cart.free': '免费',
    'cart.packaging': '包装费',
    'cart.total_summary': '合计总额',
    'tracker.order_received': '订单已接收',
    'tracker.confirming_delivery': '商家正在确认配送订单',
    'tracker.waiting_kitchen': '等待厨房接单',
    'tracker.chef_cooking': '大厨正在烹饪',
    'tracker.delivery_preparing': '外卖正在打包准备中',
    'tracker.food_preparing': '美食正在用心制作中',
    'tracker.out_for_delivery': '骑士正在配送中！',
    'tracker.ready_pickup': '餐品已备好，请取餐！',
    'tracker.courier_has_order': '骑手已取餐并出发',
    'tracker.come_to_counter': '请前往柜台取餐',
    'tracker.delivered': '已送达！',
    'tracker.enjoy_meal': '祝您用餐愉快！',
    'tracker.delivery_complete': '配送已完成',
    'tracker.order_completed': '订单已圆满完成',
    'tracker.updating_status': '正在更新状态...',
    'tracker.order_again': '再来一单',
    'reservation.title': '预订餐桌',
    'reservation.subtitle': '欢迎莅临，尊享非凡美食之旅',
    'reservation.name': '姓名',
    'reservation.name_placeholder': '您的姓名',
    'reservation.phone': '手机号码',
    'reservation.email': '电子邮箱',
    'reservation.guests': '就餐人数',
    'reservation.date': '日期',
    'reservation.time': '时间',
    'reservation.notes': '特别需求',
    'reservation.notes_placeholder': '过敏忌口、生日纪念、靠窗座位偏好等...',
    'reservation.failed': '预订提交失败',
    'reservation.error_generic': '抱歉，无法提交您的预订，请重试。',
    'reservation.submitting': '正在为您锁定餐位...',
    'reservation.confirm': '确认预订',
    'reservation.requested': '预订申请已发送！',
    'reservation.requested_desc': '我们将尽快审核您的申请并发送确认邮件。',
    'reservation.done': '完成',
    'loyalty.title': '会员积分计划',
    'loyalty.subtitle': '每笔订单均可累积积分',
    'loyalty.welcome_back': '欢迎回来！',
    'loyalty.welcome_desc': '登录以保存您的专属积分并兑换好礼。',
    'loyalty.view': '查看',
    'loyalty.enter_email': '请输入您的邮箱',
    'loyalty.sending_code': '正在发送验证码...',
    'loyalty.send_code': '发送验证码',
    'loyalty.enter_code_title': '输入验证码',
    'loyalty.code_placeholder': '6位数验证码',
    'loyalty.verifying': '正在验证...',
    'loyalty.verify_signin': '验证并登录',
    'loyalty.back_email': '返回修改邮箱',
    'loyalty.available_points': '当前可用积分',
    'loyalty.saved_to': '绑定账号：',
    'loyalty.available_rewards': '可兑换奖励',
    'loyalty.redeem': '立即兑换',
    'loyalty.locked': '积分不足',
    'loyalty.no_rewards': '暂无可用奖励。',
    'loyalty.sign_out': '退出登录',
    'loyalty.switch_account': '切换账号',
    'footer.live_encryption': '实时端到端加密保护',
    'footer.powered_by': '技术支持：',
    'footer.all_rights': '保留所有权利。',
    'menu.open_now': '正在营业',
    'menu.closed': '已打烊',
    'menu.delivery_only': '仅限外送',
    'menu.book_table': '预订座位',
    'menu.rewards': '会员奖励',
    'menu.table': '桌号',
    'menu.experience_culinary': '体验卓越美食',
    'menu.search_placeholder': '搜索美食...',
    'menu.dietary_filters': '饮食偏好',
    'menu.exclude_allergens': '排除过敏原',
    'menu.no_allergen': '无 {allergen}',
    'dietary.vegetarian': '素食',
    'dietary.vegan': '纯素',
    'dietary.spicy': '辣',
    'dietary.gluten-free': '无麸质',
    'dietary.nut-free': '不含坚果',
    'dietary.dairy-free': '无乳制品',
    'dietary.healthy': '健康',
    'dietary.halal': '清真',
    'dietary.organic': '有机',
    'dietary.keto': '生酮',
    'allergen.nuts': '坚果',
    'allergen.dairy': '乳制品',
    'allergen.gluten': '麸质',
    'allergen.shellfish': '甲壳类',
    'allergen.soy': '大豆',
    'allergen.eggs': '蛋类',
    'allergen.fish': '鱼类',
    'menu.chefs_recommendations': '主厨推荐',
    'menu.chefs': '主厨',
    'menu.recommendations': '特别推荐',
    'menu.items': '菜单项目',
    'menu.count': '项',
    'menu.section_closed': '已关闭',
    'menu.dine_in_closed_delivery_available': '堂食已关闭，此外送服务可用。',
    'menu.section_closed_notice': '该区域目前已关闭。',
    'menu.available': '有货',
    'menu.sold_out': '已售罄',
    'menu.mins': '分钟',
    'menu.running_low': '库存紧张',
    'menu.adjusted': '(已调整)',
    'menu.add_to_cart': '加入购物车',
    'menu.visual_composition': '食材构成',
    'menu.small_details': '细节',
    'menu.guest_sentiment': '顾客评价',
    'menu.how_was_experience': '您的体验如何？',
    'menu.thank_feedback': '感谢您的反馈！',
    'menu.based_on_reviews': '基于 {count} 条评价',
    'menu.water': '水',
    'menu.bill': '结账',
    'menu.cart': '购物车',
    'menu.checkout': '去结算',
    'menu.requested': '已请求 {item}',
  },
  ja: {
    'menu.no_items': '該当するメニューがありません',
    'menu.try_adjusting': '検索条件やフィルターを変更してお試しください',
    'menu.section_closed_check_back': 'このセクションは現在クローズしています。営業時間内にご確認いただくか、他のメニューをご覧ください。',
    'menu.made_with': '使用素材',
    'menu.rate_item': 'このメニューを評価',
    'menu.based_reviews': '{count} 件のレビューに基づく',
    'menu.add_to_order': '注文に追加',
    'menu.all': 'すべて',
    'menu.departments': 'メニュー部門',
    'menu.learn_more': '詳細を見る',
    'cart.title': 'ご注文内容',
    'cart.details': 'お届け先情報',
    'cart.confirmed': '注文確定！',
    'cart.empty': 'カートは空です',
    'cart.continue': '詳細入力へ進む',
    'cart.back': '戻る',
    'cart.proceed_payment': 'お支払いへ進む',
    'cart.qty': '数量',
    'cart.notes_placeholder': 'ご要望：ネギ抜き、タレ別添え、アレルギーなど…',
    'cart.upsell.title': 'おすすめのペアリング',
    'cart.upsell.add': '追加',
    'cart.perfect_match': 'おすすめ組み合わせ',
    'cart.additional': '追加料金',
    'cart.no_thanks': '結構です',
    'cart.add_both': '両方追加する',
    'cart.limited_time_offer': 'このテーブル限定のおすすめオファー',
    'cart.modes.dine_in': '店内飲食',
    'cart.modes.pickup': 'テイクアウト',
    'cart.modes.delivery': 'デリバリー',
    'cart.modes_unavailable': '現在ご注文いただけません。',
    'cart.form.name': 'お名前',
    'cart.form.phone': 'お電話番号',
    'cart.form.table': 'テーブル番号',
    'cart.form.address': '配達先住所',
    'cart.form.table_placeholder': '例: 12',
    'cart.delivery_estimate': '配達予定時間',
    'cart.busy_estimate': '混雑時目安',
    'cart.asap': '最短でお届け',
    'cart.schedule': '時間指定',
    'cart.address_placeholder': '市区町村・番地',
    'cart.apartment': '建物名・部屋番号',
    'cart.floor': '階数',
    'cart.door_code': 'オートロック番号',
    'cart.map_pin': '地図位置リンク',
    'cart.delivery_instructions': '配達時の注意点・アレルギーなど',
    'cart.contactless': '置き配（非接触受取）',
    'cart.payment.title': '注文・お支払い',
    'cart.payment.subtitle': 'テーブルでの安全な決済',
    'cart.checkout_failed': '決済に失敗しました',
    'cart.processing_payment': '決済処理中...',
    'cart.connecting_gateway': '安全な決済ゲートウェイに接続中...',
    'cart.payments_unavailable': '決済方法がありません',
    'cart.payments_unavailable_desc': 'オンライン決済は現在設定されていません。',
    'cart.pay_online': 'オンライン決済',
    'cart.credit_debit_card': 'クレジットカード・デビットカード',
    'cart.or': 'または',
    'cart.pay_courier_cash': '配達員に現金でお支払い',
    'cart.pay_counter_cash': 'レジにて現金でお支払い',
    'cart.secure_payment': '安全な決済',
    'cart.courier_payment': '配達時支払い',
    'cart.counter_payment': 'レジ支払い',
    'cart.secure_payment_desc': '安全な暗号化決済が有効です。',
    'cart.staff_payment_desc': 'スタッフが直接お会計を承ります。',
    'cart.received.title': 'ご注文を受け付けました！',
    'cart.received.dinein': 'お料理をテーブルまでお届けいたします。',
    'cart.received.pickup': '厨房に注文を送信しました。お待ちください！',
    'cart.received.close': '閉じる',
    'cart.delivery_confirmed': 'お届け確定エリア：',
    'cart.your_zone': '該当エリア',
    'cart.loyalty_earned': '獲得ポイント：',
    'cart.loyalty_points': 'ポイント！',
    'cart.how_experience': 'ご注文体験はいかがでしたか？',
    'cart.improve_placeholder': '改善点などがございましたらお聞かせください',
    'cart.send_feedback': 'ご意見を送信',
    'cart.google_review': 'Googleで口コミを書く',
    'cart.thank_feedback': 'フィードバックありがとうございます！',
    'cart.items_subtotal': '商品小計',
    'cart.delivery_fee': '配送料',
    'cart.delivery': '配送料',
    'cart.no_zone': '配達対象外',
    'cart.free': '無料',
    'cart.packaging': '容器・包装代',
    'cart.total_summary': '合計金額',
    'tracker.order_received': '注文受付完了',
    'tracker.confirming_delivery': '店舗が注文を確認しています',
    'tracker.waiting_kitchen': '厨房の受付待ち',
    'tracker.chef_cooking': '調理中',
    'tracker.delivery_preparing': '配達の準備をしています',
    'tracker.food_preparing': 'お料理を準備しています',
    'tracker.out_for_delivery': '配達中！',
    'tracker.ready_pickup': 'お受取りいただけます！',
    'tracker.courier_has_order': '配達員がお料理をお預かりしました',
    'tracker.come_to_counter': 'お受取りカウンターまでお越しください',
    'tracker.delivered': 'お届け完了！',
    'tracker.enjoy_meal': 'どうぞごゆっくりお召し上がりください！',
    'tracker.delivery_complete': '配達が完了しました',
    'tracker.order_completed': 'ご注文が完了しました',
    'tracker.updating_status': 'ステータス更新中...',
    'tracker.order_again': 'もう一度注文する',
    'reservation.title': 'テーブル予約',
    'reservation.subtitle': '特別なひとときをお過ごしください',
    'reservation.name': 'お名前',
    'reservation.name_placeholder': 'お名前を入力',
    'reservation.phone': 'お電話番号',
    'reservation.email': 'メールアドレス',
    'reservation.guests': '人数',
    'reservation.date': '日付',
    'reservation.time': '時間',
    'reservation.notes': '特別なご要望',
    'reservation.notes_placeholder': 'アレルギー、記念日、お席のご希望など...',
    'reservation.failed': '予約に失敗しました',
    'reservation.error_generic': '予約を送信できませんでした。もう一度お試しください。',
    'reservation.submitting': '予約手続き中...',
    'reservation.confirm': '予約を確定する',
    'reservation.requested': '予約リクエストを送信しました！',
    'reservation.requested_desc': '内容を確認のうえ、メールにてご連絡いたします。',
    'reservation.done': '完了',
    'loyalty.title': 'ロイヤルティプログラム',
    'loyalty.subtitle': 'ご注文ごとにポイントが貯まります',
    'loyalty.welcome_back': 'おかえりなさいませ！',
    'loyalty.welcome_desc': 'ログインしてポイントを管理・特典と交換できます。',
    'loyalty.view': '確認',
    'loyalty.enter_email': 'メールアドレスを入力',
    'loyalty.sending_code': '認証コードを送信中...',
    'loyalty.send_code': '認証コードを送信',
    'loyalty.enter_code_title': '認証コードの入力',
    'loyalty.code_placeholder': '6桁のコード',
    'loyalty.verifying': '認証中...',
    'loyalty.verify_signin': '認証してログイン',
    'loyalty.back_email': 'メールアドレス再入力へ戻る',
    'loyalty.available_points': '利用可能ポイント',
    'loyalty.saved_to': '登録メールアドレス：',
    'loyalty.available_rewards': '交換可能な特典',
    'loyalty.redeem': '交換する',
    'loyalty.locked': 'ポイント不足',
    'loyalty.no_rewards': '現在利用可能な特典はありません。',
    'loyalty.sign_out': 'ログアウト',
    'loyalty.switch_account': 'アカウントを切り替える',
    'footer.live_encryption': 'リアルタイム暗号化保護中',
    'footer.powered_by': '提供：',
    'footer.all_rights': '無断転載を禁じます。',
    'menu.open_now': '営業中',
    'menu.closed': '閉店',
    'menu.delivery_only': 'デリバリーのみ',
    'menu.book_table': '席を予約',
    'menu.rewards': 'リワード',
    'menu.table': 'テーブル',
    'menu.experience_culinary': '最高の美食体験を',
    'menu.search_placeholder': '料理を検索...',
    'menu.dietary_filters': '食事制限フィルター',
    'menu.exclude_allergens': 'アレルゲンを除外',
    'menu.no_allergen': '{allergen}なし',
    'dietary.vegetarian': 'ベジタリアン',
    'dietary.vegan': 'ヴィーガン',
    'dietary.spicy': '辛口',
    'dietary.gluten-free': 'グルテンフリー',
    'dietary.nut-free': 'ナッツ不使用',
    'dietary.dairy-free': '乳製品不使用',
    'dietary.healthy': 'ヘルシー',
    'dietary.halal': 'ハラール',
    'dietary.organic': 'オーガニック',
    'dietary.keto': 'ケト',
    'allergen.nuts': 'ナッツ',
    'allergen.dairy': '乳製品',
    'allergen.gluten': 'グルテン',
    'allergen.shellfish': '甲殻類',
    'allergen.soy': '大豆',
    'allergen.eggs': '卵',
    'allergen.fish': '魚',
    'menu.chefs_recommendations': 'シェフのおすすめ',
    'menu.chefs': 'シェフの',
    'menu.recommendations': 'おすすめ',
    'menu.items': 'メニュー項目',
    'menu.count': '品',
    'menu.section_closed': 'は閉店しています',
    'menu.dine_in_closed_delivery_available': '店内飲食は終了しました。デリバリーをご利用いただけます。',
    'menu.section_closed_notice': 'このセクションは現在閉店しています。',
    'menu.available': '注文可能',
    'menu.sold_out': '売り切れ',
    'menu.mins': '分',
    'menu.running_low': '残りわずか',
    'menu.adjusted': '(調整済み)',
    'menu.add_to_cart': 'カートに追加',
    'menu.visual_composition': '素材の構成',
    'menu.small_details': '詳細',
    'menu.guest_sentiment': 'お客様の声',
    'menu.how_was_experience': 'いかがでしたか？',
    'menu.thank_feedback': 'ご意見ありがとうございます！',
    'menu.based_on_reviews': '{count} 件のレビューに基づく',
    'menu.water': 'お冷',
    'menu.bill': 'お会計',
    'menu.cart': 'カート',
    'menu.checkout': '注文手続き',
    'menu.requested': '{item}をリクエストしました',
  },
  nl: {
    'menu.no_items': 'Geen items gevonden',
    'menu.try_adjusting': 'Probeer uw zoekopdracht of filters aan te passen',
    'menu.section_closed_check_back': 'Deze sectie is momenteel gesloten. Kom terug tijdens openingstijden.',
    'menu.made_with': 'Bereid met',
    'menu.rate_item': 'Beoordeel dit item',
    'menu.based_reviews': 'Gebaseerd op {count} beoordelingen',
    'menu.add_to_order': 'Toevoegen aan bestelling',
    'menu.all': 'Alles',
    'menu.departments': 'Menucategorieën',
    'menu.learn_more': 'Meer informatie',
    'cart.title': 'Uw Bestelling',
    'cart.details': 'Bezorggegevens',
    'cart.confirmed': 'Bevestigd!',
    'cart.empty': 'Uw winkelmand is leeg',
    'cart.continue': 'Verder naar gegevens',
    'cart.back': 'Terug',
    'cart.proceed_payment': 'Naar betaling',
    'cart.qty': 'Aantal',
    'cart.notes_placeholder': 'Aanpassen: geen ui, saus apart, allergieën…',
    'cart.upsell.title': 'Perfecte combinaties',
    'cart.upsell.add': 'Toevoegen',
    'cart.perfect_match': 'Perfecte Match',
    'cart.additional': 'Extra',
    'cart.no_thanks': 'Nee, bedankt',
    'cart.add_both': 'Beide toevoegen',
    'cart.limited_time_offer': 'Tijdelijke aanbieding voor deze tafel',
    'cart.modes.dine_in': 'In het restaurant',
    'cart.modes.pickup': 'Afhalen',
    'cart.modes.delivery': 'Bezorging',
    'cart.modes_unavailable': 'Bestelopties momenteel niet beschikbaar.',
    'cart.form.name': 'Volledige naam',
    'cart.form.phone': 'Telefoonnummer',
    'cart.form.table': 'Tafelnummer',
    'cart.form.address': 'Bezorgadres',
    'cart.form.table_placeholder': 'bijv. 12',
    'cart.delivery_estimate': 'Geschatte bezorgtijd',
    'cart.busy_estimate': 'geschatte piektijd',
    'cart.asap': 'Zo snel mogelijk',
    'cart.schedule': 'Inplannen',
    'cart.address_placeholder': 'Straat en huisnummer',
    'cart.apartment': 'Appartement / Bus',
    'cart.floor': 'Verdieping',
    'cart.door_code': 'Deurcode',
    'cart.map_pin': 'Kaartlocatie link',
    'cart.delivery_instructions': 'Bezorginstructies of allergieën',
    'cart.contactless': 'Aan de deur achterlaten (contactloos)',
    'cart.payment.title': 'Bestellen & Betalen',
    'cart.payment.subtitle': 'Veilig afrekenen aan tafel',
    'cart.checkout_failed': 'Betaling mislukt',
    'cart.processing_payment': 'Betaling wordt verwerkt...',
    'cart.connecting_gateway': 'Verbinden met beveiligde gateway...',
    'cart.payments_unavailable': 'Geen betaalmethode beschikbaar',
    'cart.payments_unavailable_desc': 'Deze locatie heeft geen online betaalmethode geactiveerd.',
    'cart.pay_online': 'Online betalen',
    'cart.credit_debit_card': 'Betaalpas of creditcard',
    'cart.or': 'Of',
    'cart.pay_courier_cash': 'Contant betalen bij bezorger',
    'cart.pay_counter_cash': 'Contant betalen aan de kassa',
    'cart.secure_payment': 'Veilige betaling',
    'cart.courier_payment': 'Betalen bij bezorger',
    'cart.counter_payment': 'Betalen aan kassa',
    'cart.secure_payment_desc': 'Beveiligde kaartbetaling is ingeschakeld.',
    'cart.staff_payment_desc': 'Betaling wordt door het personeel afgehandeld.',
    'cart.received.title': 'Bestelling ontvangen!',
    'cart.received.dinein': 'Uw gerechten worden spoedig aan uw tafel geserveerd.',
    'cart.received.pickup': 'We hebben uw bestelling naar de keuken gestuurd.',
    'cart.received.close': 'Venster sluiten',
    'cart.delivery_confirmed': 'Bezorging bevestigd voor',
    'cart.your_zone': 'uw zone',
    'cart.loyalty_earned': 'U heeft verdiend:',
    'cart.loyalty_points': 'Loyaliteitspunten!',
    'cart.how_experience': 'Hoe was uw bestelervaring?',
    'cart.improve_placeholder': 'Wat kunnen we verbeteren?',
    'cart.send_feedback': 'Stuur privé feedback',
    'cart.google_review': 'Schrijf een Google-review',
    'cart.thank_feedback': 'Bedankt voor uw feedback!',
    'cart.items_subtotal': 'Artikelen',
    'cart.delivery_fee': 'Bezorging',
    'cart.delivery': 'Bezorging',
    'cart.no_zone': 'Buiten bezorgzone',
    'cart.free': 'Gratis',
    'cart.packaging': 'Verpakking',
    'cart.total_summary': 'Totaalbedrag',
    'tracker.order_received': 'Bestelling ontvangen',
    'tracker.confirming_delivery': 'Het restaurant bevestigt uw bezorging',
    'tracker.waiting_kitchen': 'Wachten op bevestiging van keuken',
    'tracker.chef_cooking': 'De chef is aan het koken',
    'tracker.delivery_preparing': 'Uw bezorging wordt klaargemaakt',
    'tracker.food_preparing': 'Uw eten wordt bereid',
    'tracker.out_for_delivery': 'Onderweg voor bezorging!',
    'tracker.ready_pickup': 'Klaar om af te halen!',
    'tracker.courier_has_order': 'De bezorger heeft uw bestelling meegenomen',
    'tracker.come_to_counter': 'Kom alstublieft naar de balie',
    'tracker.delivered': 'Bezorgd!',
    'tracker.enjoy_meal': 'Eet smakelijk!',
    'tracker.delivery_complete': 'Uw bezorging is voltooid',
    'tracker.order_completed': 'Bestelling succesvol afgerond',
    'tracker.updating_status': 'Status bijwerken...',
    'tracker.order_again': 'Opnieuw bestellen',
    'reservation.title': 'Tafel reserveren',
    'reservation.subtitle': 'Geniet van een onvergetelijke ervaring',
    'reservation.name': 'Volledige naam',
    'reservation.name_placeholder': 'Uw naam',
    'reservation.phone': 'Telefoonnummer',
    'reservation.email': 'E-mailadres',
    'reservation.guests': 'Aantal personen',
    'reservation.date': 'Datum',
    'reservation.time': 'Tijd',
    'reservation.notes': 'Speciale verzoeken',
    'reservation.notes_placeholder': 'Allergieën, verjaardag, tafelvoorkeur...',
    'reservation.failed': 'Reservering mislukt',
    'reservation.error_generic': 'Kon reservering niet verzenden. Probeer opnieuw.',
    'reservation.submitting': 'Tafel reserveren...',
    'reservation.confirm': 'Reservering bevestigen',
    'reservation.requested': 'Reserveringsaanvraag verstuurd!',
    'reservation.requested_desc': 'We beoordelen uw aanvraag en sturen spoedig een bevestiging per e-mail.',
    'reservation.done': 'Klaar',
    'loyalty.title': 'Loyaliteitsprogramma',
    'loyalty.subtitle': 'Spaar punten bij elke bestelling',
    'loyalty.welcome_back': 'Welkom terug!',
    'loyalty.welcome_desc': 'Log in om punten te sparen bij elk bezoek.',
    'loyalty.view': 'Bekijken',
    'loyalty.enter_email': 'Vul uw e-mailadres in',
    'loyalty.sending_code': 'Code versturen...',
    'loyalty.send_code': 'Verificatiecode verzenden',
    'loyalty.enter_code_title': 'Verificatiecode invoeren',
    'loyalty.code_placeholder': '6-cijferige code',
    'loyalty.verifying': 'Verifiëren...',
    'loyalty.verify_signin': 'Verifiëren en inloggen',
    'loyalty.back_email': 'Terug naar e-mail',
    'loyalty.available_points': 'Beschikbare punten',
    'loyalty.saved_to': 'Opgeslagen voor',
    'loyalty.available_rewards': 'Beschikbare beloningen',
    'loyalty.redeem': 'Inwisselen',
    'loyalty.locked': 'Vergrendeld',
    'loyalty.no_rewards': 'Momenteel geen beloningen beschikbaar.',
    'loyalty.sign_out': 'Uitloggen',
    'loyalty.switch_account': 'Ander account',
    'footer.live_encryption': 'Live versleuteling actief',
    'footer.powered_by': 'Mogelijk gemaakt door',
    'footer.all_rights': 'Alle rechten voorbehouden.',
    'menu.open_now': 'Nu open',
    'menu.closed': 'Gesloten',
    'menu.delivery_only': 'Alleen bezorging',
    'menu.book_table': 'Tafel reserveren',
    'menu.rewards': 'Beloningen',
    'menu.table': 'Tafel',
    'menu.experience_culinary': 'Ervaar culinaire uitmuntendheid',
    'menu.search_placeholder': 'Zoek gerechten...',
    'menu.dietary_filters': 'Dieetfilters',
    'menu.exclude_allergens': 'Allergenen uitsluiten',
    'menu.no_allergen': 'Geen {allergen}',
    'dietary.vegetarian': 'Vegetarisch',
    'dietary.vegan': 'Veganistisch',
    'dietary.spicy': 'Pittig',
    'dietary.gluten-free': 'Glutenvrij',
    'dietary.nut-free': 'Notenvrij',
    'dietary.dairy-free': 'Zuivelvrij',
    'dietary.healthy': 'Gezond',
    'dietary.halal': 'Halal',
    'dietary.organic': 'Biologisch',
    'dietary.keto': 'Keto',
    'allergen.nuts': 'Noten',
    'allergen.dairy': 'Zuivel',
    'allergen.gluten': 'Gluten',
    'allergen.shellfish': 'Schaaldieren',
    'allergen.soy': 'Soja',
    'allergen.eggs': 'Eieren',
    'allergen.fish': 'Vis',
    'menu.chefs_recommendations': 'Aanbevelingen van de chef',
    'menu.chefs': 'Van de chef',
    'menu.recommendations': 'Aanbevelingen',
    'menu.items': "Menu-items",
    'menu.count': 'Items',
    'menu.section_closed': 'is gesloten',
    'menu.dine_in_closed_delivery_available': 'Dine-in is gesloten. Bezorging is beschikbaar voor deze sectie.',
    'menu.section_closed_notice': 'Deze sectie is momenteel gesloten.',
    'menu.available': 'BESCHIKBAAR',
    'menu.sold_out': 'UITVERKOCHT',
    'menu.mins': 'MIN',
    'menu.running_low': 'Bijna op',
    'menu.adjusted': '(AANGEPAST)',
    'menu.add_to_cart': 'In winkelwagen',
    'menu.visual_composition': 'Visuele samenstelling',
    'menu.small_details': 'Details',
    'menu.guest_sentiment': 'Gastbeoordelingen',
    'menu.how_was_experience': 'Hoe was uw ervaring?',
    'menu.thank_feedback': 'Bedankt voor uw feedback!',
    'menu.based_on_reviews': 'Gebaseerd op {count} beoordelingen',
    'menu.water': 'Water',
    'menu.bill': 'Rekening',
    'menu.cart': 'Winkelwagen',
    'menu.checkout': 'Afrekenen',
    'menu.requested': '{item} aangevraagd',
  },
  tr: {
    'menu.no_items': 'Öğe bulunamadı',
    'menu.try_adjusting': 'Aramanızı veya filtrelerinizi değiştirmeyi deneyin',
    'menu.all': 'Tümü',
    'menu.departments': 'Menü Departmanları',
    'menu.learn_more': 'Daha Fazla Bilgi',
    'cart.title': 'Siparişiniz',
    'cart.details': 'Teslimat Detayları',
    'cart.confirmed': 'Onaylandı!',
    'cart.empty': 'Sepetiniz boş',
    'cart.continue': 'Detaylara Geç',
    'cart.back': 'Geri',
    'cart.proceed_payment': 'Ödemeye İlerle',
    'cart.qty': 'Adet',
    'cart.notes_placeholder': 'Özelleştir: soğansız, sos ayrı, alerji notu…',
    'cart.upsell.title': 'Mükemmel Tamamlayıcılar',
    'cart.upsell.add': 'Ekle',
    'cart.perfect_match': 'Mükemmel Uyum',
    'cart.additional': 'Ek Fiyat',
    'cart.no_thanks': 'Hayır, teşekkürler',
    'cart.add_both': 'İkisini de Ekle',
    'cart.limited_time_offer': 'Bu masa oturumu için sınırlı süreli teklif',
    'cart.modes.dine_in': 'Masada',
    'cart.modes.pickup': 'Gel-Al',
    'cart.modes.delivery': 'Paket Servis',
    'cart.modes_unavailable': 'Sipariş modları şu anda kapalıdır.',
    'cart.form.name': 'Ad Soyad',
    'cart.form.phone': 'Telefon Numarası',
    'cart.form.table': 'Masa Numarası',
    'cart.form.address': 'Teslimat Adresi',
    'cart.form.table_placeholder': 'örn. 12',
    'cart.delivery_estimate': 'Tahmini Teslimat Süresi',
    'cart.busy_estimate': 'yoğun saat tahmini',
    'cart.asap': 'En Kısa Sürede',
    'cart.schedule': 'İleri Zamanlı',
    'cart.address_placeholder': 'Sokak, Mahalle ve Bina No',
    'cart.apartment': 'Daire',
    'cart.floor': 'Kat',
    'cart.door_code': 'Kapı Kodu',
    'cart.map_pin': 'Konum Bağlantısı / Harita Pini',
    'cart.delivery_instructions': 'Teslimat notu, alerjiler veya yön tarifleri',
    'cart.contactless': 'Kapıya bırak (temassız teslimat)',
    'cart.payment.title': 'Sipariş Ver ve Öde',
    'cart.payment.subtitle': 'Masada güvenli ödeme',
    'cart.checkout_failed': 'Ödeme Başarısız Oldu',
    'cart.processing_payment': 'Ödeme işleniyor...',
    'cart.connecting_gateway': 'Güvenli ödeme altyapısına bağlanılıyor...',
    'cart.payments_unavailable': 'Ödeme Yöntemi Yok',
    'cart.payments_unavailable_desc': 'Bu işletme misafir ödemesini henüz etkinleştirmedi.',
    'cart.pay_online': 'Online Öde',
    'cart.credit_debit_card': 'Kredi veya Banka Kartı',
    'cart.or': 'Veya',
    'cart.pay_courier_cash': 'Kuryeye Nakit Ödeme',
    'cart.pay_counter_cash': 'Kasada Nakit Ödeme',
    'cart.secure_payment': 'Güvenli Ödeme',
    'cart.courier_payment': 'Kuryede Ödeme',
    'cart.counter_payment': 'Kasada Ödeme',
    'cart.secure_payment_desc': 'Şifrelenmiş kartlı ödeme aktiftir.',
    'cart.staff_payment_desc': 'Ödeme işletme personeli tarafından tahsil edilecektir.',
    'cart.received.title': 'Siparişiniz Alındı!',
    'cart.received.dinein': 'Siparişiniz kısa süre içinde masanıza servis edilecektir.',
    'cart.received.pickup': 'Siparişinizi mutfağa ilettik. Şimdiden afiyet olsun!',
    'cart.received.close': 'Pencereyi Kapat',
    'cart.delivery_confirmed': 'Teslimat onaylanan bölge:',
    'cart.your_zone': 'bölgeniz',
    'cart.loyalty_earned': 'Kazandığınız:',
    'cart.loyalty_points': 'Sadakat Puanı!',
    'cart.how_experience': 'Sipariş deneyiminiz nasıldı?',
    'cart.improve_placeholder': 'Neyi daha iyi yapabiliriz?',
    'cart.send_feedback': 'Gizli Geri Bildirim Gönder',
    'cart.google_review': 'Google\'da Değerlendir',
    'cart.thank_feedback': 'Geri bildiriminiz için teşekkür ederiz!',
    'cart.items_subtotal': 'Ürünler',
    'cart.delivery_fee': 'Teslimat',
    'cart.delivery': 'Teslimat',
    'cart.no_zone': 'Bölge Dışı',
    'cart.free': 'Ücretsiz',
    'cart.packaging': 'Paketleme',
    'cart.total_summary': 'Genel Toplam',
    'tracker.order_received': 'Sipariş Alındı',
    'tracker.confirming_delivery': 'İşletme teslimatınızı onaylıyor',
    'tracker.waiting_kitchen': 'Mutfak onayı bekleniyor',
    'tracker.chef_cooking': 'Şef hazırlıyor',
    'tracker.delivery_preparing': 'Teslimat paketiniz hazırlanıyor',
    'tracker.food_preparing': 'Yemeğiniz hazırlanıyor',
    'tracker.out_for_delivery': 'Kurye yola çıktı!',
    'tracker.ready_pickup': 'Teslim almaya hazır!',
    'tracker.courier_has_order': 'Kurye siparişinizi teslim aldı',
    'tracker.come_to_counter': 'Lütfen teslimat bankosuna geliniz',
    'tracker.delivered': 'Teslim Edildi!',
    'tracker.enjoy_meal': 'Afiyet olsun!',
    'tracker.delivery_complete': 'Teslimatınız tamamlandı',
    'tracker.order_completed': 'Sipariş başarıyla tamamlandı',
    'tracker.updating_status': 'Durum güncelleniyor...',
    'tracker.order_again': 'Tekrar Sipariş Ver',
    'reservation.title': 'Masa Rezervasyonu',
    'reservation.subtitle': 'Unutulmaz bir deneyim için bize katılın',
    'reservation.name': 'Ad Soyad',
    'reservation.name_placeholder': 'Adınız',
    'reservation.phone': 'Telefon Numarası',
    'reservation.email': 'E-posta Adresi',
    'reservation.guests': 'Kişi Sayısı',
    'reservation.date': 'Tarih',
    'reservation.time': 'Saat',
    'reservation.notes': 'Özel İstekler',
    'reservation.notes_placeholder': 'Alerjiler, doğum günü, masa tercihi...',
    'reservation.failed': 'Rezervasyon Başarısız',
    'reservation.error_generic': 'Rezervasyon talebiniz iletilemedi. Lütfen tekrar deneyin.',
    'reservation.submitting': 'Masanız ayrılıyor...',
    'reservation.confirm': 'Rezervasyonu Onayla',
    'reservation.requested': 'Rezervasyon Talebi Gönderildi!',
    'reservation.requested_desc': 'Talebinizi inceleyip kısa süre içinde e-posta ile onay göndereceğiz.',
    'reservation.done': 'Tamam',
    'loyalty.title': 'Sadakat Programı',
    'loyalty.subtitle': 'Her siparişte puan kazanın',
    'loyalty.welcome_back': 'Tekrar Hoş Geldiniz!',
    'loyalty.welcome_desc': 'Her ziyarette puan kazanmak için giriş yapın.',
    'loyalty.view': 'Görüntüle',
    'loyalty.enter_email': 'E-posta adresinizi girin',
    'loyalty.sending_code': 'Kod gönderiliyor...',
    'loyalty.send_code': 'Doğrulama Kodu Gönder',
    'loyalty.enter_code_title': 'Doğrulama Kodunu Girin',
    'loyalty.code_placeholder': '6 haneli kod',
    'loyalty.verifying': 'Doğrulanıyor...',
    'loyalty.verify_signin': 'Doğrula ve Giriş Yap',
    'loyalty.back_email': 'E-postaya Dön',
    'loyalty.available_points': 'Kullanılabilir Puanlar',
    'loyalty.saved_to': 'Kayıtlı hesap:',
    'loyalty.available_rewards': 'Mevcut Ödüller',
    'loyalty.redeem': 'Kullan',
    'loyalty.locked': 'Kilitli',
    'loyalty.no_rewards': 'Şu anda kullanılabilir ödül bulunmuyor.',
    'loyalty.sign_out': 'Çıkış Yap',
    'loyalty.switch_account': 'Hesap Değiştir',
    'footer.live_encryption': 'Canlı Uçtan Uca Şifreleme Aktif',
    'footer.powered_by': 'Altyapı:',
    'footer.all_rights': 'Tüm hakları saklıdır.',
    'menu.open_now': 'Şimdi Açık',
    'menu.closed': 'Kapalı',
    'menu.delivery_only': 'Sadece Teslimat',
    'menu.book_table': 'Masa Ayırt',
    'menu.rewards': 'Ödüller',
    'menu.table': 'Masa',
    'menu.experience_culinary': 'Mükemmel lezzetleri deneyimleyin',
    'menu.search_placeholder': 'Ürün ara...',
    'menu.dietary_filters': 'Diyet filtreleri',
    'menu.exclude_allergens': 'Alerjenleri hariç tut',
    'menu.no_allergen': '{allergen} içermez',
    'dietary.vegetarian': 'Vejetaryen',
    'dietary.vegan': 'Vegan',
    'dietary.spicy': 'Baharatlı',
    'dietary.gluten-free': 'Glütensiz',
    'dietary.nut-free': 'Fındıksız',
    'dietary.dairy-free': 'Sütsüz',
    'dietary.healthy': 'Sağlıklı',
    'dietary.halal': 'Helal',
    'dietary.organic': 'Organik',
    'dietary.keto': 'Keto',
    'allergen.nuts': 'Fındık',
    'allergen.dairy': 'Süt ürünleri',
    'allergen.gluten': 'Glüten',
    'allergen.shellfish': 'Kabuklu deniz ürünleri',
    'allergen.soy': 'Soya',
    'allergen.eggs': 'Yumurta',
    'allergen.fish': 'Balık',
    'menu.chefs_recommendations': 'Şefin Önerileri',
    'menu.chefs': 'Şefin',
    'menu.recommendations': 'Öneriler',
    'menu.items': 'Menü Öğeleri',
    'menu.count': 'Öğe',
    'menu.section_closed': 'Kapalıdır',
    'menu.dine_in_closed_delivery_available': 'Masa servisi kapalıdır. Bu bölüm için teslimat mevcuttur.',
    'menu.section_closed_notice': 'Bu bölüm şu anda kapalıdır.',
    'menu.available': 'MEVCUT',
    'menu.sold_out': 'TÜKENDİ',
    'menu.mins': 'DK',
    'menu.running_low': 'Az Kaldı',
    'menu.adjusted': '(GÜNCELLENDİ)',
    'menu.add_to_cart': 'Sepete Ekle',
    'menu.add_to_order': 'Siparişe Ekle',
    'menu.visual_composition': 'İçerik Bileşenleri',
    'menu.small_details': 'Detaylar',
    'menu.made_with': 'İçindekiler',
    'menu.guest_sentiment': 'Misafir Değerlendirmeleri',
    'menu.guest_rating': 'Misafir Puanı',
    'menu.how_was_experience': 'Deneyiminiz nasıldı?',
    'menu.thank_feedback': 'Geri bildiriminiz için teşekkürler!',
    'menu.rate_item': 'Bu ürünü değerlendirin',
    'menu.based_on_reviews': '{count} misafir yorumuna göre',
    'menu.based_reviews': '{count} misafir yorumuna göre',
    'menu.water': 'Su',
    'menu.bill': 'Hesap',
    'menu.cart': 'Sepet',
    'menu.checkout': 'Ödeme',
    'menu.requested': '{item} İstendi',
    'menu.section': 'Bölüm',
    'menu.is_closed': 'Kapalıdır',
    'menu.section_closed_check_back': 'Bu bölüm şu anda kapalıdır. Lütfen çalışma saatlerinde tekrar kontrol edin.',
  },
  ko: {
    'menu.no_items': '메뉴를 찾을 수 없습니다',
    'menu.try_adjusting': '검색어나 필터 조건을 변경해 보세요',
    'menu.all': '전체',
    'menu.departments': '메뉴 카테고리',
    'menu.learn_more': '자세히 보기',
    'cart.title': '주문 내역',
    'cart.details': '배달 상세정보',
    'cart.confirmed': '주문 확정!',
    'cart.empty': '장바구니가 비어 있습니다',
    'cart.continue': '상세정보 입력하기',
    'cart.back': '뒤로가기',
    'cart.proceed_payment': '결제하기',
    'cart.qty': '수량',
    'cart.notes_placeholder': '요청사항: 양파 제외, 소스 따로, 알레르기 등…',
    'cart.upsell.title': '추천 꿀조합 메뉴',
    'cart.upsell.add': '추가',
    'cart.perfect_match': '최고의 조합',
    'cart.additional': '추가 금액',
    'cart.no_thanks': '괜찮습니다',
    'cart.add_both': '둘 다 추가',
    'cart.limited_time_offer': '이 테이블 전용 특별 혜택',
    'cart.modes.dine_in': '매장 식사',
    'cart.modes.pickup': '포장',
    'cart.modes.delivery': '배달',
    'cart.modes_unavailable': '현재 주문이 불가능합니다.',
    'cart.form.name': '성함',
    'cart.form.phone': '연락처',
    'cart.form.table': '테이블 번호',
    'cart.form.address': '배달 주소',
    'cart.form.table_placeholder': '예: 12',
    'cart.delivery_estimate': '예상 배달 소요시간',
    'cart.busy_estimate': '혼잡 시간대 예상',
    'cart.asap': '최대한 빨리',
    'cart.schedule': '예약 주문',
    'cart.address_placeholder': '도로명 주소 및 건물 번호',
    'cart.apartment': '동/호수',
    'cart.floor': '층수',
    'cart.door_code': '공동현관 비밀번호',
    'cart.map_pin': '지도 위치 링크',
    'cart.delivery_instructions': '배달 기사님께 전할 말씀 또는 알레르기 안내',
    'cart.contactless': '문 앞에 두고 가주세요 (비대면)',
    'cart.payment.title': '주문 및 결제',
    'cart.payment.subtitle': '테이블 안심 결제',
    'cart.checkout_failed': '결제 실패',
    'cart.processing_payment': '결제 진행 중...',
    'cart.connecting_gateway': '보안 결제 시스템 연결 중...',
    'cart.payments_unavailable': '사용 가능한 결제 수단이 없습니다',
    'cart.payments_unavailable_desc': '현재 매장에서 온라인 결제가 지원되지 않습니다.',
    'cart.pay_online': '온라인 결제',
    'cart.credit_debit_card': '신용/체크카드',
    'cart.or': '또는',
    'cart.pay_courier_cash': '배달 기사님께 직접 현금 결제',
    'cart.pay_counter_cash': '카운터에서 현금 결제',
    'cart.secure_payment': '보안 결제',
    'cart.courier_payment': '기사님 결제',
    'cart.counter_payment': '카운터 결제',
    'cart.secure_payment_desc': '안전한 암호화 카드 결제가 적용되어 있습니다.',
    'cart.staff_payment_desc': '매장 직원이 직접 결제를 진행해 드립니다.',
    'cart.received.title': '주문이 접수되었습니다!',
    'cart.received.dinein': '음식이 준비되는 대로 테이블로 가져다 드리겠습니다.',
    'cart.received.pickup': '주방에 주문이 전달되었습니다. 잠시만 기다려 주세요!',
    'cart.received.close': '창 닫기',
    'cart.delivery_confirmed': '배달 확정 지역:',
    'cart.your_zone': '배달 지역',
    'cart.loyalty_earned': '적립 완료:',
    'cart.loyalty_points': '포인트 적립!',
    'cart.how_experience': '주문 경험은 만족스러우셨나요?',
    'cart.improve_placeholder': '개선할 점이 있다면 편하게 남겨주세요',
    'cart.send_feedback': '소중한 의견 보내기',
    'cart.google_review': 'Google 리뷰 작성하기',
    'cart.thank_feedback': '의견을 남겨주셔서 감사합니다!',
    'cart.items_subtotal': '주문 금액',
    'cart.delivery_fee': '배달비',
    'cart.delivery': '배달비',
    'cart.no_zone': '배달 불가 지역',
    'cart.free': '무료',
    'cart.packaging': '포장비',
    'cart.total_summary': '최종 결제 금액',
    'tracker.order_received': '주문 접수',
    'tracker.confirming_delivery': '매장에서 주문을 확인하고 있습니다',
    'tracker.waiting_kitchen': '주방 승인 대기 중',
    'tracker.chef_cooking': '조리 중',
    'tracker.delivery_preparing': '배달 포장을 준비하고 있습니다',
    'tracker.food_preparing': '정성껏 음식을 조리하고 있습니다',
    'tracker.out_for_delivery': '배달 출발!',
    'tracker.ready_pickup': '수령 준비 완료!',
    'tracker.courier_has_order': '기사님이 픽업하여 배달 중입니다',
    'tracker.come_to_counter': '픽업대에서 음식을 수령해 주세요',
    'tracker.delivered': '배달 완료!',
    'tracker.enjoy_meal': '맛있게 드세요!',
    'tracker.delivery_complete': '배달이 완료되었습니다',
    'tracker.order_completed': '주문이 완료되었습니다',
    'tracker.updating_status': '상태 업데이트 중...',
    'tracker.order_again': '다시 주문하기',
    'reservation.title': '테이블 예약',
    'reservation.subtitle': '특별한 미식의 경험을 함께하세요',
    'reservation.name': '성함',
    'reservation.name_placeholder': '성함을 입력하세요',
    'reservation.phone': '연락처',
    'reservation.email': '이메일 주소',
    'reservation.guests': '방문 인원',
    'reservation.date': '예약 날짜',
    'reservation.time': '예약 시간',
    'reservation.notes': '요청사항',
    'reservation.notes_placeholder': '알레르기, 기념일, 좌석 선호도 등...',
    'reservation.failed': '예약 접수 실패',
    'reservation.error_generic': '예약을 접수하지 못했습니다. 다시 시도해 주세요.',
    'reservation.submitting': '예약 처리 중...',
    'reservation.confirm': '예약 확정하기',
    'reservation.requested': '예약 요청 완료!',
    'reservation.requested_desc': '내용 확인 후 등록하신 이메일로 안내해 드리겠습니다.',
    'reservation.done': '확인',
    'loyalty.title': '멤버십 포인트',
    'loyalty.subtitle': '주문할 때마다 포인트가 쌓입니다',
    'loyalty.welcome_back': '다시 오신 것을 환영합니다!',
    'loyalty.welcome_desc': '로그인하고 포인트 적립 및 리워드 혜택을 받아보세요.',
    'loyalty.view': '확인',
    'loyalty.enter_email': '이메일을 입력하세요',
    'loyalty.sending_code': '인증 코드 발송 중...',
    'loyalty.send_code': '인증 코드 발송',
    'loyalty.enter_code_title': '인증 코드 입력',
    'loyalty.code_placeholder': '6자리 인증 코드',
    'loyalty.verifying': '인증 진행 중...',
    'loyalty.verify_signin': '인증 및 로그인',
    'loyalty.back_email': '이메일 다시 입력',
    'loyalty.available_points': '보유 포인트',
    'loyalty.saved_to': '연동 계정:',
    'loyalty.available_rewards': '사용 가능한 혜택',
    'loyalty.redeem': '교환하기',
    'loyalty.locked': '포인트 부족',
    'loyalty.no_rewards': '현재 사용 가능한 리워드가 없습니다.',
    'loyalty.sign_out': '로그아웃',
    'loyalty.switch_account': '다른 계정으로 로그인',
    'footer.live_encryption': '실시간 데이터 암호화 적용 중',
    'footer.powered_by': '제공:',
    'footer.all_rights': 'All Rights Reserved.',
    'menu.open_now': '영업 중',
    'menu.closed': '영업 종료',
    'menu.delivery_only': '배달 전용',
    'menu.book_table': '테이블 예약',
    'menu.rewards': '리워드',
    'menu.table': '테이블',
    'menu.experience_culinary': '최고의 미식을 경험해보세요',
    'menu.search_placeholder': '메뉴 검색...',
    'menu.dietary_filters': '식이 요법 필터',
    'menu.exclude_allergens': '알레르기 유발 성분 제외',
    'menu.no_allergen': '{allergen} 없음',
    'dietary.vegetarian': '채식',
    'dietary.vegan': '비건',
    'dietary.spicy': '매움',
    'dietary.gluten-free': '글루텐 프리',
    'dietary.nut-free': '견과류 프리',
    'dietary.dairy-free': '유제품 프리',
    'dietary.healthy': '건강식',
    'dietary.halal': '할랄',
    'dietary.organic': '유기농',
    'dietary.keto': '키토',
    'allergen.nuts': '견과류',
    'allergen.dairy': '유제품',
    'allergen.gluten': '글루텐',
    'allergen.shellfish': '갑각류',
    'allergen.soy': '대두',
    'allergen.eggs': '계란',
    'allergen.fish': '생선',
    'menu.chefs_recommendations': '셰프 추천 메뉴',
    'menu.chefs': '셰프의',
    'menu.recommendations': '추천 메뉴',
    'menu.items': '메뉴 항목',
    'menu.count': '개',
    'menu.section_closed': '영업 종료',
    'menu.dine_in_closed_delivery_available': '매장 식사는 마감되었습니다. 배달 주문이 가능합니다.',
    'menu.section_closed_notice': '이 구역은 현재 영업이 종료되었습니다.',
    'menu.available': '주문 가능',
    'menu.sold_out': '품절',
    'menu.mins': '분',
    'menu.running_low': '마감 임박',
    'menu.adjusted': '(조정됨)',
    'menu.add_to_cart': '장바구니 담기',
    'menu.add_to_order': '주문에 추가',
    'menu.visual_composition': '재료 구성',
    'menu.small_details': '상세 정보',
    'menu.made_with': '포함 재료',
    'menu.guest_sentiment': '방문자 평가',
    'menu.guest_rating': '고객 평점',
    'menu.how_was_experience': '경험은 어떠셨나요?',
    'menu.thank_feedback': '소중한 의견 감사합니다!',
    'menu.rate_item': '이 메뉴를 평가해주세요',
    'menu.based_on_reviews': '방문자 리뷰 {count}개 기준',
    'menu.based_reviews': '방문자 리뷰 {count}개 기준',
    'menu.water': '물',
    'menu.bill': '계산서',
    'menu.cart': '장바구니',
    'menu.checkout': '주문하기',
    'menu.requested': '{item} 요청됨',
    'menu.section': '구역',
    'menu.is_closed': '영업 종료됨',
    'menu.section_closed_check_back': '이 구역은 현재 닫혀 있습니다. 영업 시간에 다시 확인해주세요.',
  },
  el: {
    'menu.no_items': 'Δεν βρέθηκαν πιάτα',
    'menu.try_adjusting': 'Δοκιμάστε να αλλάξετε την αναζήτηση ή τα φίλτρα',
    'menu.all': 'Όλα',
    'menu.departments': 'Τμήματα Μενού',
    'menu.learn_more': 'Μάθετε περισσότερα',
    'cart.title': 'Η Παραγγελία σας',
    'cart.details': 'Στοιχεία Παράδοσης',
    'cart.confirmed': 'Επιβεβαιώθηκε!',
    'cart.empty': 'Το καλάθι σας είναι άδειο',
    'cart.continue': 'Συνέχεια στα στοιχεία',
    'cart.back': 'Πίσω',
    'cart.proceed_payment': 'Συνέχεια στην πληρωμή',
    'cart.qty': 'Ποσ.',
    'cart.notes_placeholder': 'Προσαρμογή: χωρίς κρεμμύδι, σάλτσα χωριστά, αλλεργίες…',
    'cart.upsell.title': 'Τέλειοι συνδυασμοί',
    'cart.upsell.add': 'Προσθήκη',
    'cart.perfect_match': 'Ιδανικό Ταίριασμα',
    'cart.additional': 'Επιπλέον',
    'cart.no_thanks': 'Όχι, ευχαριστώ',
    'cart.add_both': 'Προσθήκη και των δύο',
    'cart.limited_time_offer': 'Προσφορά περιορισμένου χρόνου για αυτό το τραπέζι',
    'cart.modes.dine_in': 'Στο κατάστημα',
    'cart.modes.pickup': 'Παραλαβή (Takeaway)',
    'cart.modes.delivery': 'Διανομή (Delivery)',
    'cart.modes_unavailable': 'Οι τρόποι παραγγελίας δεν είναι διαθέσιμοι προς το παρόν.',
    'cart.form.name': 'Ονοματεπώνυμο',
    'cart.form.phone': 'Τηλέφωνο επικοινωνίας',
    'cart.form.table': 'Αριθμός τραπεζιού',
    'cart.form.address': 'Διεύθυνση παράδοσης',
    'cart.form.table_placeholder': 'π.χ. 12',
    'cart.delivery_estimate': 'Εκτιμώμενος χρόνος παράδοσης',
    'cart.busy_estimate': 'εκτίμηση σε ώρα αιχμής',
    'cart.asap': 'Το συντομότερο δυνατό',
    'cart.schedule': 'Προγραμματισμός',
    'cart.address_placeholder': 'Οδός και αριθμός',
    'cart.apartment': 'Διαμέρισμα / Όροφος',
    'cart.floor': 'Όροφος',
    'cart.door_code': 'Κωδικός εισόδου',
    'cart.map_pin': 'Σύνδεσμος τοποθεσίας στο χάρτη',
    'cart.delivery_instructions': 'Οδηγίες παράδοσης ή σημειώσεις αλλεργιών',
    'cart.contactless': 'Παράδοση στην πόρτα (ανέπαφη)',
    'cart.payment.title': 'Παραγγελία & Πληρωμή',
    'cart.payment.subtitle': 'Ασφαλής πληρωμή στο τραπέζι',
    'cart.checkout_failed': 'Η πληρωμή απέτυχε',
    'cart.processing_payment': 'Επεξεργασία πληρωμής...',
    'cart.connecting_gateway': 'Σύνδεση με ασφαλή πύλη πληρωμών...',
    'cart.payments_unavailable': 'Μη διαθέσιμες μέθοδοι πληρωμής',
    'cart.payments_unavailable_desc': 'Το κατάστημα δεν έχει ενεργοποιήσει ηλεκτρονικές πληρωμές.',
    'cart.pay_online': 'Ηλεκτρονική πληρωμή',
    'cart.credit_debit_card': 'Πιστωτική ή Χρεωστική Κάρτα',
    'cart.or': 'Ή',
    'cart.pay_courier_cash': 'Πληρωμή με μετρητά στον διανομέα',
    'cart.pay_counter_cash': 'Πληρωμή με μετρητά στο ταμείο',
    'cart.secure_payment': 'Ασφαλής πληρωμή',
    'cart.courier_payment': 'Πληρωμή στον διανομέα',
    'cart.counter_payment': 'Πληρωμή στο ταμείο',
    'cart.secure_payment_desc': 'Ενεργή κρυπτογραφημένη πληρωμή με κάρτα.',
    'cart.staff_payment_desc': 'Η πληρωμή θα εισπραχθεί από το προσωπικό του καταστήματος.',
    'cart.received.title': 'Η παραγγελία ελήφθη!',
    'cart.received.dinein': 'Τα πιάτα σας θα σερβιριστούν στο τραπέζι σας σε λίγο.',
    'cart.received.pickup': 'Στείλαμε την παραγγελία στην κουζίνα. Καλή σας όρεξη!',
    'cart.received.close': 'Κλείσιμο παραθύρου',
    'cart.delivery_confirmed': 'Επιβεβαιωμένη παράδοση για',
    'cart.your_zone': 'τη ζώνη σας',
    'cart.loyalty_earned': 'Κερδίσατε',
    'cart.loyalty_points': 'Πόντους Επιβράβευσης!',
    'cart.how_experience': 'Πώς ήταν η εμπειρία της παραγγελίας σας;',
    'cart.improve_placeholder': 'Τι θα μπορούσαμε να βελτιώσουμε;',
    'cart.send_feedback': 'Αποστολή προσωπικού σχολίου',
    'cart.google_review': 'Αξιολογήστε μας στο Google',
    'cart.thank_feedback': 'Σας ευχαριστούμε για την αξιολόγηση!',
    'cart.items_subtotal': 'Προϊόντα',
    'cart.delivery_fee': 'Διανομή',
    'cart.delivery': 'Διανομή',
    'cart.no_zone': 'Εκτός ζώνης',
    'cart.free': 'Δωρεάν',
    'cart.packaging': 'Συσκευασία',
    'cart.total_summary': 'Συνολικό Ποσό',
    'tracker.order_received': 'Η παραγγελία ελήφθη',
    'tracker.confirming_delivery': 'Το κατάστημα επιβεβαιώνει τη διανομή σας',
    'tracker.waiting_kitchen': 'Αναμονή έγκρισης από την κουζίνα',
    'tracker.chef_cooking': 'Ο σεφ μαγειρεύει',
    'tracker.delivery_preparing': 'Η παραγγελία σας ετοιμάζεται για παράδοση',
    'tracker.food_preparing': 'Το φαγητό σας προετοιμάζεται',
    'tracker.out_for_delivery': 'Σε πορεία διανομής!',
    'tracker.ready_pickup': 'Έτοιμο για παραλαβή!',
    'tracker.courier_has_order': 'Ο διανομέας παρέλαβε την παραγγελία σας',
    'tracker.come_to_counter': 'Παρακαλούμε περάστε από το ταμείο',
    'tracker.delivered': 'Παραδόθηκε!',
    'tracker.enjoy_meal': 'Καλή σας απόλαυση!',
    'tracker.delivery_complete': 'Η διανομή ολοκληρώθηκε',
    'tracker.order_completed': 'Η παραγγελία ολοκληρώθηκε με επιτυχία',
    'tracker.updating_status': 'Ενημέρωση κατάστασης...',
    'tracker.order_again': 'Παραγγείλτε ξανά',
    'reservation.title': 'Κράτηση Τραπεζιού',
    'reservation.subtitle': 'Ελάτε να ζήσετε μια αξέχαστη γαστρονομική εμπειρία',
    'reservation.name': 'Ονοματεπώνυμο',
    'reservation.name_placeholder': 'Το όνομά σας',
    'reservation.phone': 'Τηλέφωνο',
    'reservation.email': 'Email',
    'reservation.guests': 'Άτομα',
    'reservation.date': 'Ημερομηνία',
    'reservation.time': 'Ώρα',
    'reservation.notes': 'Ειδικά αιτήματα',
    'reservation.notes_placeholder': 'Αλλεργίες, γενέθλια, προτίμηση τραπεζιού...',
    'reservation.failed': 'Η κράτηση απέτυχε',
    'reservation.error_generic': 'Δεν ήταν δυνατή η υποβολή της κράτησης. Δοκιμάστε ξανά.',
    'reservation.submitting': 'Κατοχύρωση τραπεζιού...',
    'reservation.confirm': 'Επιβεβαίωση Κράτησης',
    'reservation.requested': 'Το αίτημα κράτησης εστάλη!',
    'reservation.requested_desc': 'Θα εξετάσουμε το αίτημά σας και θα σας στείλουμε επιβεβαίωση στο email σας σύντομα.',
    'reservation.done': 'Ολοκληρώθηκε',
    'loyalty.title': 'Πρόγραμμα Επιβράβευσης',
    'loyalty.subtitle': 'Κερδίστε πόντους με κάθε παραγγελία',
    'loyalty.welcome_back': 'Καλώς ήρθατε και πάλι!',
    'loyalty.welcome_desc': 'Συνδεθείτε για να συλλέγετε πόντους σε κάθε επίσκεψη.',
    'loyalty.view': 'Προβολή',
    'loyalty.enter_email': 'Εισαγάγετε το email σας',
    'loyalty.sending_code': 'Αποστολή κωδικού...',
    'loyalty.send_code': 'Αποστολή κωδικού επιβεβαίωσης',
    'loyalty.enter_code_title': 'Εισαγωγή κωδικού επιβεβαίωσης',
    'loyalty.code_placeholder': '6-ψήφιος κωδικός',
    'loyalty.verifying': 'Επαλήθευση...',
    'loyalty.verify_signin': 'Επαλήθευση & Σύνδεση',
    'loyalty.back_email': 'Επιστροφή στο email',
    'loyalty.available_points': 'Διαθέσιμοι Πόντοι',
    'loyalty.saved_to': 'Αποθηκεύτηκε για',
    'loyalty.available_rewards': 'Διαθέσιμες Επιβραβεύσεις',
    'loyalty.redeem': 'Εξαργύρωση',
    'loyalty.locked': 'Κλειδωμένο',
    'loyalty.no_rewards': 'Δεν υπάρχουν διαθέσιμες επιβραβεύσεις αυτή τη στιγμή.',
    'loyalty.sign_out': 'Αποσύνδεση',
    'loyalty.switch_account': 'Αλλαγή λογαριασμού',
    'footer.live_encryption': 'Ενεργή κρυπτογράφηση σε πραγματικό χρόνο',
    'footer.powered_by': 'Με την υποστήριξη του',
    'footer.all_rights': 'Όλα τα δικαιώματα διατηρούνται.',
    'menu.open_now': 'Ανοιχτά',
    'menu.closed': 'Κλειστά',
    'menu.delivery_only': 'Μόνο Delivery',
    'menu.book_table': 'Κράτηση Τραπεζιού',
    'menu.rewards': 'Ανταμοιβές',
    'menu.table': 'Τραπέζι',
    'menu.experience_culinary': 'Ζήστε μια γαστρονομική εμπειρία',
    'menu.search_placeholder': 'Αναζήτηση προϊόντων...',
    'menu.dietary_filters': 'Διατροφικά φίλτρα',
    'menu.exclude_allergens': 'Εξαίρεση αλλεργιογόνων',
    'menu.no_allergen': 'Χωρίς {allergen}',
    'dietary.vegetarian': 'Χορτοφαγικό',
    'dietary.vegan': 'Vegan',
    'dietary.spicy': 'Καυτερό',
    'dietary.gluten-free': 'Χωρίς Γλουτένη',
    'dietary.nut-free': 'Χωρίς Ξηρούς Καρπούς',
    'dietary.dairy-free': 'Χωρίς Γαλακτοκομικά',
    'dietary.healthy': 'Υγιεινό',
    'dietary.halal': 'Halal',
    'dietary.organic': 'Βιολογικό',
    'dietary.keto': 'Keto',
    'allergen.nuts': 'Ξηροί Καρποί',
    'allergen.dairy': 'Γαλακτοκομικά',
    'allergen.gluten': 'Γλουτένη',
    'allergen.shellfish': 'Οστρακοειδή',
    'allergen.soy': 'Σόγια',
    'allergen.eggs': 'Αυγά',
    'allergen.fish': 'Ψάρι',
    'menu.chefs_recommendations': 'Προτάσεις του Σεφ',
    'menu.chefs': 'Του Σεφ',
    'menu.recommendations': 'Προτάσεις',
    'menu.items': 'Είδη Μενού',
    'menu.count': 'Είδη',
    'menu.section_closed': 'Κλειστό',
    'menu.dine_in_closed_delivery_available': 'Το σέρβις στο κατάστημα είναι κλειστό. Διατίθεται delivery.',
    'menu.section_closed_notice': 'Αυτό το τμήμα είναι κλειστό προς το παρόν.',
    'menu.available': 'ΔΙΑΘΕΣΙΜΟ',
    'menu.sold_out': 'ΕΞΑΝΤΛΗΘΗΚΕ',
    'menu.mins': 'ΛΕΠ',
    'menu.running_low': 'Λίγα Τεμάχια',
    'menu.adjusted': '(ΠΡΟΣΑΡΜΟΣΜΕΝΟ)',
    'menu.add_to_cart': 'Προσθήκη στο Καλάθι',
    'menu.add_to_order': 'Προσθήκη στην Παραγγελία',
    'menu.visual_composition': 'Σύνθεση & Υλικά',
    'menu.small_details': 'Λεπτομέρειες',
    'menu.made_with': 'Παρασκευάζεται με',
    'menu.guest_sentiment': 'Κριτικές Επισκεπτών',
    'menu.guest_rating': 'Αξιολόγηση Επισκεπτών',
    'menu.how_was_experience': 'Πώς ήταν η εμπειρία σας;',
    'menu.thank_feedback': 'Ευχαριστούμε για την κριτική σας!',
    'menu.rate_item': 'Βαθμολογήστε αυτό το πιάτο',
    'menu.based_on_reviews': 'Βάσει {count} κριτικών',
    'menu.based_reviews': 'Βάσει {count} κριτικών',
    'menu.water': 'Νερό',
    'menu.bill': 'Λογαριασμός',
    'menu.cart': 'Καλάθι',
    'menu.checkout': 'Ολοκλήρωση',
    'menu.requested': 'Ζητήθηκε {item}',
    'menu.section': 'Τμήμα',
    'menu.is_closed': 'είναι Κλειστό',
    'menu.section_closed_check_back': 'Αυτό το τμήμα είναι προσωρινά κλειστό. Ελέγξτε ξανά κατά τις ώρες λειτουργίας.',
  },
  hi: {
    'menu.no_items': 'कोई व्यंजन नहीं मिला',
    'menu.try_adjusting': 'अपनी खोज या फ़िल्टर समायोजित करने का प्रयास करें',
    'menu.all': 'सभी',
    'menu.departments': 'मेनू श्रेणियां',
    'menu.learn_more': 'और जानें',
    'cart.title': 'आपका ऑर्डर',
    'cart.details': 'डिलीवरी विवरण',
    'cart.confirmed': 'पुष्टि हो गई!',
    'cart.empty': 'आपकी कार्ट खाली है',
    'cart.continue': 'विवरण के लिए आगे बढ़ें',
    'cart.back': 'वापस',
    'cart.proceed_payment': 'भुगतान के लिए आगे बढ़ें',
    'cart.qty': 'मात्रा',
    'cart.notes_placeholder': 'अनुकूलित करें: बिना प्याज, सॉस अलग, एलर्जी…',
    'cart.upsell.title': 'उत्कृष्ट संयोजन',
    'cart.upsell.add': 'जोड़ें',
    'cart.perfect_match': 'परफेक्ट मैच',
    'cart.additional': 'अतिरिक्त',
    'cart.no_thanks': 'जी नहीं, धन्यवाद',
    'cart.add_both': 'दोनों जोड़ें',
    'cart.limited_time_offer': 'इस टेबल सत्र के लिए विशेष सीमित ऑफर',
    'cart.modes.dine_in': 'डाइन-इन (रेस्तरां में)',
    'cart.modes.pickup': 'टेकअवे (पिकअप)',
    'cart.modes.delivery': 'डिलीवरी',
    'cart.modes_unavailable': 'ऑर्डरिंग मोड वर्तमान में उपलब्ध नहीं हैं।',
    'cart.form.name': 'पूरा नाम',
    'cart.form.phone': 'फ़ोन नंबर',
    'cart.form.table': 'टेबल नंबर',
    'cart.form.address': 'डिलीवरी का पता',
    'cart.form.table_placeholder': 'उदा. 12',
    'cart.delivery_estimate': 'अनुमानित डिलीवरी समय',
    'cart.busy_estimate': 'व्यस्त समय का अनुमान',
    'cart.asap': 'यथाशीघ्र',
    'cart.schedule': 'शेड्यूल करें',
    'cart.address_placeholder': 'सड़क और इलाका',
    'cart.apartment': 'अपार्टमेंट / फ्लैट',
    'cart.floor': 'मंजिल',
    'cart.door_code': 'डोर कोड / घंटी',
    'cart.map_pin': 'मैप लोकेशन लिंक',
    'cart.delivery_instructions': 'डिलीवरी निर्देश या एलर्जी संबंधी जानकारी',
    'cart.contactless': 'दरवाजे पर छोड़ें (कॉन्टैक्टलेस)',
    'cart.payment.title': 'ऑर्डर और भुगतान',
    'cart.payment.subtitle': 'टेबल पर सुरक्षित भुगतान',
    'cart.checkout_failed': 'भुगतान विफल रहा',
    'cart.processing_payment': 'भुगतान संसाधित हो रहा है...',
    'cart.connecting_gateway': 'सुरक्षित गेटवे से जुड़ रहा है...',
    'cart.payments_unavailable': 'भुगतान उपलब्ध नहीं है',
    'cart.payments_unavailable_desc': 'इस रेस्तरां ने ऑनलाइन भुगतान सक्षम नहीं किया है।',
    'cart.pay_online': 'ऑनलाइन भुगतान करें',
    'cart.credit_debit_card': 'क्रेडिट या डेबिट कार्ड',
    'cart.or': 'या',
    'cart.pay_courier_cash': 'डिलीवरी पार्टनर को नकद भुगतान करें',
    'cart.pay_counter_cash': 'काउंटर पर नकद भुगतान करें',
    'cart.secure_payment': 'सुरक्षित भुगतान',
    'cart.courier_payment': 'डिलीवरी पर भुगतान',
    'cart.counter_payment': 'काउंटर पर भुगतान',
    'cart.secure_payment_desc': 'एन्क्रिप्टेड कार्ड भुगतान सक्रिय है।',
    'cart.staff_payment_desc': 'भुगतान रेस्तरां स्टाफ द्वारा लिया जाएगा।',
    'cart.received.title': 'ऑर्डर प्राप्त हुआ!',
    'cart.received.dinein': 'आपके व्यंजन जल्द ही टेबल पर परोसे जाएंगे।',
    'cart.received.pickup': 'ऑर्डर रसोई में भेज दिया गया है। धन्यवाद!',
    'cart.received.close': 'विंडो बंद करें',
    'cart.delivery_confirmed': 'डिलीवरी की पुष्टि:',
    'cart.your_zone': 'आपका क्षेत्र',
    'cart.loyalty_earned': 'आपने कमाए',
    'cart.loyalty_points': 'लॉयल्टी पॉइंट्स!',
    'cart.how_experience': 'आपका ऑर्डर अनुभव कैसा रहा?',
    'cart.improve_placeholder': 'हम क्या बेहतर कर सकते हैं?',
    'cart.send_feedback': 'निजी फीडबैक भेजें',
    'cart.google_review': 'Google पर समीक्षा लिखें',
    'cart.thank_feedback': 'आपकी प्रतिक्रिया के लिए धन्यवाद!',
    'cart.items_subtotal': 'कुल व्यंजन',
    'cart.delivery_fee': 'डिलीवरी शुल्क',
    'cart.delivery': 'डिलीवरी शुल्क',
    'cart.no_zone': 'डिलीवरी क्षेत्र से बाहर',
    'cart.free': 'निःशुल्क',
    'cart.packaging': 'पैकेजिंग शुल्क',
    'cart.total_summary': 'कुल राशि',
    'tracker.order_received': 'ऑर्डर प्राप्त हुआ',
    'tracker.confirming_delivery': 'रेस्तरां आपकी डिलीवरी की पुष्टि कर रहा है',
    'tracker.waiting_kitchen': 'रसोई की स्वीकृति की प्रतीक्षा है',
    'tracker.chef_cooking': 'शेफ तैयार कर रहे हैं',
    'tracker.delivery_preparing': 'आपकी डिलीवरी तैयार की जा रही है',
    'tracker.food_preparing': 'आपका भोजन तैयार हो रहा है',
    'tracker.out_for_delivery': 'डिलीवरी के लिए निकल चुका है!',
    'tracker.ready_pickup': 'पिकअप के लिए तैयार है!',
    'tracker.courier_has_order': 'डिलीवरी पार्टनर ने ऑर्डर ले लिया है',
    'tracker.come_to_counter': 'कृपया काउंटर पर आएं',
    'tracker.delivered': 'डिलीवर हो गया!',
    'tracker.enjoy_meal': 'भोजन का आनंद लें!',
    'tracker.delivery_complete': 'डिलीवरी पूरी हो गई',
    'tracker.order_completed': 'ऑर्डर सफलतापूर्वक पूरा हुआ',
    'tracker.updating_status': 'स्थिति अपडेट हो रही है...',
    'tracker.order_again': 'फिर से ऑर्डर करें',
    'reservation.title': 'टेबल बुक करें',
    'reservation.subtitle': 'एक अविस्मरणीय अनुभव के लिए हमारे साथ जुड़ें',
    'reservation.name': 'पूरा नाम',
    'reservation.name_placeholder': 'आपका नाम',
    'reservation.phone': 'फ़ोन नंबर',
    'reservation.email': 'ईमेल पता',
    'reservation.guests': 'अतिथियों की संख्या',
    'reservation.date': 'तारीख',
    'reservation.time': 'समय',
    'reservation.notes': 'विशेष अनुरोध',
    'reservation.notes_placeholder': 'एलर्जी, जन्मदिन, टेबल प्राथमिकता...',
    'reservation.failed': 'बुकिंग विफल रही',
    'reservation.error_generic': 'आरक्षण अनुरोध सबमिट नहीं हो सका। कृपया पुनः प्रयास करें।',
    'reservation.submitting': 'टेबल बुक की जा रही है...',
    'reservation.confirm': 'बुकिंग की पुष्टि करें',
    'reservation.requested': 'आरक्षण अनुरोध भेजा गया!',
    'reservation.requested_desc': 'हम आपके अनुरोध की समीक्षा करेंगे और ईमेल पर पुष्टि भेजेंगे।',
    'reservation.done': 'संपन्न',
    'loyalty.title': 'लॉयल्टी रिवार्ड्स',
    'loyalty.subtitle': 'हर ऑर्डर पर पॉइंट्स कमाएं',
    'loyalty.welcome_back': 'पुनः स्वागत है!',
    'loyalty.welcome_desc': 'हर बार पॉइंट्स सुरक्षित करने के लिए साइन इन करें।',
    'loyalty.view': 'देखें',
    'loyalty.enter_email': 'अपना ईमेल दर्ज करें',
    'loyalty.sending_code': 'कोड भेजा जा रहा है...',
    'loyalty.send_code': 'सत्यापन कोड भेजें',
    'loyalty.enter_code_title': 'सत्यापन कोड दर्ज करें',
    'loyalty.code_placeholder': '6-अंकों का कोड',
    'loyalty.verifying': 'सत्यापित किया जा रहा है...',
    'loyalty.verify_signin': 'सत्यापित करें और साइन इन करें',
    'loyalty.back_email': 'ईमेल पर वापस जाएं',
    'loyalty.available_points': 'उपलब्ध पॉइंट्स',
    'loyalty.saved_to': 'सहेजा गया:',
    'loyalty.available_rewards': 'उपलब्ध रिवार्ड्स',
    'loyalty.redeem': 'रिडीम करें',
    'loyalty.locked': 'लॉक किया गया',
    'loyalty.no_rewards': 'वर्तमान में कोई रिवार्ड उपलब्ध नहीं है।',
    'loyalty.sign_out': 'साइन आउट',
    'loyalty.switch_account': 'खाता बदलें',
    'footer.live_encryption': 'लाइव एन्क्रिप्शन सक्रिय है',
    'footer.powered_by': 'संचालित द्वारा',
    'footer.all_rights': 'सर्वाधिकार सुरक्षित।',
    'menu.open_now': 'खुला है',
    'menu.closed': 'बंद है',
    'menu.delivery_only': 'केवल डिलीवरी',
    'menu.book_table': 'टेबल बुक करें',
    'menu.rewards': 'रिवॉर्ड्स',
    'menu.table': 'टेबल',
    'menu.experience_culinary': 'उत्कृष्ट भोजन का अनुभव करें',
    'menu.search_placeholder': 'मेनू खोजें...',
    'menu.dietary_filters': 'आहार फिल्टर',
    'menu.exclude_allergens': 'एलर्जी कारक हटाएं',
    'menu.no_allergen': 'बिना {allergen}',
    'dietary.vegetarian': 'शाकाहारी',
    'dietary.vegan': 'वीगन',
    'dietary.spicy': 'मसालेदार',
    'dietary.gluten-free': 'ग्लूटेन-मुक्त',
    'dietary.nut-free': 'नट-मुक्त',
    'dietary.dairy-free': 'डेयरी-मुक्त',
    'dietary.healthy': 'स्वस्थ',
    'dietary.halal': 'हलाल',
    'dietary.organic': 'जैविक',
    'dietary.keto': 'कीटो',
    'allergen.nuts': 'नट्स',
    'allergen.dairy': 'डेयरी',
    'allergen.gluten': 'ग्लूटेन',
    'allergen.shellfish': 'शेलफिश',
    'allergen.soy': 'सोया',
    'allergen.eggs': 'अंडे',
    'allergen.fish': 'मछली',
    'menu.chefs_recommendations': 'शेफ की सिफारिशें',
    'menu.chefs': 'शेफ की',
    'menu.recommendations': 'सिफारिशें',
    'menu.items': 'मेनू व्यंजन',
    'menu.count': 'व्यंजन',
    'menu.section_closed': 'बंद है',
    'menu.dine_in_closed_delivery_available': 'डाइन-इन सेवा बंद है। डिलीवरी उपलब्ध है।',
    'menu.section_closed_notice': 'यह अनुभाग वर्तमान में बंद है।',
    'menu.available': 'उपलब्ध',
    'menu.sold_out': 'समाप्त',
    'menu.mins': 'मिनट',
    'menu.running_low': 'कम बचा है',
    'menu.adjusted': '(समायोजित)',
    'menu.add_to_cart': 'कार्ट में जोड़ें',
    'menu.add_to_order': 'ऑर्डर में जोड़ें',
    'menu.visual_composition': 'सामग्री संरचना',
    'menu.small_details': 'विवरण',
    'menu.made_with': 'इससे बना है',
    'menu.guest_sentiment': 'अतिथि समीक्षाएं',
    'menu.guest_rating': 'अतिथि रेटिंग',
    'menu.how_was_experience': 'आपका अनुभव कैसा रहा?',
    'menu.thank_feedback': 'प्रतिक्रिया के लिए धन्यवाद!',
    'menu.rate_item': 'इस व्यंजन को रेट करें',
    'menu.based_on_reviews': '{count} अतिथि समीक्षाओं पर आधारित',
    'menu.based_reviews': '{count} अतिथि समीक्षाओं पर आधारित',
    'menu.water': 'पानी',
    'menu.bill': 'बिल',
    'menu.cart': 'कार्ट',
    'menu.checkout': 'चेकआउट',
    'menu.requested': '{item} का अनुरोध किया गया',
    'menu.section': 'अनुभाग',
    'menu.is_closed': 'बंद है',
    'menu.section_closed_check_back': 'यह अनुभाग बंद है। कृपया कार्य समय के दौरान दोबारा देखें।',
  },
  th: {
    'menu.no_items': 'ไม่พบรายการอาหาร',
    'menu.try_adjusting': 'ลองปรับเปลี่ยนคำค้นหาหรือตัวกรอง',
    'menu.all': 'ทั้งหมด',
    'menu.departments': 'หมวดหมู่อาหาร',
    'menu.learn_more': 'ดูรายละเอียด',
    'cart.title': 'รายการสั่งซื้อของคุณ',
    'cart.details': 'รายละเอียดการจัดส่ง',
    'cart.confirmed': 'ยืนยันแล้ว!',
    'cart.empty': 'ตะกร้าของคุณว่างเปล่า',
    'cart.continue': 'ดำเนินการต่อเพื่อระบุข้อมูล',
    'cart.back': 'ย้อนกลับ',
    'cart.proceed_payment': 'ดำเนินการชำระเงิน',
    'cart.qty': 'จำนวน',
    'cart.notes_placeholder': 'เพิ่มเติม: ไม่ใส่หอม, แยกน้ำจิ้ม, แจ้งแพ้อาหาร…',
    'cart.upsell.title': 'เมนูจับคู่สุดพิเศษ',
    'cart.upsell.add': 'เพิ่ม',
    'cart.perfect_match': 'จับคู่อร่อยลงตัว',
    'cart.additional': 'เพิ่มเติม',
    'cart.no_thanks': 'ไม่ ขอบคุณ',
    'cart.add_both': 'เพิ่มทั้งสองอย่าง',
    'cart.limited_time_offer': 'ข้อเสนอพิเศษเฉพาะช่วงเวลานี้',
    'cart.modes.dine_in': 'ทานที่ร้าน',
    'cart.modes.pickup': 'รับกลับบ้าน',
    'cart.modes.delivery': 'จัดส่งเดลิเวอรี่',
    'cart.modes_unavailable': 'ระบบสั่งอาหารยังไม่เปิดให้บริการขณะนี้',
    'cart.form.name': 'ชื่อ-นามสกุล',
    'cart.form.phone': 'เบอร์โทรศัพท์',
    'cart.form.table': 'หมายเลขโต๊ะ',
    'cart.form.address': 'ที่อยู่สำหรับจัดส่ง',
    'cart.form.table_placeholder': 'เช่น 12',
    'cart.delivery_estimate': 'ระยะเวลาจัดส่งโดยประมาณ',
    'cart.busy_estimate': 'เวลาประมาณการช่วงลูกค้าหนาแน่น',
    'cart.asap': 'เร็วที่สุด',
    'cart.schedule': 'กำหนดเวลาล่วงหน้า',
    'cart.address_placeholder': 'ชื่อถนน บ้านเลขที่ หรือจุดสังเกต',
    'cart.apartment': 'อาคาร / ห้อง',
    'cart.floor': 'ชั้น',
    'cart.door_code': 'รหัสประตู / คีย์การ์ด',
    'cart.map_pin': 'ลิงก์พิกัดแผนที่',
    'cart.delivery_instructions': 'คำแนะนำการจัดส่ง หรือแจ้งข้อจำกัดด้านอาหาร',
    'cart.contactless': 'วางไว้หน้าประตู (ไร้การสัมผัส)',
    'cart.payment.title': 'สั่งอาหารและชำระเงิน',
    'cart.payment.subtitle': 'ชำระเงินอย่างปลอดภัยที่โต๊ะ',
    'cart.checkout_failed': 'การชำระเงินไม่สำเร็จ',
    'cart.processing_payment': 'กำลังประมวลผลการชำระเงิน...',
    'cart.connecting_gateway': 'กำลังเชื่อมต่อไปยังช่องทางชำระเงิน...',
    'cart.payments_unavailable': 'ยังไม่มีช่องทางชำระเงิน',
    'cart.payments_unavailable_desc': 'ร้านค้ายังไม่ได้เปิดรับการชำระเงินออนไลน์',
    'cart.pay_online': 'ชำระเงินออนไลน์',
    'cart.credit_debit_card': 'บัตรเครดิตหรือเดบิต',
    'cart.or': 'หรือ',
    'cart.pay_courier_cash': 'ชำระเงินสดกับพนักงานส่ง',
    'cart.pay_counter_cash': 'ชำระเงินสดที่เคาน์เตอร์',
    'cart.secure_payment': 'การชำระเงินปลอดภัย',
    'cart.courier_payment': 'ชำระกับคนส่ง',
    'cart.counter_payment': 'ชำระที่เคาน์เตอร์',
    'cart.secure_payment_desc': 'ระบบชำระเงินผ่านบัตรเข้ารหัสความปลอดภัยระดับสูง',
    'cart.staff_payment_desc': 'พนักงานร้านจะมาเก็บเงินกับท่านโดยตรง',
    'cart.received.title': 'ได้รับคำสั่งซื้อแล้ว!',
    'cart.received.dinein': 'อาหารจะถูกนำมาเสิร์ฟที่โต๊ะของท่านในไม่ช้า',
    'cart.received.pickup': 'เราได้ส่งออเดอร์เข้าครัวเรียบร้อยแล้ว!',
    'cart.received.close': 'ปิดหน้าต่าง',
    'cart.delivery_confirmed': 'ยืนยันการจัดส่งสำหรับ',
    'cart.your_zone': 'พื้นที่ของคุณ',
    'cart.loyalty_earned': 'คุณได้รับ',
    'cart.loyalty_points': 'คะแนนสะสม!',
    'cart.how_experience': 'ประสบการณ์การสั่งซื้อของคุณเป็นอย่างไรบ้าง?',
    'cart.improve_placeholder': 'มีอะไรที่เราสามารถปรับปรุงได้บ้าง?',
    'cart.send_feedback': 'ส่งความคิดเห็นส่วนตัว',
    'cart.google_review': 'เขียนรีวิวบน Google',
    'cart.thank_feedback': 'ขอบคุณสำหรับความคิดเห็นของคุณ!',
    'cart.items_subtotal': 'รวมค่าอาหาร',
    'cart.delivery_fee': 'ค่าจัดส่ง',
    'cart.delivery': 'ค่าจัดส่ง',
    'cart.no_zone': 'อยู่นอกพื้นที่บริการ',
    'cart.free': 'ฟรี',
    'cart.packaging': 'ค่าบรรจุภัณฑ์',
    'cart.total_summary': 'ยอดรวมสุทธิ',
    'tracker.order_received': 'ได้รับคำสั่งซื้อแล้ว',
    'tracker.confirming_delivery': 'ทางร้านกำลังยืนยันการจัดส่ง',
    'tracker.waiting_kitchen': 'รอห้องครัวตอบรับออเดอร์',
    'tracker.chef_cooking': 'เชฟกำลังปรุงอาหาร',
    'tracker.delivery_preparing': 'กำลังจัดเตรียมอาหารสำหรับจัดส่ง',
    'tracker.food_preparing': 'อาหารกำลังปรุงอย่างพิถีพิถัน',
    'tracker.out_for_delivery': 'กำลังนำส่งถึงคุณ!',
    'tracker.ready_pickup': 'พร้อมรับอาหารแล้ว!',
    'tracker.courier_has_order': 'พนักงานส่งรับอาหารแล้วและกำลังเดินทาง',
    'tracker.come_to_counter': 'กรุณาติดต่อรับอาหารที่เคาน์เตอร์',
    'tracker.delivered': 'จัดส่งเรียบร้อยแล้ว!',
    'tracker.enjoy_meal': 'ขอให้อร่อยกับมื้ออาหาร!',
    'tracker.delivery_complete': 'การจัดส่งเสร็จสมบูรณ์',
    'tracker.order_completed': 'การสั่งซื้อเสร็จสมบูรณ์',
    'tracker.updating_status': 'กำลังอัปเดตสถานะ...',
    'tracker.order_again': 'สั่งอาหารอีกครั้ง',
    'reservation.title': 'จองโต๊ะอาหาร',
    'reservation.subtitle': 'ร่วมสัมผัสประสบการณ์มื้ออาหารสุดพิเศษกับเรา',
    'reservation.name': 'ชื่อ-นามสกุล',
    'reservation.name_placeholder': 'ชื่อของคุณ',
    'reservation.phone': 'เบอร์โทรศัพท์',
    'reservation.email': 'อีเมล',
    'reservation.guests': 'จำนวนท่าน',
    'reservation.date': 'วันที่',
    'reservation.time': 'เวลา',
    'reservation.notes': 'คำขอพิเศษ',
    'reservation.notes_placeholder': 'แจ้งแพ้อาหาร, วันเกิด, โซนที่นั่งที่ต้องการ...',
    'reservation.failed': 'การจองไม่สำเร็จ',
    'reservation.error_generic': 'ไม่สามารถส่งคำขอจองโต๊ะได้ กรุณาลองใหม่อีกครั้ง',
    'reservation.submitting': 'กำลังดำเนินการจองโต๊ะ...',
    'reservation.confirm': 'ยืนยันการจอง',
    'reservation.requested': 'ส่งคำขอจองโต๊ะเรียบร้อยแล้ว!',
    'reservation.requested_desc': 'เราจะตรวจสอบคำขอและส่งอีเมลยืนยันให้ท่านโดยเร็ว',
    'reservation.done': 'เสร็จสิ้น',
    'loyalty.title': 'โปรแกรมสะสมคะแนน',
    'loyalty.subtitle': 'รับคะแนนสะสมทุกครั้งที่สั่งอาหาร',
    'loyalty.welcome_back': 'ยินดีต้อนรับกลับมาอีกครั้ง!',
    'loyalty.welcome_desc': 'เข้าสู่ระบบเพื่อสะสมคะแนนและแลกรับสิทธิพิเศษ',
    'loyalty.view': 'ดูคะแนน',
    'loyalty.enter_email': 'กรอกอีเมลของคุณ',
    'loyalty.sending_code': 'กำลังส่งรหัส...',
    'loyalty.send_code': 'ส่งรหัสยืนยัน',
    'loyalty.enter_code_title': 'กรอกรหัสยืนยัน',
    'loyalty.code_placeholder': 'รหัส 6 หลัก',
    'loyalty.verifying': 'กำลังตรวจสอบ...',
    'loyalty.verify_signin': 'ยืนยันและเข้าสู่ระบบ',
    'loyalty.back_email': 'กลับไปแก้ไขอีเมล',
    'loyalty.available_points': 'คะแนนสะสมที่ใช้ได้',
    'loyalty.saved_to': 'บันทึกในบัญชี:',
    'loyalty.available_rewards': 'รางวัลที่แลกได้',
    'loyalty.redeem': 'แลกรับสิทธิ์',
    'loyalty.locked': 'คะแนนยังไม่พอ',
    'loyalty.no_rewards': 'ยังไม่มีของรางวัลให้แลกในขณะนี้',
    'loyalty.sign_out': 'ออกจากระบบ',
    'loyalty.switch_account': 'เปลี่ยนบัญชี',
    'footer.live_encryption': 'ระบบรักษาความปลอดภัยแบบเรียลไทม์',
    'footer.powered_by': 'สนับสนุนโดย',
    'footer.all_rights': 'สงวนลิขสิทธิ์ทั้งหมด',
    'menu.open_now': 'เปิดให้บริการ',
    'menu.closed': 'ปิดให้บริการ',
    'menu.delivery_only': 'เดลิเวอรี่เท่านั้น',
    'menu.book_table': 'จองโต๊ะ',
    'menu.rewards': 'ของรางวัล',
    'menu.table': 'โต๊ะ',
    'menu.experience_culinary': 'สัมผัสประสบการณ์ความอร่อยชั้นเลิศ',
    'menu.search_placeholder': 'ค้นหาเมนู...',
    'menu.dietary_filters': 'ตัวกรองอาหาร',
    'menu.exclude_allergens': 'ยกเว้นสารก่อภูมิแพ้',
    'menu.no_allergen': 'ไม่มี{allergen}',
    'dietary.vegetarian': 'มังสวิรัติ',
    'dietary.vegan': 'วีแกน',
    'dietary.spicy': 'เผ็ด',
    'dietary.gluten-free': 'ไร้กลูเตน',
    'dietary.nut-free': 'ไม่มีถั่ว',
    'dietary.dairy-free': 'ไม่มีนม',
    'dietary.healthy': 'เพื่อสุขภาพ',
    'dietary.halal': 'ฮาลาล',
    'dietary.organic': 'ออร์แกนิก',
    'dietary.keto': 'คีโต',
    'allergen.nuts': 'ถั่ว',
    'allergen.dairy': 'ผลิตภัณฑ์นม',
    'allergen.gluten': 'กลูเตน',
    'allergen.shellfish': 'สัตว์น้ำเปลือกแข็ง',
    'allergen.soy': 'ถั่วเหลือง',
    'allergen.eggs': 'ไข่',
    'allergen.fish': 'ปลา',
    'menu.chefs_recommendations': 'เมนูแนะนำโดยเชฟ',
    'menu.chefs': 'เมนูของเชฟ',
    'menu.recommendations': 'แนะนำ',
    'menu.items': 'รายการอาหาร',
    'menu.count': 'รายการ',
    'menu.section_closed': 'ปิดให้บริการ',
    'menu.dine_in_closed_delivery_available': 'บริการรับประทานที่ร้านปิดแล้ว มีบริการจัดส่งเดลิเวอรี่',
    'menu.section_closed_notice': 'ส่วนนี้ปิดให้บริการชั่วคราว',
    'menu.available': 'พร้อมเสิร์ฟ',
    'menu.sold_out': 'หมดแล้ว',
    'menu.mins': 'นาที',
    'menu.running_low': 'ใกล้หมด',
    'menu.adjusted': '(ปรับปรุงเวลา)',
    'menu.add_to_cart': 'เพิ่มลงตะกร้า',
    'menu.add_to_order': 'เพิ่มในรายการสั่ง',
    'menu.visual_composition': 'ส่วนประกอบ',
    'menu.small_details': 'รายละเอียด',
    'menu.made_with': 'ปรุงด้วย',
    'menu.guest_sentiment': 'ความคิดเห็นจากลูกค้า',
    'menu.guest_rating': 'คะแนนจากลูกค้า',
    'menu.how_was_experience': 'ความพึงพอใจของคุณเป็นอย่างไร?',
    'menu.thank_feedback': 'ขอบคุณสำหรับคำติชม!',
    'menu.rate_item': 'ให้คะแนนเมนูนี้',
    'menu.based_on_reviews': 'จากความคิดเห็น {count} รายการ',
    'menu.based_reviews': 'จากความคิดเห็น {count} รายการ',
    'menu.water': 'น้ำดื่ม',
    'menu.bill': 'เช็คบิล',
    'menu.cart': 'ตะกร้า',
    'menu.checkout': 'ชำระเงิน',
    'menu.requested': 'เรียกขอ{item}แล้ว',
    'menu.section': 'แผนก',
    'menu.is_closed': 'ปิดให้บริการ',
    'menu.section_closed_check_back': 'ส่วนนี้ปิดให้บริการอยู่ โปรดตรวจสอบอีกครั้งในช่วงเวลาทำการ',
  },
  id: {
    'menu.no_items': 'Tidak ada menu yang ditemukan',
    'menu.try_adjusting': 'Coba sesuaikan pencarian atau filter Anda',
    'menu.all': 'Semua',
    'menu.departments': 'Kategori Menu',
    'menu.learn_more': 'Pelajari Lebih Lanjut',
    'cart.title': 'Pesanan Anda',
    'cart.details': 'Detail Pengiriman',
    'cart.confirmed': 'Dikonfirmasi!',
    'cart.empty': 'Keranjang Anda kosong',
    'cart.continue': 'Lanjut ke Detail',
    'cart.back': 'Kembali',
    'cart.proceed_payment': 'Lanjut ke Pembayaran',
    'cart.qty': 'Jml',
    'cart.notes_placeholder': 'Kustomisasi: tanpa bawang, saus dipisah, alergi…',
    'cart.upsell.title': 'Paduan Menu Sempurna',
    'cart.upsell.add': 'Tambah',
    'cart.perfect_match': 'Kombinasi Pas',
    'cart.additional': 'Tambahan',
    'cart.no_thanks': 'Tidak, terima kasih',
    'cart.add_both': 'Tambah Keduanya',
    'cart.limited_time_offer': 'Penawaran terbatas untuk sesi meja ini',
    'cart.modes.dine_in': 'Makan di Tempat',
    'cart.modes.pickup': 'Bawa Pulang (Takeaway)',
    'cart.modes.delivery': 'Pesan Antar (Delivery)',
    'cart.modes_unavailable': 'Mode pemesanan saat ini tidak tersedia.',
    'cart.form.name': 'Nama Lengkap',
    'cart.form.phone': 'Nomor Telepon',
    'cart.form.table': 'Nomor Meja',
    'cart.form.address': 'Alamat Pengiriman',
    'cart.form.table_placeholder': 'contoh: 12',
    'cart.delivery_estimate': 'Estimasi Waktu Pengiriman',
    'cart.busy_estimate': 'estimasi jam sibuk',
    'cart.asap': 'Secepatnya',
    'cart.schedule': 'Jadwalkan',
    'cart.address_placeholder': 'Nama jalan, nomor rumah, atau patokan',
    'cart.apartment': 'Apartemen / Unit',
    'cart.floor': 'Lantai',
    'cart.door_code': 'Kode Pintu / Akses',
    'cart.map_pin': 'Tautan Titik Lokasi Peta',
    'cart.delivery_instructions': 'Catatan pengantaran, alergi, atau instruksi kurir',
    'cart.contactless': 'Titipkan di pintu (tanpa kontak)',
    'cart.payment.title': 'Pesan & Bayar',
    'cart.payment.subtitle': 'Pembayaran aman di meja',
    'cart.checkout_failed': 'Pembayaran Gagal',
    'cart.processing_payment': 'Memproses pembayaran...',
    'cart.connecting_gateway': 'Menghubungkan ke gateway pembayaran...',
    'cart.payments_unavailable': 'Metode Pembayaran Tidak Tersedia',
    'cart.payments_unavailable_desc': 'Restoran belum mengaktifkan metode pembayaran online.',
    'cart.pay_online': 'Bayar Online',
    'cart.credit_debit_card': 'Kartu Debit atau Kredit',
    'cart.or': 'Atau',
    'cart.pay_courier_cash': 'Bayar Tunai ke Kurir',
    'cart.pay_counter_cash': 'Bayar Tunai di Kasir',
    'cart.secure_payment': 'Pembayaran Aman',
    'cart.courier_payment': 'Bayar ke Kurir',
    'cart.counter_payment': 'Bayar di Kasir',
    'cart.secure_payment_desc': 'Pembayaran kartu terenkripsi aktif.',
    'cart.staff_payment_desc': 'Pembayaran akan dilayani langsung oleh staf restoran.',
    'cart.received.title': 'Pesanan Diterima!',
    'cart.received.dinein': 'Pesanan Anda akan segera diantarkan ke meja.',
    'cart.received.pickup': 'Pesanan sudah dikirim ke dapur. Mohon ditunggu!',
    'cart.received.close': 'Tutup Jendela',
    'cart.delivery_confirmed': 'Pengiriman dikonfirmasi untuk',
    'cart.your_zone': 'zona Anda',
    'cart.loyalty_earned': 'Anda mendapatkan',
    'cart.loyalty_points': 'Poin Loyalitas!',
    'cart.how_experience': 'Bagaimana pengalaman pemesanan Anda?',
    'cart.improve_placeholder': 'Apa yang bisa kami tingkatkan?',
    'cart.send_feedback': 'Kirim Masukan Pribadi',
    'cart.google_review': 'Beri Ulasan di Google',
    'cart.thank_feedback': 'Terima kasih atas ulasan Anda!',
    'cart.items_subtotal': 'Total Menu',
    'cart.delivery_fee': 'Ongkos Kirim',
    'cart.delivery': 'Ongkos Kirim',
    'cart.no_zone': 'Di luar area',
    'cart.free': 'Gratis',
    'cart.packaging': 'Biaya Kemasan',
    'cart.total_summary': 'Total Pembayaran',
    'tracker.order_received': 'Pesanan Diterima',
    'tracker.confirming_delivery': 'Restoran sedang mengonfirmasi pengiriman Anda',
    'tracker.waiting_kitchen': 'Menunggu konfirmasi dapur',
    'tracker.chef_cooking': 'Koki sedang memasak',
    'tracker.delivery_preparing': 'Pesanan sedang dikemas',
    'tracker.food_preparing': 'Makanan Anda sedang disiapkan',
    'tracker.out_for_delivery': 'Sedang Diantar!',
    'tracker.ready_pickup': 'Siap Diambil!',
    'tracker.courier_has_order': 'Kurir telah membawa pesanan Anda',
    'tracker.come_to_counter': 'Silakan ambil pesanan di kasir',
    'tracker.delivered': 'Terkirim!',
    'tracker.enjoy_meal': 'Selamat menikmati makanan Anda!',
    'tracker.delivery_complete': 'Pengiriman selesai',
    'tracker.order_completed': 'Pesanan selesai dengan sukses',
    'tracker.updating_status': 'Memperbarui status...',
    'tracker.order_again': 'Pesan Lagi',
    'reservation.title': 'Reservasi Meja',
    'reservation.subtitle': 'Nikmati momen kuliner istimewa bersama kami',
    'reservation.name': 'Nama Lengkap',
    'reservation.name_placeholder': 'Nama Anda',
    'reservation.phone': 'Nomor Telepon',
    'reservation.email': 'Alamat Email',
    'reservation.guests': 'Jumlah Tamu',
    'reservation.date': 'Tanggal',
    'reservation.time': 'Waktu',
    'reservation.notes': 'Permintaan Khusus',
    'reservation.notes_placeholder': 'Alergi, perayaan ulang tahun, preferensi meja...',
    'reservation.failed': 'Reservasi Gagal',
    'reservation.error_generic': 'Tidak dapat mengirimkan reservasi. Silakan coba lagi.',
    'reservation.submitting': 'Memproses reservasi meja...',
    'reservation.confirm': 'Konfirmasi Reservasi',
    'reservation.requested': 'Permintaan Reservasi Terkirim!',
    'reservation.requested_desc': 'Kami akan memeriksa dan mengirimkan konfirmasi via email.',
    'reservation.done': 'Selesai',
    'loyalty.title': 'Program Loyalitas',
    'loyalty.subtitle': 'Kumpulkan poin di setiap pesanan',
    'loyalty.welcome_back': 'Selamat Datang Kembali!',
    'loyalty.welcome_desc': 'Masuk untuk mengumpulkan poin dan menukarkannya dengan hadiah.',
    'loyalty.view': 'Lihat',
    'loyalty.enter_email': 'Masukkan email Anda',
    'loyalty.sending_code': 'Mengirim kode...',
    'loyalty.send_code': 'Kirim Kode Verifikasi',
    'loyalty.enter_code_title': 'Masukkan Kode Verifikasi',
    'loyalty.code_placeholder': '6 digit kode',
    'loyalty.verifying': 'Memverifikasi...',
    'loyalty.verify_signin': 'Verifikasi & Masuk',
    'loyalty.back_email': 'Kembali ke Email',
    'loyalty.available_points': 'Poin Tersedia',
    'loyalty.saved_to': 'Tersimpan untuk',
    'loyalty.available_rewards': 'Hadiah Tersedia',
    'loyalty.redeem': 'Tukarkan',
    'loyalty.locked': 'Terkunci',
    'loyalty.no_rewards': 'Belum ada hadiah yang tersedia saat ini.',
    'loyalty.sign_out': 'Keluar',
    'loyalty.switch_account': 'Ganti Akun',
    'footer.live_encryption': 'Enkripsi Langsung Aktif',
    'footer.powered_by': 'Didukung oleh',
    'footer.all_rights': 'Hak cipta dilindungi undang-undang.',
    'menu.open_now': 'Buka Sekarang',
    'menu.closed': 'Tutup',
    'menu.delivery_only': 'Hanya Pengiriman',
    'menu.book_table': 'Pesan Meja',
    'menu.rewards': 'Hadiah',
    'menu.table': 'Meja',
    'menu.experience_culinary': 'Nikmati keunggulan kuliner terbaik',
    'menu.search_placeholder': 'Cari menu...',
    'menu.dietary_filters': 'Filter diet',
    'menu.exclude_allergens': 'Kecualikan alergen',
    'menu.no_allergen': 'Tanpa {allergen}',
    'dietary.vegetarian': 'Vegetarian',
    'dietary.vegan': 'Vegan',
    'dietary.spicy': 'Pedas',
    'dietary.gluten-free': 'Bebas Gluten',
    'dietary.nut-free': 'Bebas Kacang',
    'dietary.dairy-free': 'Bebas Susu',
    'dietary.healthy': 'Sehat',
    'dietary.halal': 'Halal',
    'dietary.organic': 'Organik',
    'dietary.keto': 'Keto',
    'allergen.nuts': 'Kacang',
    'allergen.dairy': 'Produk Susu',
    'allergen.gluten': 'Gluten',
    'allergen.shellfish': 'Kerang',
    'allergen.soy': 'Kedelai',
    'allergen.eggs': 'Telur',
    'allergen.fish': 'Ikan',
    'menu.chefs_recommendations': 'Rekomendasi Koki',
    'menu.chefs': 'Koki',
    'menu.recommendations': 'Rekomendasi',
    'menu.items': 'Menu',
    'menu.count': 'Item',
    'menu.section_closed': 'Tutup',
    'menu.dine_in_closed_delivery_available': 'Makan di tempat sedang tutup. Layanan pesan antar tersedia.',
    'menu.section_closed_notice': 'Bagian ini sedang tutup.',
    'menu.available': 'TERSEDIA',
    'menu.sold_out': 'HABIS',
    'menu.mins': 'MNT',
    'menu.running_low': 'Hampir Habis',
    'menu.adjusted': '(DISESUAIKAN)',
    'menu.add_to_cart': 'Tambah ke Keranjang',
    'menu.add_to_order': 'Tambah ke Pesanan',
    'menu.visual_composition': 'Komposisi Bahan',
    'menu.small_details': 'Detail',
    'menu.made_with': 'Dibuat Dengan',
    'menu.guest_sentiment': 'Ulasan Pengunjung',
    'menu.guest_rating': 'Penilaian Tamu',
    'menu.how_was_experience': 'Bagaimana pengalaman Anda?',
    'menu.thank_feedback': 'Terima kasih atas ulasannya!',
    'menu.rate_item': 'Beri nilai item ini',
    'menu.based_on_reviews': 'Berdasarkan {count} ulasan tamu',
    'menu.based_reviews': 'Berdasarkan {count} ulasan tamu',
    'menu.water': 'Air Mineral',
    'menu.bill': 'Tagihan',
    'menu.cart': 'Keranjang',
    'menu.checkout': 'Pembayaran',
    'menu.requested': '{item} Diminta',
    'menu.section': 'Bagian',
    'menu.is_closed': 'Tutup',
    'menu.section_closed_check_back': 'Bagian ini saat ini tutup. Silakan periksa kembali saat jam buka.',
  },
  pl: {
    'menu.no_items': 'Nie znaleziono dań',
    'menu.try_adjusting': 'Spróbuj zmienić wyszukiwanie lub filtry',
    'menu.all': 'Wszystko',
    'menu.departments': 'Działy menu',
    'menu.learn_more': 'Dowiedz się więcej',
    'cart.title': 'Twoje zamówienie',
    'cart.details': 'Szczegóły dostawy',
    'cart.confirmed': 'Potwierdzono!',
    'cart.empty': 'Twój koszyk jest pusty',
    'cart.continue': 'Przejdź do szczegółów',
    'cart.back': 'Wstecz',
    'cart.proceed_payment': 'Przejdź do płatności',
    'cart.qty': 'Ilość',
    'cart.notes_placeholder': 'Dostosuj: bez cebuli, sos osobno, alergie…',
    'cart.upsell.title': 'Idealne dodatki',
    'cart.upsell.add': 'Dodaj',
    'cart.perfect_match': 'Świetny wybór',
    'cart.additional': 'Dodatkowo',
    'cart.no_thanks': 'Nie, dziękuję',
    'cart.add_both': 'Dodaj oba',
    'cart.limited_time_offer': 'Oferta limitowana dla tego stolika',
    'cart.modes.dine_in': 'Na miejscu',
    'cart.modes.pickup': 'Na wynos',
    'cart.modes.delivery': 'Dostawa',
    'cart.modes_unavailable': 'Opcje zamawiania są obecnie niedostępne.',
    'cart.form.name': 'Imię i nazwisko',
    'cart.form.phone': 'Numer telefonu',
    'cart.form.table': 'Numer stolika',
    'cart.form.address': 'Adres dostawy',
    'cart.form.table_placeholder': 'np. 12',
    'cart.delivery_estimate': 'Szacowany czas dostawy',
    'cart.busy_estimate': 'w godzinach szczytu',
    'cart.asap': 'Jak najszybciej',
    'cart.schedule': 'Zaplanuj',
    'cart.address_placeholder': 'Ulica i numer',
    'cart.apartment': 'Mieszkanie / Lokal',
    'cart.floor': 'Piętro',
    'cart.door_code': 'Kod do domofonu',
    'cart.map_pin': 'Link do lokalizacji na mapie',
    'cart.delivery_instructions': 'Wskazówki dla kuriera, alergie lub uwagi',
    'cart.contactless': 'Zostaw pod drzwiami (bez kontaktu)',
    'cart.payment.title': 'Zamów i zapłać',
    'cart.payment.subtitle': 'Bezpieczna płatność przy stoliku',
    'cart.checkout_failed': 'Płatność nie powiodła się',
    'cart.processing_payment': 'Przetwarzanie płatności...',
    'cart.connecting_gateway': 'Łączenie z bramką płatności...',
    'cart.payments_unavailable': 'Brak dostępnych płatności',
    'cart.payments_unavailable_desc': 'Lokal nie skonfigurował płatności online.',
    'cart.pay_online': 'Płać online',
    'cart.credit_debit_card': 'Karta płatnicza',
    'cart.or': 'Lub',
    'cart.pay_courier_cash': 'Gotówka u kuriera',
    'cart.pay_counter_cash': 'Gotówka przy kasie',
    'cart.secure_payment': 'Bezpieczna płatność',
    'cart.courier_payment': 'Płatność u kuriera',
    'cart.counter_payment': 'Płatność przy kasie',
    'cart.secure_payment_desc': 'Szyfrowana płatność kartą jest aktywna.',
    'cart.staff_payment_desc': 'Płatność zostanie pobrana przez obsługę lokalu.',
    'cart.received.title': 'Zamówienie przyjęte!',
    'cart.received.dinein': 'Dania zostaną wkrótce podane do Twojego stolika.',
    'cart.received.pickup': 'Przekazaliśmy zamówienie do kuchni. Do zobaczenia!',
    'cart.received.close': 'Zamknij okno',
    'cart.delivery_confirmed': 'Dostawa potwierdzona dla',
    'cart.your_zone': 'Twoja strefa',
    'cart.loyalty_earned': 'Zdobyłeś',
    'cart.loyalty_points': 'Punktów Lojalnościowych!',
    'cart.how_experience': 'Jak oceniasz proces zamawiania?',
    'cart.improve_placeholder': 'Co możemy poprawić?',
    'cart.send_feedback': 'Wyślij prywatną opinię',
    'cart.google_review': 'Wystaw opinię w Google',
    'cart.thank_feedback': 'Dziękujemy za Twoją opinię!',
    'cart.items_subtotal': 'Pozycje',
    'cart.delivery_fee': 'Dostawa',
    'cart.delivery': 'Dostawa',
    'cart.no_zone': 'Poza strefą',
    'cart.free': 'Gratis',
    'cart.packaging': 'Opakowanie',
    'cart.total_summary': 'Podsumowanie',
    'tracker.order_received': 'Zamówienie przyjęte',
    'tracker.confirming_delivery': 'Lokal potwierdza Twoją dostawę',
    'tracker.waiting_kitchen': 'Oczekiwanie na potwierdzenie kuchni',
    'tracker.chef_cooking': 'Szef kuchni przygotowuje dania',
    'tracker.delivery_preparing': 'Twoja dostawa jest pakowana',
    'tracker.food_preparing': 'Twoje jedzenie jest przygotowywane',
    'tracker.out_for_delivery': 'W drodze do Ciebie!',
    'tracker.ready_pickup': 'Gotowe do odbioru!',
    'tracker.courier_has_order': 'Kurier odebrał Twoje zamówienie',
    'tracker.come_to_counter': 'Prosimy podejść do kasy',
    'tracker.delivered': 'Dostarczono!',
    'tracker.enjoy_meal': 'Smacznego!',
    'tracker.delivery_complete': 'Dostawa została zakończona',
    'tracker.order_completed': 'Zamówienie zrealizowane pomyślnie',
    'tracker.updating_status': 'Aktualizacja statusu...',
    'tracker.order_again': 'Zamów ponownie',
    'reservation.title': 'Zarezerwuj stolik',
    'reservation.subtitle': 'Dołącz do nas i przeżyj niezapomniane chwile',
    'reservation.name': 'Imię i nazwisko',
    'reservation.name_placeholder': 'Twoje imię',
    'reservation.phone': 'Numer telefonu',
    'reservation.email': 'Adres e-mail',
    'reservation.guests': 'Liczba gości',
    'reservation.date': 'Data',
    'reservation.time': 'Godzina',
    'reservation.notes': 'Uwagi specjalne',
    'reservation.notes_placeholder': 'Alergie, urodziny, preferencje dotyczące stolika...',
    'reservation.failed': 'Błąd rezerwacji',
    'reservation.error_generic': 'Nie udało się przesłać rezerwacji. Spróbuj ponownie.',
    'reservation.submitting': 'Rezerwowanie stolika...',
    'reservation.confirm': 'Potwierdź rezerwację',
    'reservation.requested': 'Wysłano prośbę o rezerwację!',
    'reservation.requested_desc': 'Przeanalizujemy zgłoszenie i wkrótce wyślemy potwierdzenie na Twój e-mail.',
    'reservation.done': 'Gotowe',
    'loyalty.title': 'Program lojalnościowy',
    'loyalty.subtitle': 'Zbieraj punkty przy każdym zamówieniu',
    'loyalty.welcome_back': 'Witaj ponownie!',
    'loyalty.welcome_desc': 'Zaloguj się, aby gromadzić punkty przy każdej wizycie.',
    'loyalty.view': 'Zobacz',
    'loyalty.enter_email': 'Wpisz swój e-mail',
    'loyalty.sending_code': 'Wysyłanie kodu...',
    'loyalty.send_code': 'Wyślij kod weryfikacyjny',
    'loyalty.enter_code_title': 'Wpisz kod weryfikacyjny',
    'loyalty.code_placeholder': '6-cyfrowy kod',
    'loyalty.verifying': 'Weryfikacja...',
    'loyalty.verify_signin': 'Zweryfikuj i zaloguj',
    'loyalty.back_email': 'Wróć do e-maila',
    'loyalty.available_points': 'Dostępne punkty',
    'loyalty.saved_to': 'Zapisano dla',
    'loyalty.available_rewards': 'Dostępne nagrody',
    'loyalty.redeem': 'Odbierz',
    'loyalty.locked': 'Zablokowane',
    'loyalty.no_rewards': 'Brak dostępnych nagród w tej chwili.',
    'loyalty.sign_out': 'Wyloguj się',
    'loyalty.switch_account': 'Zmień konto',
    'footer.live_encryption': 'Aktywne szyfrowanie na żywo',
    'footer.powered_by': 'Obsługiwane przez',
    'footer.all_rights': 'Wszelkie prawa zastrzeżone.',
    'menu.open_now': 'Otwarte',
    'menu.closed': 'Zamknięte',
    'menu.delivery_only': 'Tylko dostawa',
    'menu.book_table': 'Zarezerwuj stolik',
    'menu.rewards': 'Nagrody',
    'menu.table': 'Stolik',
    'menu.experience_culinary': 'Doświadcz kulinarnej doskonałości',
    'menu.search_placeholder': 'Szukaj dań...',
    'menu.dietary_filters': 'Filtry dietetyczne',
    'menu.exclude_allergens': 'Wyklucz alergeny',
    'menu.no_allergen': 'Bez: {allergen}',
    'dietary.vegetarian': 'Wegetariańskie',
    'dietary.vegan': 'Wegańskie',
    'dietary.spicy': 'Ostre',
    'dietary.gluten-free': 'Bezglutenowe',
    'dietary.nut-free': 'Bez orzechów',
    'dietary.dairy-free': 'Bez laktozy',
    'dietary.healthy': 'Zdrowe',
    'dietary.halal': 'Halal',
    'dietary.organic': 'Eko',
    'dietary.keto': 'Keto',
    'allergen.nuts': 'Orzechy',
    'allergen.dairy': 'Nabiał',
    'allergen.gluten': 'Gluten',
    'allergen.shellfish': 'Skorupiaki',
    'allergen.soy': 'Soja',
    'allergen.eggs': 'Jaja',
    'allergen.fish': 'Ryby',
    'menu.chefs_recommendations': 'Polecane przez Szefa Kuchni',
    'menu.chefs': 'Szefa Kuchni',
    'menu.recommendations': 'Polecane',
    'menu.items': 'Pozycje menu',
    'menu.count': 'Dania',
    'menu.section_closed': 'Zamknięte',
    'menu.dine_in_closed_delivery_available': 'Obsługa na miejscu jest zamknięta. Dostawa jest dostępna.',
    'menu.section_closed_notice': 'Ta sekcja jest obecnie zamknięta.',
    'menu.available': 'DOSTĘPNE',
    'menu.sold_out': 'WYPRZEDANE',
    'menu.mins': 'MIN',
    'menu.running_low': 'Ostatnie sztuki',
    'menu.adjusted': '(SKORYGOWANO)',
    'menu.add_to_cart': 'Dodaj do koszyka',
    'menu.add_to_order': 'Dodaj do zamówienia',
    'menu.visual_composition': 'Składniki dania',
    'menu.small_details': 'Szczegóły',
    'menu.made_with': 'Przyrządzono z',
    'menu.guest_sentiment': 'Opinie gości',
    'menu.guest_rating': 'Ocena gości',
    'menu.how_was_experience': 'Jak oceniasz swoje wrażenia?',
    'menu.thank_feedback': 'Dziękujemy za opinię!',
    'menu.rate_item': 'Oceń to danie',
    'menu.based_on_reviews': 'Na podstawie {count} opinii gości',
    'menu.based_reviews': 'Na podstawie {count} opinii gości',
    'menu.water': 'Woda',
    'menu.bill': 'Rachunek',
    'menu.cart': 'Koszyk',
    'menu.checkout': 'Kasa',
    'menu.requested': 'Poproszono o: {item}',
    'menu.section': 'Sekcja',
    'menu.is_closed': 'jest zamknięta',
    'menu.section_closed_check_back': 'Ta sekcja jest obecnie zamknięta. Zapraszamy w godzinach otwarcia.',
  },
  sv: {
    'menu.no_items': 'Inga artiklar hittades',
    'menu.try_adjusting': 'Försök justera din sökning eller filter',
    'menu.all': 'Alla',
    'menu.departments': 'Menyavdelningar',
    'menu.learn_more': 'Läs mer',
    'cart.title': 'Din beställning',
    'cart.details': 'Leveransuppgifter',
    'cart.confirmed': 'Bekräftad!',
    'cart.empty': 'Din varukorg är tom',
    'cart.continue': 'Fortsätt till uppgifter',
    'cart.back': 'Tillbaka',
    'cart.proceed_payment': 'Gå till betalning',
    'cart.qty': 'Antal',
    'cart.notes_placeholder': 'Anpassa: utan lök, sås vid sidan, allergier…',
    'cart.upsell.title': 'Perfekta tillbehör',
    'cart.upsell.add': 'Lägg till',
    'cart.perfect_match': 'Perfekt matchning',
    'cart.additional': 'Extra',
    'cart.no_thanks': 'Nej, tack',
    'cart.add_both': 'Lägg till båda',
    'cart.limited_time_offer': 'Tidsbegränsat erbjudande för detta bord',
    'cart.modes.dine_in': 'På plats',
    'cart.modes.pickup': 'Avhämtning',
    'cart.modes.delivery': 'Hemleverans',
    'cart.modes_unavailable': 'Beställningslägen är för närvarande inte tillgängliga.',
    'cart.form.name': 'Fullständigt namn',
    'cart.form.phone': 'Telefonnummer',
    'cart.form.table': 'Bordsnummer',
    'cart.form.address': 'Leveransadress',
    'cart.form.table_placeholder': 't.ex. 12',
    'cart.delivery_estimate': 'Beräknad leveranstid',
    'cart.busy_estimate': 'uppskattning under rusningstid',
    'cart.asap': 'Snarast möjligt',
    'cart.schedule': 'Schemalägg',
    'cart.address_placeholder': 'Gata och nummer',
    'cart.apartment': 'Lägenhet / Lgh',
    'cart.floor': 'Våning',
    'cart.door_code': 'Portkod',
    'cart.map_pin': 'Kartlänk',
    'cart.delivery_instructions': 'Leveransinstruktioner eller allergier',
    'cart.contactless': 'Lämna vid dörren (kontaktfritt)',
    'cart.payment.title': 'Beställ och betala',
    'cart.payment.subtitle': 'Säker betalning vid bordet',
    'cart.checkout_failed': 'Betalningen misslyckades',
    'cart.processing_payment': 'Behandlar betalning...',
    'cart.connecting_gateway': 'Ansluter till säker betalningsgateway...',
    'cart.payments_unavailable': 'Inga betalningsmetoder',
    'cart.payments_unavailable_desc': 'Denna restaurang har inte aktiverat onlinebetalning.',
    'cart.pay_online': 'Betala online',
    'cart.credit_debit_card': 'Kredit- eller betalkort',
    'cart.or': 'Eller',
    'cart.pay_courier_cash': 'Betala kontant till budet',
    'cart.pay_counter_cash': 'Betala kontant i kassan',
    'cart.secure_payment': 'Säker betalning',
    'cart.courier_payment': 'Betalning till bud',
    'cart.counter_payment': 'Betalning i kassa',
    'cart.secure_payment_desc': 'Krypterad kortbetalning är aktiverad.',
    'cart.staff_payment_desc': 'Betalning tas emot av restaurangens personal.',
    'cart.received.title': 'Beställning mottagen!',
    'cart.received.dinein': 'Dina rätter serveras vid bordet inom kort.',
    'cart.received.pickup': 'Vi har skickat beställningen till köket!',
    'cart.received.close': 'Stäng fönster',
    'cart.delivery_confirmed': 'Leverans bekräftad för',
    'cart.your_zone': 'din zon',
    'cart.loyalty_earned': 'Du tjänade',
    'cart.loyalty_points': 'Lojalitetspoäng!',
    'cart.how_experience': 'Hur var din beställningsupplevelse?',
    'cart.improve_placeholder': 'Vad kan vi förbättra?',
    'cart.send_feedback': 'Skicka privat feedback',
    'cart.google_review': 'Lämna ett Google-omdöme',
    'cart.thank_feedback': 'Tack för din feedback!',
    'cart.items_subtotal': 'Artiklar',
    'cart.delivery_fee': 'Leverans',
    'cart.delivery': 'Leverans',
    'cart.no_zone': 'Utanför leveransområde',
    'cart.free': 'Gratis',
    'cart.packaging': 'Förpackning',
    'cart.total_summary': 'Totalsumma',
    'tracker.order_received': 'Beställning mottagen',
    'tracker.confirming_delivery': 'Restaurangen bekräftar din leverans',
    'tracker.waiting_kitchen': 'Väntar på godkännande från köket',
    'tracker.chef_cooking': 'Kocken tillagar maten',
    'tracker.delivery_preparing': 'Din leverans förbereds',
    'tracker.food_preparing': 'Din mat tillagas',
    'tracker.out_for_delivery': 'Ute för leverans!',
    'tracker.ready_pickup': 'Klar för avhämtning!',
    'tracker.courier_has_order': 'Budet har hämtat din beställning',
    'tracker.come_to_counter': 'Vänligen kom till kassan',
    'tracker.delivered': 'Levererad!',
    'tracker.enjoy_meal': 'Smaklig måltid!',
    'tracker.delivery_complete': 'Din leverans är slutförd',
    'tracker.order_completed': 'Beställningen slutfördes framgångsrikt',
    'tracker.updating_status': 'Uppdaterar status...',
    'tracker.order_again': 'Beställ igen',
    'reservation.title': 'Boka bord',
    'reservation.subtitle': 'Njut av en oförglömlig upplevelse',
    'reservation.name': 'Fullständigt namn',
    'reservation.name_placeholder': 'Ditt namn',
    'reservation.phone': 'Telefonnummer',
    'reservation.email': 'E-postadress',
    'reservation.guests': 'Antal gäster',
    'reservation.date': 'Datum',
    'reservation.time': 'Tid',
    'reservation.notes': 'Särskilda önskemål',
    'reservation.notes_placeholder': 'Allergier, födelsedag, bordsönskemål...',
    'reservation.failed': 'Bokningen misslyckades',
    'reservation.error_generic': 'Kunde inte skicka bokningen. Försök igen.',
    'reservation.submitting': 'Bokar ditt bord...',
    'reservation.confirm': 'Bekräfta bokning',
    'reservation.requested': 'Bokningsförfrågan skickad!',
    'reservation.requested_desc': 'Vi granskar förfrågan och skickar bekräftelse via e-post inom kort.',
    'reservation.done': 'Klart',
    'loyalty.title': 'Lojalitetsprogram',
    'loyalty.subtitle': 'Tjäna poäng på varje beställning',
    'loyalty.welcome_back': 'Välkommen tillbaka!',
    'loyalty.welcome_desc': 'Logga in för att spara dina poäng vid varje besök.',
    'loyalty.view': 'Visa',
    'loyalty.enter_email': 'Ange din e-postadress',
    'loyalty.sending_code': 'Skickar kod...',
    'loyalty.send_code': 'Skicka verifieringskod',
    'loyalty.enter_code_title': 'Ange verifieringskod',
    'loyalty.code_placeholder': '6-siffrig kod',
    'loyalty.verifying': 'Verifierar...',
    'loyalty.verify_signin': 'Verifiera och logga in',
    'loyalty.back_email': 'Tillbaka till e-post',
    'loyalty.available_points': 'Tillgängliga poäng',
    'loyalty.saved_to': 'Sparat för',
    'loyalty.available_rewards': 'Tillgängliga belöningar',
    'loyalty.redeem': 'Lös in',
    'loyalty.locked': 'Låst',
    'loyalty.no_rewards': 'Inga belöningar tillgängliga just nu.',
    'loyalty.sign_out': 'Logga ut',
    'loyalty.switch_account': 'Byt konto',
    'footer.live_encryption': 'Livekryptering aktiv',
    'footer.powered_by': 'Drivs av',
    'footer.all_rights': 'Alla rättigheter förbehållna.',
    'menu.open_now': 'Öppet nu',
    'menu.closed': 'Stängt',
    'menu.delivery_only': 'Endast leverans',
    'menu.book_table': 'Boka bord',
    'menu.rewards': 'Belöningar',
    'menu.table': 'Bord',
    'menu.experience_culinary': 'Upplev kulinarisk perfektion',
    'menu.search_placeholder': 'Sök i menyn...',
    'menu.dietary_filters': 'Kostfilter',
    'menu.exclude_allergens': 'Exkludera allergener',
    'menu.no_allergen': 'Utan {allergen}',
    'dietary.vegetarian': 'Vegetariskt',
    'dietary.vegan': 'Veganskt',
    'dietary.spicy': 'Starkt',
    'dietary.gluten-free': 'Glutenfritt',
    'dietary.nut-free': 'Nötfri',
    'dietary.dairy-free': 'Mellkfri',
    'dietary.healthy': 'Hälsosamt',
    'dietary.halal': 'Halal',
    'dietary.organic': 'Ekologiskt',
    'dietary.keto': 'Keto',
    'allergen.nuts': 'Nötter',
    'allergen.dairy': 'Mejeri',
    'allergen.gluten': 'Gluten',
    'allergen.shellfish': 'Skaldjur',
    'allergen.soy': 'Soja',
    'allergen.eggs': 'Ägg',
    'allergen.fish': 'Fisk',
    'menu.chefs_recommendations': 'Kockens rekommendationer',
    'menu.chefs': 'Kockens',
    'menu.recommendations': 'Rekommendationer',
    'menu.items': 'Menyartiklar',
    'menu.count': 'Artiklar',
    'menu.section_closed': 'Stängt',
    'menu.dine_in_closed_delivery_available': 'Servering på plats är stängd. Leverans är tillgänglig.',
    'menu.section_closed_notice': 'Denna sektion är för närvarande stängd.',
    'menu.available': 'TILLGÄNGLIG',
    'menu.sold_out': 'SLUTSÅLD',
    'menu.mins': 'MIN',
    'menu.running_low': 'Få kvar',
    'menu.adjusted': '(JUSTERAD)',
    'menu.add_to_cart': 'Lägg i varukorg',
    'menu.add_to_order': 'Lägg till i beställning',
    'menu.visual_composition': 'Ingredienser',
    'menu.small_details': 'Detaljer',
    'menu.made_with': 'Tillagad med',
    'menu.guest_sentiment': 'Gästrecensioner',
    'menu.guest_rating': 'Gästbetyg',
    'menu.how_was_experience': 'Hur var din upplevelse?',
    'menu.thank_feedback': 'Tack för din feedback!',
    'menu.rate_item': 'Betygsätt denna rätt',
    'menu.based_on_reviews': 'Baserat på {count} gästrecensioner',
    'menu.based_reviews': 'Baserat på {count} gästrecensioner',
    'menu.water': 'Vatten',
    'menu.bill': 'Notan',
    'menu.cart': 'Varukorg',
    'menu.checkout': 'Till kassan',
    'menu.requested': '{item} beställt',
    'menu.section': 'Sektion',
    'menu.is_closed': 'är stängd',
    'menu.section_closed_check_back': 'Denna sektion är stängd för tillfället. Vänligen återkom under öppettiderna.',
  },
};

export const translations: TranslationDictionary = {
  en: {
    ...guestMenuTranslations.en,
  },
  es: {
    ...guestMenuTranslations.es,
    'admin.nav.intelligence': 'Estudio de inteligencia',
    'admin.nav.insights': 'Analítica del local',
    'admin.nav.departments': 'Departamentos',
    'admin.nav.service_hours': 'Horarios de servicio',
    'admin.nav.categories': 'Categorías',
    'admin.nav.items': 'Artículos del menú',
    'admin.nav.ingredients': 'Ingredientes',
    'admin.nav.loyalty': 'Programa de fidelidad',
    'admin.nav.campaigns': 'Estudio de campañas',
    'admin.nav.signage': 'Pantallas',
    'admin.nav.qr': 'Código QR',
    'admin.nav.kitchen': 'Operaciones de cocina',
    'admin.nav.theme': 'Motor de tema',
    'admin.nav.governance': 'Gobernanza',
    'admin.nav.audit': 'Registro de auditoría',
    'admin.nav.api': 'API y webhooks',
    'admin.nav.sso': 'SSO y seguridad',
    'admin.nav.team': 'Equipo',
    'admin.nav.billing': 'Facturación',
    'admin.nav.tax': 'Impuestos y Cumplimiento',
    'admin.nav.seo': 'SEO y Google',
    'admin.nav.notifications': 'Notificaciones',
    'admin.nav.support': 'Centro de soporte',
    'admin.nav.settings': 'Configuración',
    'admin.subtitle.seo': 'Asistente de visibilidad local',
    'admin.subtitle.admin': 'Resumen estratégico',
    'admin.subtitle.insights': 'Rendimiento real del local',
    'admin.subtitle.departments': 'Segmentación del local',
    'admin.subtitle.service-hours': 'Programación por franjas',
    'admin.subtitle.categories': 'Arquitectura del menú',
    'admin.subtitle.items': 'Control de inventario',
    'admin.subtitle.ingredients': 'Biblioteca de activos',
    'admin.subtitle.campaigns': 'Orquestación de ingresos',
    'admin.subtitle.signage': 'Menú en pantalla grande',
    'admin.subtitle.qr': 'Presencia digital',
    'admin.subtitle.theme': 'Identidad visual',
    'admin.subtitle.audit': 'Forense del sistema',
    'admin.subtitle.team': 'Gobernanza de acceso',
    'admin.subtitle.billing': 'Resumen fiscal',
    'admin.subtitle.tax': 'Gestión fiscal y libros contables',
    'admin.subtitle.settings': 'Lógica operativa',
    'admin.subtitle.loyalty': 'Orquestación de retención',
    'admin.subtitle.governance': 'Control multilocal',
    'admin.subtitle.api': 'Ecosistema para desarrolladores',
    'admin.subtitle.sso': 'Identidad empresarial',
    'admin.subtitle.notifications': 'Consola de alertas operativas',
    'admin.subtitle.support': 'Ayuda y comunicaciones',
    'admin.subtitle.default': 'Consola de gestión',
    'admin.sign_out': 'Cerrar sesión',
    'admin.user.administrator': 'Administrador',
    'admin.user.global_admin': 'Admin global',
    'admin.user.staff_access': 'Acceso del personal',
    'admin.language.change': 'Cambiar idioma',
    'admin.command.search_placeholder': "Buscar comandos... (ej. 'inventario', 'precios')",
    'admin.command.esc': 'ESC para cerrar',
    'admin.command.close': 'Cerrar paleta de comandos',
    'admin.command.categories.pages': 'Páginas',
    'admin.command.categories.menu_items': 'Artículos del menú',
    'admin.command.categories.categories': 'Categorías',
    'admin.command.categories.actions': 'Acciones',
    'admin.command.overview': 'Resumen de tu local',
    'admin.command.business_data': 'Datos de inteligencia de negocio',
    'admin.command.manage_items': 'Gestiona artículos, stock y disponibilidad',
    'admin.command.organize_taxonomy': 'Organiza la taxonomía del menú',
    'admin.command.manage_staff': 'Gestiona accesos e invitaciones',
    'admin.command.customize_appearance': 'Personaliza la apariencia del menú',
    'admin.command.create_qr': 'Crea y exporta códigos QR',
    'admin.command.configure_signage': 'Configura el menú de pantalla grande',
    'admin.command.audit_log': 'Registro de transparencia y seguridad',
    'admin.command.business_details': 'Configura los datos del negocio',
    'admin.command.export_menu': 'Exportar menú (CSV)',
    'admin.command.export_current': 'Exportación rápida de datos actuales',
    'admin.command.view_category': 'Ver categoría en el gestor',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Operaciones del Chef",
    'chef.kitchen_mode': "Modo Cocina",
    'chef.exit_kitchen_mode': "Salir del modo cocina",
    'chef.start_preparing': "Comenzar preparación",
    'chef.mark_ready': "Marcar como listo",
    'chef.finalize_order': "Finalizar pedido",
    'chef.nothing_to_track': "Nada que rastrear aún",
    'chef.preparing_time': "Preparando: {time}",
    'chef.waiting_time': "En espera: {time}",
    'chef.ready_time': "Listo: {time}",
    'chef.table': "Mesa",
    'chef.dine_in': "En sala",
    'chef.delivery': "Entrega",
    'chef.pickup': "Para llevar",
    'waiter.title': "Servicio de Sala",
    'waiter.table_calls': "Llamadas de Mesa",
    'waiter.ready_pickups': "Pedidos Listos",
    'waiter.active_tickets': "Tickets Activos",
    'waiter.no_calls': "Sin llamadas de mesa pendientes",
    'waiter.nothing_pickup': "Nada listo para recoger aún",
    'waiter.no_active_orders': "Sin pedidos activos",
    'waiter.mark_attended': "Marcar como atendido",
    'waiter.ready_for_pickup': "Listo para servir",
    'waiter.call_request': "Asistencia en Mesa",
    'waiter.bill_request': "Cuenta Solicitada",
    'waiter.water_request': "Agua Solicitada",
    'status.active': "Activo",
    'status.pending': "Pendiente",
    'status.preparing': "En preparación",
    'status.ready': "Listo",
    'status.completed': "Completado",
    'status.cancelled': "Cancelado",
    'status.paid': "Pagado",
    'status.unpaid': "No pagado",
  },
  fr: {
    'admin.nav.intelligence': "Studio d'intelligence",
    'admin.nav.insights': 'Analyses du lieu',
    'admin.nav.departments': 'Départements',
    'admin.nav.service_hours': 'Heures de service',
    'admin.nav.categories': 'Catégories',
    'admin.nav.items': 'Articles du menu',
    'admin.nav.ingredients': 'Ingrédients',
    'admin.nav.loyalty': 'Programme de fidélité',
    'admin.nav.campaigns': 'Studio de campagnes',
    'admin.nav.signage': 'Affichage',
    'admin.nav.qr': 'Code QR',
    'admin.nav.kitchen': 'Opérations cuisine',
    'admin.nav.theme': 'Moteur de thème',
    'admin.nav.governance': 'Gouvernance',
    'admin.nav.audit': "Journal d'audit",
    'admin.nav.api': 'API et webhooks',
    'admin.nav.sso': 'SSO et sécurité',
    'admin.nav.team': 'Équipe',
    'admin.nav.billing': 'Facturation',
    'admin.nav.tax': 'Fiscalité et Conformité',
    'admin.nav.seo': 'SEO et Google',
    'admin.nav.notifications': 'Notifications',
    'admin.nav.support': 'Centre de support',
    'admin.nav.settings': 'Paramètres',
    'admin.subtitle.seo': 'Assistant de visibilité locale',
    'admin.subtitle.admin': 'Vue stratégique',
    'admin.subtitle.insights': 'Performance réelle du lieu',
    'admin.subtitle.departments': 'Segmentation du lieu',
    'admin.subtitle.service-hours': 'Planification par service',
    'admin.subtitle.categories': 'Architecture du menu',
    'admin.subtitle.items': "Contrôle de l'inventaire",
    'admin.subtitle.ingredients': "Bibliothèque d'actifs",
    'admin.subtitle.campaigns': 'Orchestration des revenus',
    'admin.subtitle.signage': 'Menu grand écran',
    'admin.subtitle.qr': 'Présence numérique',
    'admin.subtitle.theme': 'Identité visuelle',
    'admin.subtitle.audit': 'Analyse système',
    'admin.subtitle.team': "Gouvernance d'accès",
    'admin.subtitle.billing': 'Vue fiscale',
    'admin.subtitle.tax': 'Gestion fiscale et registres TVA',
    'admin.subtitle.settings': 'Logique opérationnelle',
    'admin.subtitle.loyalty': 'Orchestration de rétention',
    'admin.subtitle.governance': 'Contrôle multi-lieux',
    'admin.subtitle.api': 'Écosystème développeur',
    'admin.subtitle.sso': 'Identité entreprise',
    'admin.subtitle.notifications': "Console d'alertes opérationnelles",
    'admin.subtitle.support': 'Aide et communications',
    'admin.subtitle.default': 'Console de gestion',
    'admin.sign_out': 'Se déconnecter',
    'admin.user.administrator': 'Administrateur',
    'admin.user.global_admin': 'Admin global',
    'admin.user.staff_access': 'Accès équipe',
    'admin.language.change': 'Changer la langue',
    'admin.command.search_placeholder': "Rechercher une commande... (ex. 'inventaire', 'tarifs')",
    'admin.command.esc': 'ESC pour fermer',
    'admin.command.close': 'Fermer la palette de commandes',
    'admin.command.categories.pages': 'Pages',
    'admin.command.categories.menu_items': 'Articles du menu',
    'admin.command.categories.categories': 'Catégories',
    'admin.command.categories.actions': 'Actions',
    'admin.command.overview': 'Vue d’ensemble de votre lieu',
    'admin.command.business_data': 'Données de business intelligence',
    'admin.command.manage_items': 'Gérer les articles, le stock et la disponibilité',
    'admin.command.organize_taxonomy': 'Organiser la taxonomie du menu',
    'admin.command.manage_staff': 'Gérer les accès et invitations',
    'admin.command.customize_appearance': "Personnaliser l'apparence du menu",
    'admin.command.create_qr': 'Créer et exporter des codes QR',
    'admin.command.configure_signage': 'Configurer le menu grand écran',
    'admin.command.audit_log': 'Journal de transparence et sécurité',
    'admin.command.business_details': "Configurer les détails de l'entreprise",
    'admin.command.export_menu': 'Exporter le menu (CSV)',
    'admin.command.export_current': 'Export rapide des données actuelles',
    'admin.command.view_category': 'Voir la catégorie dans le gestionnaire',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Opérations Chef",
    'chef.kitchen_mode': "Mode Cuisine",
    'chef.exit_kitchen_mode': "Quitter le mode cuisine",
    'chef.start_preparing': "Commencer la préparation",
    'chef.mark_ready': "Prêt à servir",
    'chef.finalize_order': "Finaliser la commande",
    'chef.nothing_to_track': "Aucune commande pour le moment",
    'chef.preparing_time': "En préparation : {time}",
    'chef.waiting_time': "En attente : {time}",
    'chef.ready_time': "Prêt : {time}",
    'chef.table': "Table",
    'chef.dine_in': "Sur place",
    'chef.delivery': "Livraison",
    'chef.pickup': "À emporter",
    'waiter.title': "Service en Salle",
    'waiter.table_calls': "Appels Tables",
    'waiter.ready_pickups': "Plats Prêts",
    'waiter.active_tickets': "Commandes En Cours",
    'waiter.no_calls': "Aucun appel de table en attente",
    'waiter.nothing_pickup': "Rien à récupérer pour le moment",
    'waiter.no_active_orders': "Aucune commande active",
    'waiter.mark_attended': "Marquer comme traité",
    'waiter.ready_for_pickup': "Prêt à servir",
    'waiter.call_request': "Demande de Serveur",
    'waiter.bill_request': "L'addition Demandée",
    'waiter.water_request': "Bouteille d'eau Demandée",
    'status.active': "Actif",
    'status.pending': "En attente",
    'status.preparing': "En préparation",
    'status.ready': "Prêt",
    'status.completed': "Terminé",
    'status.cancelled': "Annulé",
    'status.paid': "Payé",
    'status.unpaid': "Non payé",
  },
  ar: {
    'admin.nav.intelligence': 'استوديو الذكاء',
    'admin.nav.insights': 'رؤى المطعم',
    'admin.nav.departments': 'الأقسام',
    'admin.nav.service_hours': 'ساعات الخدمة',
    'admin.nav.categories': 'الفئات',
    'admin.nav.items': 'عناصر القائمة',
    'admin.nav.ingredients': 'المكونات',
    'admin.nav.loyalty': 'برنامج الولاء',
    'admin.nav.campaigns': 'استوديو الحملات',
    'admin.nav.signage': 'الشاشات',
    'admin.nav.qr': 'رمز QR',
    'admin.nav.kitchen': 'عمليات المطبخ',
    'admin.nav.theme': 'محرك التصميم',
    'admin.nav.governance': 'الحوكمة',
    'admin.nav.audit': 'سجل التدقيق',
    'admin.nav.api': 'API والويبهوكس',
    'admin.nav.sso': 'SSO والأمان',
    'admin.nav.team': 'الفريق',
    'admin.nav.billing': 'الفوترة',
    'admin.nav.tax': 'الضرائب والامتثال',
    'admin.nav.seo': 'سيو وجوجل',
    'admin.nav.notifications': 'الإشعارات',
    'admin.nav.support': 'مركز الدعم',
    'admin.nav.settings': 'الإعدادات',
    'admin.subtitle.seo': 'مساعد الظهور المحلي',
    'admin.subtitle.admin': 'نظرة استراتيجية',
    'admin.subtitle.insights': 'أداء المطعم الفعلي',
    'admin.subtitle.departments': 'تقسيم المطعم',
    'admin.subtitle.service-hours': 'جدولة فترات الخدمة',
    'admin.subtitle.categories': 'هيكلة القائمة',
    'admin.subtitle.items': 'إدارة المخزون',
    'admin.subtitle.ingredients': 'مكتبة العناصر',
    'admin.subtitle.campaigns': 'تنظيم الإيرادات',
    'admin.subtitle.signage': 'قائمة الشاشة الكبيرة',
    'admin.subtitle.qr': 'الحضور الرقمي',
    'admin.subtitle.theme': 'الهوية البصرية',
    'admin.subtitle.audit': 'تدقيق النظام',
    'admin.subtitle.team': 'حوكمة الوصول',
    'admin.subtitle.billing': 'نظرة مالية',
    'admin.subtitle.tax': 'الإدارة الضريبية ودفاتر القيمة المضافة',
    'admin.subtitle.settings': 'المنطق التشغيلي',
    'admin.subtitle.loyalty': 'تنظيم الاحتفاظ بالعملاء',
    'admin.subtitle.governance': 'إدارة متعددة المواقع',
    'admin.subtitle.api': 'منظومة المطورين',
    'admin.subtitle.sso': 'هوية المؤسسات',
    'admin.subtitle.notifications': 'منصة تنبيهات العمليات',
    'admin.subtitle.support': 'المساعدة والتواصل',
    'admin.subtitle.default': 'لوحة الإدارة',
    'admin.sign_out': 'تسجيل الخروج',
    'admin.user.administrator': 'المدير',
    'admin.user.global_admin': 'مدير عام',
    'admin.user.staff_access': 'وصول الموظفين',
    'admin.language.change': 'تغيير اللغة',
    'admin.command.search_placeholder': "ابحث في الأوامر... (مثلا 'المخزون' أو 'الأسعار')",
    'admin.command.esc': 'ESC للإغلاق',
    'admin.command.close': 'إغلاق لوحة الأوامر',
    'admin.command.categories.pages': 'الصفحات',
    'admin.command.categories.menu_items': 'عناصر القائمة',
    'admin.command.categories.categories': 'الفئات',
    'admin.command.categories.actions': 'الإجراءات',
    'admin.command.overview': 'نظرة عامة على مطعمك',
    'admin.command.business_data': 'بيانات ذكاء الأعمال',
    'admin.command.manage_items': 'إدارة العناصر والمخزون والتوفر',
    'admin.command.organize_taxonomy': 'تنظيم تصنيف القائمة',
    'admin.command.manage_staff': 'إدارة وصول الموظفين والدعوات',
    'admin.command.customize_appearance': 'تخصيص مظهر القائمة',
    'admin.command.create_qr': 'إنشاء وتصدير رموز QR',
    'admin.command.configure_signage': 'إعداد قائمة الشاشة الكبيرة',
    'admin.command.audit_log': 'سجل الشفافية والأمان',
    'admin.command.business_details': 'إعداد تفاصيل النشاط',
    'admin.command.export_menu': 'تصدير القائمة (CSV)',
    'admin.command.export_current': 'تصدير سريع للبيانات الحالية',
    'admin.command.view_category': 'عرض الفئة في المدير',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "عمليات المطبخ والشيف",
    'chef.kitchen_mode': "وضع المطبخ",
    'chef.exit_kitchen_mode': "الخروج من وضع المطبخ",
    'chef.start_preparing': "بدء التحضير",
    'chef.mark_ready': "جاهز للتقديم",
    'chef.finalize_order': "إنهاء الطلب",
    'chef.nothing_to_track': "لا توجد طلبات للمتابعة بعد",
    'chef.preparing_time': "قيد التحضير: {time}",
    'chef.waiting_time': "في الانتظار: {time}",
    'chef.ready_time': "جاهز: {time}",
    'chef.table': "طاولة",
    'chef.dine_in': "تناول بالداخل",
    'chef.delivery': "توصيل",
    'chef.pickup': "استلام",
    'waiter.title': "خدمة الصالة",
    'waiter.table_calls': "نداءات الطاولات",
    'waiter.ready_pickups': "أطباق جاهزة",
    'waiter.active_tickets': "الطلبات النشطة",
    'waiter.no_calls': "لا توجد نداءات طاولات معلقة",
    'waiter.nothing_pickup': "لا توجد أطباق جاهزة للاستلام بعد",
    'waiter.no_active_orders': "لا توجد طلبات نشطة",
    'waiter.mark_attended': "تمت الخدمة",
    'waiter.ready_for_pickup': "جاهز للتقديم",
    'waiter.call_request': "طلب المساعدة",
    'waiter.bill_request': "طلب الحساب / الفاتورة",
    'waiter.water_request': "طلب ماء",
    'status.active': "نشط",
    'status.pending': "قيد الانتظار",
    'status.preparing': "قيد التحضير",
    'status.ready': "جاهز",
    'status.completed': "مكتمل",
    'status.cancelled': "ملغى",
    'status.paid': "مدفوع",
    'status.unpaid': "غير مدفوع",
  },
  de: {
    'admin.nav.intelligence': 'Intelligence Studio',
    'admin.nav.insights': 'Betriebsanalysen',
    'admin.nav.departments': 'Abteilungen',
    'admin.nav.service_hours': 'Servicezeiten',
    'admin.nav.categories': 'Kategorien',
    'admin.nav.items': 'Menüartikel',
    'admin.nav.ingredients': 'Zutaten',
    'admin.nav.loyalty': 'Treueprogramm',
    'admin.nav.campaigns': 'Kampagnen',
    'admin.nav.signage': 'Digitale Anzeigen',
    'admin.nav.qr': 'QR-Code',
    'admin.nav.kitchen': 'Küchenbetrieb',
    'admin.nav.theme': 'Design-Engine',
    'admin.nav.governance': 'Unternehmensführung',
    'admin.nav.audit': 'Audit-Protokoll',
    'admin.nav.api': 'API & Webhooks',
    'admin.nav.sso': 'SSO & Sicherheit',
    'admin.nav.team': 'Team',
    'admin.nav.billing': 'Abrechnung',
    'admin.nav.seo': 'SEO & Google',
    'admin.nav.notifications': 'Benachrichtigungen',
    'admin.nav.support': 'Hilfe-Center',
    'admin.nav.settings': 'Einstellungen',
    'admin.subtitle.seo': 'Lokaler Sichtbarkeitsassistent',
    'admin.subtitle.admin': 'Strategische Übersicht',
    'admin.subtitle.insights': 'Echte Betriebsleistung',
    'admin.subtitle.departments': 'Bereichssegmentierung',
    'admin.subtitle.service-hours': 'Servicezeitplanung',
    'admin.subtitle.categories': 'Menüstruktur',
    'admin.subtitle.items': 'Bestandskontrolle',
    'admin.subtitle.ingredients': 'Zutatenbibliothek',
    'admin.subtitle.campaigns': 'Umsatzsteuerung',
    'admin.subtitle.signage': 'Großbildschirm-Menü',
    'admin.subtitle.qr': 'Digitale Präsenz',
    'admin.subtitle.theme': 'Visuelle Identität',
    'admin.subtitle.audit': 'Systemforensik',
    'admin.subtitle.team': 'Zugriffsverwaltung',
    'admin.subtitle.billing': 'Finanzübersicht',
    'admin.subtitle.settings': 'Betriebslogik',
    'admin.subtitle.loyalty': 'Kundenbindung',
    'admin.subtitle.governance': 'Standortübergreifende Steuerung',
    'admin.subtitle.api': 'Entwickler-Ökosystem',
    'admin.subtitle.sso': 'Unternehmensidentität',
    'admin.subtitle.notifications': 'Betriebs-Warnkonsole',
    'admin.subtitle.support': 'Hilfe und Support',
    'admin.subtitle.default': 'Verwaltungskonsole',
    'admin.sign_out': 'Abmelden',
    'admin.user.administrator': 'Administrator',
    'admin.user.global_admin': 'Globaler Admin',
    'admin.user.staff_access': 'Mitarbeiterzugang',
    'admin.language.change': 'Sprache ändern',
    'admin.command.search_placeholder': "Befehle suchen... (z.B. 'Bestand', 'Preise')",
    'admin.command.esc': 'ESC zum Schließen',
    'admin.command.close': 'Befehlspalette schließen',
    'admin.command.categories.pages': 'Seiten',
    'admin.command.categories.menu_items': 'Menüartikel',
    'admin.command.categories.categories': 'Kategorien',
    'admin.command.categories.actions': 'Aktionen',
    'admin.command.overview': 'Übersicht Ihres Betriebs',
    'admin.command.business_data': 'Geschäftsanalysedaten',
    'admin.command.manage_items': 'Artikel, Bestand und Verfügbarkeit verwalten',
    'admin.command.organize_taxonomy': 'Menütaxonomie organisieren',
    'admin.command.manage_staff': 'Personal und Einladungen verwalten',
    'admin.command.customize_appearance': 'Menüerscheinungsbild anpassen',
    'admin.command.create_qr': 'QR-Codes erstellen und exportieren',
    'admin.command.configure_signage': 'Großbildschirm-Menü konfigurieren',
    'admin.command.audit_log': 'Transparenz- und Sicherheitsprotokoll',
    'admin.command.business_details': 'Geschäftsdaten konfigurieren',
    'admin.command.export_menu': 'Menü exportieren (CSV)',
    'admin.command.export_current': 'Schnellexport aktueller Daten',
    'admin.command.view_category': 'Kategorie im Manager ansehen',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Küchenbetrieb",
    'chef.kitchen_mode': "Küchenmodus",
    'chef.exit_kitchen_mode': "Küchenmodus beenden",
    'chef.start_preparing': "Zubereitung starten",
    'chef.mark_ready': "Als fertig markieren",
    'chef.finalize_order': "Bestellung abschließen",
    'chef.nothing_to_track': "Noch nichts zu verfolgen",
    'chef.preparing_time': "In Zubereitung: {time}",
    'chef.waiting_time': "Wartend: {time}",
    'chef.ready_time': "Fertig: {time}",
    'chef.table': "Tisch",
    'chef.dine_in': "Vor Ort",
    'chef.delivery': "Lieferung",
    'chef.pickup': "Abholung",
    'waiter.title': "Saalservice",
    'waiter.table_calls': "Tischrufe",
    'waiter.ready_pickups': "Fertige Bestellungen",
    'waiter.active_tickets': "Aktive Tickets",
    'waiter.no_calls': "Keine offenen Tischrufe",
    'waiter.nothing_pickup': "Noch nichts abzuholen",
    'waiter.no_active_orders': "Keine aktiven Bestellungen",
    'waiter.mark_attended': "Als bedient markieren",
    'waiter.ready_for_pickup': "Bereit zum Servieren",
    'waiter.call_request': "Tisch-Assistent",
    'waiter.bill_request': "Rechnung angefordert",
    'waiter.water_request': "Wasser angefordert",
    'status.active': "Aktiv",
    'status.pending': "Ausstehend",
    'status.preparing': "In Zubereitung",
    'status.ready': "Fertig",
    'status.completed': "Abgeschlossen",
    'status.cancelled': "Storniert",
    'status.paid': "Bezahlt",
    'status.unpaid': "Unbezahlt",
  },
  it: {
    'admin.nav.intelligence': 'Studio Intelligence',
    'admin.nav.insights': 'Analisi del locale',
    'admin.nav.departments': 'Reparti',
    'admin.nav.service_hours': 'Orari di servizio',
    'admin.nav.categories': 'Categorie',
    'admin.nav.items': 'Voci di menu',
    'admin.nav.ingredients': 'Ingredienti',
    'admin.nav.loyalty': 'Programma fedeltà',
    'admin.nav.campaigns': 'Studio campagne',
    'admin.nav.signage': 'Schermi TV',
    'admin.nav.qr': 'Codice QR',
    'admin.nav.kitchen': 'Operazioni di cucina',
    'admin.nav.theme': 'Motore di tema',
    'admin.nav.governance': 'Governance',
    'admin.nav.audit': 'Registro audit',
    'admin.nav.api': 'API e webhook',
    'admin.nav.sso': 'SSO e sicurezza',
    'admin.nav.team': 'Team',
    'admin.nav.billing': 'Fatturazione',
    'admin.nav.seo': 'SEO e Google',
    'admin.nav.notifications': 'Notifiche',
    'admin.nav.support': 'Centro assistenza',
    'admin.nav.settings': 'Impostazioni',
    'admin.subtitle.seo': 'Assistente visibilità locale',
    'admin.subtitle.admin': 'Panoramica strategica',
    'admin.subtitle.insights': 'Prestazioni reali del locale',
    'admin.subtitle.departments': 'Segmentazione del locale',
    'admin.subtitle.service-hours': 'Pianificazione per fasce orarie',
    'admin.subtitle.categories': 'Architettura del menu',
    'admin.subtitle.items': 'Controllo inventario',
    'admin.subtitle.ingredients': 'Libreria ingredienti',
    'admin.subtitle.campaigns': 'Orchestrazione entrate',
    'admin.subtitle.signage': 'Menu su grande schermo',
    'admin.subtitle.qr': 'Presenza digitale',
    'admin.subtitle.theme': 'Identità visiva',
    'admin.subtitle.audit': 'Forense di sistema',
    'admin.subtitle.team': 'Governance degli accessi',
    'admin.subtitle.billing': 'Panoramica fiscale',
    'admin.subtitle.settings': 'Logica operativa',
    'admin.subtitle.loyalty': 'Orchestrazione fidelizzazione',
    'admin.subtitle.governance': 'Controllo multi-locale',
    'admin.subtitle.api': 'Ecosistema sviluppatori',
    'admin.subtitle.sso': 'Identità aziendale',
    'admin.subtitle.notifications': 'Console di avvisi operativi',
    'admin.subtitle.support': 'Assistenza e comunicazioni',
    'admin.subtitle.default': 'Console di gestione',
    'admin.sign_out': 'Disconnetti',
    'admin.user.administrator': 'Amministratore',
    'admin.user.global_admin': 'Admin globale',
    'admin.user.staff_access': 'Accesso staff',
    'admin.language.change': 'Cambia lingua',
    'admin.command.search_placeholder': "Cerca comandi... (es. 'inventario', 'prezzi')",
    'admin.command.esc': 'ESC per chiudere',
    'admin.command.close': 'Chiudi tavolozza comandi',
    'admin.command.categories.pages': 'Pagine',
    'admin.command.categories.menu_items': 'Voci di menu',
    'admin.command.categories.categories': 'Categorie',
    'admin.command.categories.actions': 'Azioni',
    'admin.command.overview': 'Panoramica del tuo locale',
    'admin.command.business_data': 'Dati di business intelligence',
    'admin.command.manage_items': 'Gestisci voci, stock e disponibilità',
    'admin.command.organize_taxonomy': 'Organizza la tassonomia del menu',
    'admin.command.manage_staff': 'Gestisci staff e inviti',
    'admin.command.customize_appearance': "Personalizza l'aspetto del menu",
    'admin.command.create_qr': 'Crea ed esporta codici QR',
    'admin.command.configure_signage': 'Configura il menu per grande schermo',
    'admin.command.audit_log': 'Registro trasparenza e sicurezza',
    'admin.command.business_details': 'Configura dettagli aziendali',
    'admin.command.export_menu': 'Esporta menu (CSV)',
    'admin.command.export_current': 'Esportazione rapida dei dati',
    'admin.command.view_category': 'Visualizza categoria nel gestore',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Operazioni Chef",
    'chef.kitchen_mode': "Modalità Cucina",
    'chef.exit_kitchen_mode': "Esci dalla modalità cucina",
    'chef.start_preparing': "Inizia preparazione",
    'chef.mark_ready': "Segna come pronto",
    'chef.finalize_order': "Completa l'ordine",
    'chef.nothing_to_track': "Nessun ordine da monitorare",
    'chef.preparing_time': "In preparazione: {time}",
    'chef.waiting_time': "In attesa: {time}",
    'chef.ready_time': "Pronto: {time}",
    'chef.table': "Tavolo",
    'chef.dine_in': "Al tavolo",
    'chef.delivery': "Consegna",
    'chef.pickup': "Asporto",
    'waiter.title': "Servizio di Sala",
    'waiter.table_calls': "Chiamate Tavoli",
    'waiter.ready_pickups': "Ordini Pronti",
    'waiter.active_tickets': "Ticket Attivi",
    'waiter.no_calls': "Nessuna chiamata dal tavolo in attesa",
    'waiter.nothing_pickup': "Nessun piatto pronto per il ritiro",
    'waiter.no_active_orders': "Nessun ordine attivo",
    'waiter.mark_attended': "Segna come servito",
    'waiter.ready_for_pickup': "Pronto per il servizio",
    'waiter.call_request': "Assistenza al Tavolo",
    'waiter.bill_request': "Conto Richiesto",
    'waiter.water_request': "Acqua Richiesta",
    'status.active': "Attivo",
    'status.pending': "In attesa",
    'status.preparing': "In preparazione",
    'status.ready': "Pronto",
    'status.completed': "Completato",
    'status.cancelled': "Annullato",
    'status.paid': "Pagato",
    'status.unpaid': "Non pagato",
  },
  pt: {
    'admin.nav.intelligence': 'Estúdio de Inteligência',
    'admin.nav.insights': 'Análise do Estabelecimento',
    'admin.nav.departments': 'Departamentos',
    'admin.nav.service_hours': 'Horários de Atendimento',
    'admin.nav.categories': 'Categorias',
    'admin.nav.items': 'Itens do Menu',
    'admin.nav.ingredients': 'Ingredientes',
    'admin.nav.loyalty': 'Programa de Fidelidade',
    'admin.nav.campaigns': 'Estúdio de Campanhas',
    'admin.nav.signage': 'Telas Digitais',
    'admin.nav.qr': 'Código QR',
    'admin.nav.kitchen': 'Operações de Cozinha',
    'admin.nav.theme': 'Motor de Temas',
    'admin.nav.governance': 'Governança',
    'admin.nav.audit': 'Registo de Auditoria',
    'admin.nav.api': 'API e Webhooks',
    'admin.nav.sso': 'SSO e Segurança',
    'admin.nav.team': 'Equipa',
    'admin.nav.billing': 'Faturação',
    'admin.nav.seo': 'SEO e Google',
    'admin.nav.notifications': 'Notificações',
    'admin.nav.support': 'Centro de Suporte',
    'admin.nav.settings': 'Definições',
    'admin.subtitle.seo': 'Assistente de visibilidade local',
    'admin.subtitle.admin': 'Visão geral estratégica',
    'admin.subtitle.insights': 'Desempenho real do espaço',
    'admin.subtitle.departments': 'Segmentação do espaço',
    'admin.subtitle.service-hours': 'Agendamento por turnos',
    'admin.subtitle.categories': 'Arquitetura do menu',
    'admin.subtitle.items': 'Controlo de inventário',
    'admin.subtitle.ingredients': 'Biblioteca de ativos',
    'admin.subtitle.campaigns': 'Orquestração de receitas',
    'admin.subtitle.signage': 'Menu em tela grande',
    'admin.subtitle.qr': 'Presença digital',
    'admin.subtitle.theme': 'Identidade visual',
    'admin.subtitle.audit': 'Análise forense do sistema',
    'admin.subtitle.team': 'Governança de acessos',
    'admin.subtitle.billing': 'Visão geral financeira',
    'admin.subtitle.settings': 'Lógica operacional',
    'admin.subtitle.loyalty': 'Orquestração de retenção',
    'admin.subtitle.governance': 'Controlo multi-espaços',
    'admin.subtitle.api': 'Ecossistema de programadores',
    'admin.subtitle.sso': 'Identidade corporativa',
    'admin.subtitle.notifications': 'Consola de alertas operacionais',
    'admin.subtitle.support': 'Ajuda e comunicações',
    'admin.subtitle.default': 'Consola de gestão',
    'admin.sign_out': 'Terminar sessão',
    'admin.user.administrator': 'Administrador',
    'admin.user.global_admin': 'Admin Global',
    'admin.user.staff_access': 'Acesso de Funcionário',
    'admin.language.change': 'Mudar idioma',
    'admin.command.search_placeholder': "Pesquisar comandos... (ex: 'inventário', 'preços')",
    'admin.command.esc': 'ESC para fechar',
    'admin.command.close': 'Fechar paleta de comandos',
    'admin.command.categories.pages': 'Páginas',
    'admin.command.categories.menu_items': 'Itens do menu',
    'admin.command.categories.categories': 'Categorias',
    'admin.command.categories.actions': 'Ações',
    'admin.command.overview': 'Visão geral do seu espaço',
    'admin.command.business_data': 'Dados de inteligência comercial',
    'admin.command.manage_items': 'Gerir itens, stock e disponibilidade',
    'admin.command.organize_taxonomy': 'Organizar taxonomia do menu',
    'admin.command.manage_staff': 'Gerir equipa e convites',
    'admin.command.customize_appearance': 'Personalizar aparência do menu',
    'admin.command.create_qr': 'Criar e exportar códigos QR',
    'admin.command.configure_signage': 'Configurar menu em tela grande',
    'admin.command.audit_log': 'Registo de segurança e transparência',
    'admin.command.business_details': 'Configurar dados do negócio',
    'admin.command.export_menu': 'Exportar menu (CSV)',
    'admin.command.export_current': 'Exportação rápida dos dados atuais',
    'admin.command.view_category': 'Ver categoria no gestor',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Operações do Chef",
    'chef.kitchen_mode': "Modo Cozinha",
    'chef.exit_kitchen_mode': "Sair do modo cozinha",
    'chef.start_preparing': "Iniciar preparo",
    'chef.mark_ready': "Marcar como pronto",
    'chef.finalize_order': "Finalizar pedido",
    'chef.nothing_to_track': "Nada para acompanhar ainda",
    'chef.preparing_time': "Preparando: {time}",
    'chef.waiting_time': "Aguardando: {time}",
    'chef.ready_time': "Pronto: {time}",
    'chef.table': "Mesa",
    'chef.dine_in': "No local",
    'chef.delivery': "Entrega",
    'chef.pickup': "Retirada",
    'waiter.title': "Serviço de Salão",
    'waiter.table_calls': "Chamadas de Mesa",
    'waiter.ready_pickups': "Pratos Prontos",
    'waiter.active_tickets': "Pedidos Ativos",
    'waiter.no_calls': "Nenhuma chamada de mesa pendente",
    'waiter.nothing_pickup': "Nada pronto para retirada ainda",
    'waiter.no_active_orders': "Nenhum pedido ativo",
    'waiter.mark_attended': "Marcar como atendido",
    'waiter.ready_for_pickup': "Pronto para servir",
    'waiter.call_request': "Ajuda na Mesa",
    'waiter.bill_request': "Conta Solicitada",
    'waiter.water_request': "Água Solicitada",
    'status.active': "Ativo",
    'status.pending': "Pendente",
    'status.preparing': "Em preparo",
    'status.ready': "Pronto",
    'status.completed': "Concluído",
    'status.cancelled': "Cancelado",
    'status.paid': "Pago",
    'status.unpaid': "Não pago",
  },
  ru: {
    'admin.nav.intelligence': 'Студия интеллекта',
    'admin.nav.insights': 'Аналитика заведения',
    'admin.nav.departments': 'Отделы',
    'admin.nav.service_hours': 'Часы работы',
    'admin.nav.categories': 'Категории',
    'admin.nav.items': 'Позиции меню',
    'admin.nav.ingredients': 'Ингредиенты',
    'admin.nav.loyalty': 'Программа лояльности',
    'admin.nav.campaigns': 'Студия кампаний',
    'admin.nav.signage': 'Экраны меню',
    'admin.nav.qr': 'QR-код',
    'admin.nav.kitchen': 'Кухня',
    'admin.nav.theme': 'Темы оформления',
    'admin.nav.governance': 'Управление сетью',
    'admin.nav.audit': 'Журнал аудита',
    'admin.nav.api': 'API и вебхуки',
    'admin.nav.sso': 'SSO и безопасность',
    'admin.nav.team': 'Команда',
    'admin.nav.billing': 'Оплата и счета',
    'admin.nav.seo': 'SEO и Google',
    'admin.nav.notifications': 'Уведомления',
    'admin.nav.support': 'Служба поддержки',
    'admin.nav.settings': 'Настройки',
    'admin.subtitle.seo': 'Помощник локальной видимости',
    'admin.subtitle.admin': 'Стратегический обзор',
    'admin.subtitle.insights': 'Реальная эффективность заведения',
    'admin.subtitle.departments': 'Сегментация заведения',
    'admin.subtitle.service-hours': 'График по сменам',
    'admin.subtitle.categories': 'Структура меню',
    'admin.subtitle.items': 'Учет и остатки',
    'admin.subtitle.ingredients': 'Библиотека компонентов',
    'admin.subtitle.campaigns': 'Управление выручкой',
    'admin.subtitle.signage': 'Меню на большом экране',
    'admin.subtitle.qr': 'Цифровое присутствие',
    'admin.subtitle.theme': 'Визуальный стиль',
    'admin.subtitle.audit': 'Системный аудит',
    'admin.subtitle.team': 'Управление доступом',
    'admin.subtitle.billing': 'Финансовый обзор',
    'admin.subtitle.settings': 'Операционная логика',
    'admin.subtitle.loyalty': 'Удержание клиентов',
    'admin.subtitle.governance': 'Управление несколькими точками',
    'admin.subtitle.api': 'Экосистема разработчиков',
    'admin.subtitle.sso': 'Корпоративная безопасность',
    'admin.subtitle.notifications': 'Панель операционных оповещений',
    'admin.subtitle.support': 'Справка и коммуникации',
    'admin.subtitle.default': 'Панель управления',
    'admin.sign_out': 'Выйти',
    'admin.user.administrator': 'Администратор',
    'admin.user.global_admin': 'Главный администратор',
    'admin.user.staff_access': 'Доступ персонала',
    'admin.language.change': 'Сменить язык',
    'admin.command.search_placeholder': "Поиск команд... (например, 'меню', 'цены')",
    'admin.command.esc': 'ESC для закрытия',
    'admin.command.close': 'Закрыть командную панель',
    'admin.command.categories.pages': 'Страницы',
    'admin.command.categories.menu_items': 'Позиции меню',
    'admin.command.categories.categories': 'Категории',
    'admin.command.categories.actions': 'Действия',
    'admin.command.overview': 'Обзор вашего заведения',
    'admin.command.business_data': 'Бизнес-аналитика',
    'admin.command.manage_items': 'Управление позициями, наличием и остатками',
    'admin.command.organize_taxonomy': 'Организация структуры меню',
    'admin.command.manage_staff': 'Управление персоналом и приглашениями',
    'admin.command.customize_appearance': 'Настройка внешнего вида меню',
    'admin.command.create_qr': 'Создание и экспорт QR-кодов',
    'admin.command.configure_signage': 'Настройка экрана меню',
    'admin.command.audit_log': 'Журнал безопасности и прозрачности',
    'admin.command.business_details': 'Настройка профиля бизнеса',
    'admin.command.export_menu': 'Экспорт меню (CSV)',
    'admin.command.export_current': 'Быстрый экспорт текущих данных',
    'admin.command.view_category': 'Открыть категорию в редакторе',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Работа шеф-повара",
    'chef.kitchen_mode': "Режим кухни",
    'chef.exit_kitchen_mode': "Выйти из режима кухни",
    'chef.start_preparing': "Начать приготовление",
    'chef.mark_ready': "Готово к выдаче",
    'chef.finalize_order': "Завершить заказ",
    'chef.nothing_to_track': "Пока нет заказов",
    'chef.preparing_time': "Готовится: {time}",
    'chef.waiting_time': "В ожидании: {time}",
    'chef.ready_time': "Готово: {time}",
    'chef.table': "Стол",
    'chef.dine_in': "В зале",
    'chef.delivery': "Доставка",
    'chef.pickup': "Самовывоз",
    'waiter.title': "Обслуживание зала",
    'waiter.table_calls': "Вызовы со столов",
    'waiter.ready_pickups': "Готовые блюда",
    'waiter.active_tickets': "Активные заказы",
    'waiter.no_calls': "Нет ожидающих вызовов",
    'waiter.nothing_pickup': "Пока нечего забирать",
    'waiter.no_active_orders': "Нет активных заказов",
    'waiter.mark_attended': "Отметить как выполненный",
    'waiter.ready_for_pickup': "Готово к подаче",
    'waiter.call_request': "Помощь за столом",
    'waiter.bill_request': "Запрошен счет",
    'waiter.water_request': "Запрошена вода",
    'status.active': "Активный",
    'status.pending': "В ожидании",
    'status.preparing': "Готовится",
    'status.ready': "Готово",
    'status.completed': "Завершено",
    'status.cancelled': "Отменено",
    'status.paid': "Оплачено",
    'status.unpaid': "Не оплачено",
  },
  zh: {
    'admin.nav.intelligence': '智能工作室',
    'admin.nav.insights': '门店分析',
    'admin.nav.departments': '部门设置',
    'admin.nav.service_hours': '营业时段',
    'admin.nav.categories': '品类管理',
    'admin.nav.items': '菜单商品',
    'admin.nav.ingredients': '原料食材',
    'admin.nav.loyalty': '会员积分',
    'admin.nav.campaigns': '营销活动',
    'admin.nav.signage': '电子标牌',
    'admin.nav.qr': '二维码',
    'admin.nav.kitchen': '后厨管理',
    'admin.nav.theme': '主题引擎',
    'admin.nav.governance': '多店管控',
    'admin.nav.audit': '审计日志',
    'admin.nav.api': 'API 与 Webhooks',
    'admin.nav.sso': 'SSO 与安全',
    'admin.nav.team': '团队成员',
    'admin.nav.billing': '账单财务',
    'admin.nav.seo': 'SEO 与 谷歌',
    'admin.nav.notifications': '通知中心',
    'admin.nav.support': '支持中心',
    'admin.nav.settings': '系统设置',
    'admin.subtitle.seo': '本地化搜索助手',
    'admin.subtitle.admin': '战略概览',
    'admin.subtitle.insights': '门店实时经营表现',
    'admin.subtitle.departments': '服务区域划分',
    'admin.subtitle.service-hours': '分时段经营配置',
    'admin.subtitle.categories': '菜单架构体系',
    'admin.subtitle.items': '商品库存管理',
    'admin.subtitle.ingredients': '配料与资产库',
    'admin.subtitle.campaigns': '营收增长方案',
    'admin.subtitle.signage': '大屏看板菜单',
    'admin.subtitle.qr': '数字扫码入口',
    'admin.subtitle.theme': '视觉品牌形象',
    'admin.subtitle.audit': '系统安全审计',
    'admin.subtitle.team': '权限与团队治理',
    'admin.subtitle.billing': '财务账单概览',
    'admin.subtitle.settings': '业务逻辑设置',
    'admin.subtitle.loyalty': '顾客留存计划',
    'admin.subtitle.governance': '多门店集中管理',
    'admin.subtitle.api': '开发者开放生态',
    'admin.subtitle.sso': '企业身份安全',
    'admin.subtitle.notifications': '运营预警控制台',
    'admin.subtitle.support': '帮助与客户支持',
    'admin.subtitle.default': '管理控制台',
    'admin.sign_out': '退出登录',
    'admin.user.administrator': '管理员',
    'admin.user.global_admin': '全局管理员',
    'admin.user.staff_access': '员工权限',
    'admin.language.change': '切换语言',
    'admin.command.search_placeholder': "搜索命令... (如 '库存', '价格')",
    'admin.command.esc': 'ESC 关闭',
    'admin.command.close': '关闭命令面板',
    'admin.command.categories.pages': '页面',
    'admin.command.categories.menu_items': '菜单商品',
    'admin.command.categories.categories': '品类',
    'admin.command.categories.actions': '快捷操作',
    'admin.command.overview': '门店经营总览',
    'admin.command.business_data': '商业智能数据',
    'admin.command.manage_items': '管理商品、库存与在售状态',
    'admin.command.organize_taxonomy': '组织菜单品类层级',
    'admin.command.manage_staff': '管理员工权限与邀请',
    'admin.command.customize_appearance': '自定义菜单界面外观',
    'admin.command.create_qr': '生成并导出二维码',
    'admin.command.configure_signage': '配置大屏电视菜单看板',
    'admin.command.audit_log': '透明与安全日志',
    'admin.command.business_details': '配置商业基本资料',
    'admin.command.export_menu': '导出菜单 (CSV)',
    'admin.command.export_current': '快速导出当前数据',
    'admin.command.view_category': '在管理器中查看品类',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "后厨运营",
    'chef.kitchen_mode': "后厨模式",
    'chef.exit_kitchen_mode': "退出后厨模式",
    'chef.start_preparing': "开始制作",
    'chef.mark_ready': "标记为完成",
    'chef.finalize_order': "完成订单",
    'chef.nothing_to_track': "暂无待处理订单",
    'chef.preparing_time': "制作中：{time}",
    'chef.waiting_time': "等待中：{time}",
    'chef.ready_time': "已就绪：{time}",
    'chef.table': "餐桌",
    'chef.dine_in': "堂食",
    'chef.delivery': "外卖配送",
    'chef.pickup': "自取",
    'waiter.title': "楼面服务",
    'waiter.table_calls': "呼叫服务",
    'waiter.ready_pickups': "出餐就绪",
    'waiter.active_tickets': "进行中订单",
    'waiter.no_calls': "暂无待处理服务呼叫",
    'waiter.nothing_pickup': "暂无出餐",
    'waiter.no_active_orders': "暂无活跃订单",
    'waiter.mark_attended': "标记为已服务",
    'waiter.ready_for_pickup': "可传菜",
    'waiter.call_request': "呼叫服务员",
    'waiter.bill_request': "请求结账",
    'waiter.water_request': "加水服务",
    'status.active': "进行中",
    'status.pending': "待处理",
    'status.preparing': "制作中",
    'status.ready': "已出餐",
    'status.completed': "已完成",
    'status.cancelled': "已取消",
    'status.paid': "已支付",
    'status.unpaid': "未支付",
  },
  ja: {
    'admin.nav.intelligence': 'インテリジェンススタジオ',
    'admin.nav.insights': '店舗アナリティクス',
    'admin.nav.departments': '部門設定',
    'admin.nav.service_hours': '営業時間帯',
    'admin.nav.categories': 'カテゴリー',
    'admin.nav.items': 'メニュー品目',
    'admin.nav.ingredients': '原材料',
    'admin.nav.loyalty': 'ロイヤルティプログラム',
    'admin.nav.campaigns': 'キャンペーン',
    'admin.nav.signage': 'デジタルサイネージ',
    'admin.nav.qr': 'QRコード',
    'admin.nav.kitchen': '厨房オペレーション',
    'admin.nav.theme': 'テーマエンジン',
    'admin.nav.governance': 'ガバナンス',
    'admin.nav.audit': '監査ログ',
    'admin.nav.api': 'APIとWebhook',
    'admin.nav.sso': 'SSOとセキュリティ',
    'admin.nav.team': 'チーム',
    'admin.nav.billing': '請求・お支払い',
    'admin.nav.seo': 'SEOとGoogle',
    'admin.nav.notifications': '通知',
    'admin.nav.support': 'サポートセンター',
    'admin.nav.settings': '設定',
    'admin.subtitle.seo': 'ローカル検索アシスタント',
    'admin.subtitle.admin': '戦略概要',
    'admin.subtitle.insights': '店舗のリアルタイム実績',
    'admin.subtitle.departments': 'フロア・部門の区分',
    'admin.subtitle.service-hours': '時間帯別サービス設定',
    'admin.subtitle.categories': 'メニュー構造',
    'admin.subtitle.items': '在庫・商品管理',
    'admin.subtitle.ingredients': 'アセットライブラリ',
    'admin.subtitle.campaigns': '売上最適化',
    'admin.subtitle.signage': '大画面メニューボード',
    'admin.subtitle.qr': 'デジタルプレゼンス',
    'admin.subtitle.theme': 'ビジュアルアイデンティティ',
    'admin.subtitle.audit': 'システム監査',
    'admin.subtitle.team': 'アクセス権限管理',
    'admin.subtitle.billing': '財務概要',
    'admin.subtitle.settings': '運用ロジック',
    'admin.subtitle.loyalty': 'リピート獲得プログラム',
    'admin.subtitle.governance': 'マルチ店舗統括',
    'admin.subtitle.api': '開発者エコシステム',
    'admin.subtitle.sso': 'エンタープライズID',
    'admin.subtitle.notifications': '運用アラートコンソール',
    'admin.subtitle.support': 'ヘルプ＆サポート',
    'admin.subtitle.default': '管理コンソール',
    'admin.sign_out': 'ログアウト',
    'admin.user.administrator': '管理者',
    'admin.user.global_admin': 'グローバル管理者',
    'admin.user.staff_access': 'スタッフアクセス',
    'admin.language.change': '言語を変更',
    'admin.command.search_placeholder': "コマンドを検索... (例: '在庫', '価格')",
    'admin.command.esc': 'ESCで閉じる',
    'admin.command.close': 'コマンドパレットを閉じる',
    'admin.command.categories.pages': 'ページ',
    'admin.command.categories.menu_items': 'メニュー品目',
    'admin.command.categories.categories': 'カテゴリー',
    'admin.command.categories.actions': 'アクション',
    'admin.command.overview': '店舗の全体概要',
    'admin.command.business_data': 'ビジネスインテリジェンスデータ',
    'admin.command.manage_items': '品目・在庫・提供状況の管理',
    'admin.command.organize_taxonomy': 'メニュー分類の整理',
    'admin.command.manage_staff': 'スタッフ権限と招待の管理',
    'admin.command.customize_appearance': 'メニュー表示のカスタマイズ',
    'admin.command.create_qr': 'QRコードの作成と出力',
    'admin.command.configure_signage': '大画面メニューボードの設定',
    'admin.command.audit_log': '透明性とセキュリティログ',
    'admin.command.business_details': '店舗基本情報の設定',
    'admin.command.export_menu': 'メニュー出力 (CSV)',
    'admin.command.export_current': '現在のデータを素早く出力',
    'admin.command.view_category': 'マネージャーでカテゴリーを開く',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "調理場オペレーション",
    'chef.kitchen_mode': "キッチンモード",
    'chef.exit_kitchen_mode': "キッチンモードを終了",
    'chef.start_preparing': "調理開始",
    'chef.mark_ready': "完成にする",
    'chef.finalize_order': "注文を完了",
    'chef.nothing_to_track': "まだ注文はありません",
    'chef.preparing_time': "調理中: {time}",
    'chef.waiting_time': "待機中: {time}",
    'chef.ready_time': "準備完了: {time}",
    'chef.table': "テーブル",
    'chef.dine_in': "店内飲食",
    'chef.delivery': "配達",
    'chef.pickup': "テイクアウト",
    'waiter.title': "フロアサービス",
    'waiter.table_calls': "テーブル呼び出し",
    'waiter.ready_pickups': "配膳待ち",
    'waiter.active_tickets': "進行中の注文",
    'waiter.no_calls': "待機中の呼び出しはありません",
    'waiter.nothing_pickup': "配膳待ちの料理はありません",
    'waiter.no_active_orders': "アクティブな注文はありません",
    'waiter.mark_attended': "対応済みにする",
    'waiter.ready_for_pickup': "配膳可能",
    'waiter.call_request': "スタッフ呼び出し",
    'waiter.bill_request': "お会計のお願い",
    'waiter.water_request': "お水のお願い",
    'status.active': "アクティブ",
    'status.pending': "保留中",
    'status.preparing': "調理中",
    'status.ready': "完了",
    'status.completed': "完了済み",
    'status.cancelled': "キャンセル",
    'status.paid': "支払済み",
    'status.unpaid': "未払い",
  },
  nl: {
    'admin.nav.intelligence': 'Intelligence Studio',
    'admin.nav.insights': 'Locatie-inzichten',
    'admin.nav.departments': 'Afdelingen',
    'admin.nav.service_hours': 'Servicetijden',
    'admin.nav.categories': 'Categorieën',
    'admin.nav.items': 'Menu-items',
    'admin.nav.ingredients': 'Ingrediënten',
    'admin.nav.loyalty': 'Loyaliteitsprogramma',
    'admin.nav.campaigns': 'Campagne Studio',
    'admin.nav.signage': 'Schermen',
    'admin.nav.qr': 'QR-code',
    'admin.nav.kitchen': 'Keukenoperaties',
    'admin.nav.theme': 'Thema-engine',
    'admin.nav.governance': 'Governance',
    'admin.nav.audit': 'Auditlog',
    'admin.nav.api': 'API & Webhooks',
    'admin.nav.sso': 'SSO & Beveiliging',
    'admin.nav.team': 'Team',
    'admin.nav.billing': 'Facturatie',
    'admin.nav.seo': 'SEO & Google',
    'admin.nav.notifications': 'Meldingen',
    'admin.nav.support': 'Ondersteuning',
    'admin.nav.settings': 'Instellingen',
    'admin.subtitle.seo': 'Lokale zichtbaarheidsassistent',
    'admin.subtitle.admin': 'Strategisch overzicht',
    'admin.subtitle.insights': 'Echte locatieprestaties',
    'admin.subtitle.departments': 'Locatiesegmentatie',
    'admin.subtitle.service-hours': 'Planning per dagdeel',
    'admin.subtitle.categories': 'Menustructuur',
    'admin.subtitle.items': 'Voorraadbeheer',
    'admin.subtitle.ingredients': 'Ingrediëntenbibliotheek',
    'admin.subtitle.campaigns': 'Omzetoptimalisatie',
    'admin.subtitle.signage': 'Groot scherm menu',
    'admin.subtitle.qr': 'Digitale aanwezigheid',
    'admin.subtitle.theme': 'Visuele identiteit',
    'admin.subtitle.audit': 'Systeemforensica',
    'admin.subtitle.team': 'Toegangsbeheer',
    'admin.subtitle.billing': 'Financieel overzicht',
    'admin.subtitle.settings': 'Operationele logica',
    'admin.subtitle.loyalty': 'Klantenbinding',
    'admin.subtitle.governance': 'Multi-locatiebeheer',
    'admin.subtitle.api': 'Ontwikkelaars-ecosysteem',
    'admin.subtitle.sso': 'Ondernemingsidentiteit',
    'admin.subtitle.notifications': 'Operationele waarschuwingen',
    'admin.subtitle.support': 'Hulp & communicatie',
    'admin.subtitle.default': 'Beheerconsole',
    'admin.sign_out': 'Uitloggen',
    'admin.user.administrator': 'Beheerder',
    'admin.user.global_admin': 'Algemeen beheerder',
    'admin.user.staff_access': 'Personeelstoegang',
    'admin.language.change': 'Taal wijzigen',
    'admin.command.search_placeholder': "Opdrachten zoeken... (bijv. 'voorraad', 'prijzen')",
    'admin.command.esc': 'ESC om te sluiten',
    'admin.command.close': 'Opdrachtenpaneel sluiten',
    'admin.command.categories.pages': "Pagina's",
    'admin.command.categories.menu_items': 'Menu-items',
    'admin.command.categories.categories': 'Categorieën',
    'admin.command.categories.actions': 'Acties',
    'admin.command.overview': 'Overzicht van uw locatie',
    'admin.command.business_data': 'Zakelijke analysegegevens',
    'admin.command.manage_items': 'Beheer items, voorraad en beschikbaarheid',
    'admin.command.organize_taxonomy': 'Menustructuur organiseren',
    'admin.command.manage_staff': 'Personeel en uitnodigingen beheren',
    'admin.command.customize_appearance': 'Menu-uiterlijk aanpassen',
    'admin.command.create_qr': 'QR-codes maken en exporteren',
    'admin.command.configure_signage': 'Groot scherm menu configureren',
    'admin.command.audit_log': 'Transparantie- en beveiligingslog',
    'admin.command.business_details': 'Bedrijfsgegevens configureren',
    'admin.command.export_menu': 'Menu exporteren (CSV)',
    'admin.command.export_current': 'Snelle export van huidige gegevens',
    'admin.command.view_category': 'Categorie bekijken in beheerder',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Chef Operaties",
    'chef.kitchen_mode': "Keukenmodus",
    'chef.exit_kitchen_mode': "Keukenmodus verlaten",
    'chef.start_preparing': "Start bereiding",
    'chef.mark_ready': "Markeer als gereed",
    'chef.finalize_order': "Bestelling afronden",
    'chef.nothing_to_track': "Nog niets om te volgen",
    'chef.preparing_time': "In bereiding: {time}",
    'chef.waiting_time': "Wachtend: {time}",
    'chef.ready_time': "Gereed: {time}",
    'chef.table': "Tafel",
    'chef.dine_in': "Ter plaatse",
    'chef.delivery': "Bezorging",
    'chef.pickup': "Afhalen",
    'waiter.title': "Zaalbediening",
    'waiter.table_calls': "Tafeloproepen",
    'waiter.ready_pickups': "Klaar voor Serveren",
    'waiter.active_tickets': "Actieve Tickets",
    'waiter.no_calls': "Geen openstaande tafeloproepen",
    'waiter.nothing_pickup': "Nog niets om op te halen",
    'waiter.no_active_orders': "Geen actieve bestellingen",
    'waiter.mark_attended': "Markeer als afgehandeld",
    'waiter.ready_for_pickup': "Klaar om uit te serveren",
    'waiter.call_request': "Tafelassistentie",
    'waiter.bill_request': "Rekening gevraagd",
    'waiter.water_request': "Water gevraagd",
    'status.active': "Actief",
    'status.pending': "In behandeling",
    'status.preparing': "In bereiding",
    'status.ready': "Gereed",
    'status.completed': "Afgerond",
    'status.cancelled': "Geannuleerd",
    'status.paid': "Betaald",
    'status.unpaid': "Niet betaald",
  },
  tr: {
    'admin.nav.intelligence': 'Zeka Stüdyosu',
    'admin.nav.insights': 'Mekan Analitiği',
    'admin.nav.departments': 'Departmanlar',
    'admin.nav.service_hours': 'Servis Saatleri',
    'admin.nav.categories': 'Kategoriler',
    'admin.nav.items': 'Menü Öğeleri',
    'admin.nav.ingredients': 'Malzemeler',
    'admin.nav.loyalty': 'Sadakat Programı',
    'admin.nav.campaigns': 'Kampanya Stüdyosu',
    'admin.nav.signage': 'Ekranlar',
    'admin.nav.qr': 'QR Kodu',
    'admin.nav.kitchen': 'Mutfak Operasyonları',
    'admin.nav.theme': 'Tema Motoru',
    'admin.nav.governance': 'Yönetişim',
    'admin.nav.audit': 'Denetim Günlüğü',
    'admin.nav.api': 'API ve Webhooklar',
    'admin.nav.sso': 'SSO ve Güvenlik',
    'admin.nav.team': 'Ekip',
    'admin.nav.billing': 'Faturalandırma',
    'admin.nav.seo': 'SEO ve Google',
    'admin.nav.notifications': 'Bildirimler',
    'admin.nav.support': 'Destek Merkezi',
    'admin.nav.settings': 'Ayarlar',
    'admin.subtitle.seo': 'Yerel görünürlük asistanı',
    'admin.subtitle.admin': 'Stratejik genel bakış',
    'admin.subtitle.insights': 'Gerçek mekan performansı',
    'admin.subtitle.departments': 'Mekan bölümleme',
    'admin.subtitle.service-hours': 'Zaman dilimli planlama',
    'admin.subtitle.categories': 'Menü mimarisi',
    'admin.subtitle.items': 'Envanter kontrolü',
    'admin.subtitle.ingredients': 'Varlık kütüphanesi',
    'admin.subtitle.campaigns': 'Gelir yönetimi',
    'admin.subtitle.signage': 'Büyük ekran menü',
    'admin.subtitle.qr': 'Dijital varlık',
    'admin.subtitle.theme': 'Görsel kimlik',
    'admin.subtitle.audit': 'Sistem güvenliği',
    'admin.subtitle.team': 'Erişim yönetimi',
    'admin.subtitle.billing': 'Mali genel bakış',
    'admin.subtitle.settings': 'Operasyonel mantık',
    'admin.subtitle.loyalty': 'Müşteri bağlılığı',
    'admin.subtitle.governance': 'Çoklu mekan kontrolü',
    'admin.subtitle.api': 'Geliştirici ekosistemi',
    'admin.subtitle.sso': 'Kurumsal kimlik',
    'admin.subtitle.notifications': 'Operasyonel uyarı konsolu',
    'admin.subtitle.support': 'Yardım ve iletişim',
    'admin.subtitle.default': 'Yönetim konsolu',
    'admin.sign_out': 'Çıkış Yap',
    'admin.user.administrator': 'Yönetici',
    'admin.user.global_admin': 'Genel Yönetici',
    'admin.user.staff_access': 'Personel Erişimi',
    'admin.language.change': 'Dili Değiştir',
    'admin.command.search_placeholder': "Komutları ara... (örn. 'envanter', 'fiyatlar')",
    'admin.command.esc': 'Kapatmak için ESC',
    'admin.command.close': 'Komut paletini kapat',
    'admin.command.categories.pages': 'Sayfalar',
    'admin.command.categories.menu_items': 'Menü Öğeleri',
    'admin.command.categories.categories': 'Kategoriler',
    'admin.command.categories.actions': 'Eylemler',
    'admin.command.overview': 'Mekanınızın genel görünümü',
    'admin.command.business_data': 'İş zekası verileri',
    'admin.command.manage_items': 'Öğeleri, stoğu ve uygunluğu yönetin',
    'admin.command.organize_taxonomy': 'Menü kategorilerini düzenleyin',
    'admin.command.manage_staff': 'Personel erişimini ve davetleri yönetin',
    'admin.command.customize_appearance': 'Menü görünümünü özelleştirin',
    'admin.command.create_qr': 'QR kodları oluşturun ve dışa aktarın',
    'admin.command.configure_signage': 'Büyük ekran menüsünü yapılandırın',
    'admin.command.audit_log': 'Şeffaflık ve güvenlik günlüğü',
    'admin.command.business_details': 'İşletme ayrıntılarını yapılandırın',
    'admin.command.export_menu': 'Menüyü dışa aktar (CSV)',
    'admin.command.export_current': 'Geçerli verileri hızlı dışa aktar',
    'admin.command.view_category': 'Kategoriyi yöneticide görüntüle',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Şef Operasyonları",
    'chef.kitchen_mode': "Mutfak Modu",
    'chef.exit_kitchen_mode': "Mutfak modundan çık",
    'chef.start_preparing': "Hazırlamaya Başla",
    'chef.mark_ready': "Hazır Olarak İşaretle",
    'chef.finalize_order': "Siparişi Tamamla",
    'chef.nothing_to_track': "Henüz takip edilecek bir şey yok",
    'chef.preparing_time': "Hazırlanıyor: {time}",
    'chef.waiting_time': "Bekliyor: {time}",
    'chef.ready_time': "Hazır: {time}",
    'chef.table': "Masa",
    'chef.dine_in': "Restoranda",
    'chef.delivery': "Teslimat",
    'chef.pickup': "Gel-Al",
    'waiter.title': "Salon Servisi",
    'waiter.table_calls': "Masa Çağrıları",
    'waiter.ready_pickups': "Servise Hazır",
    'waiter.active_tickets': "Aktif Siparişler",
    'waiter.no_calls': "Bekleyen masa çağrısı yok",
    'waiter.nothing_pickup': "Henüz alınacak bir şey yok",
    'waiter.no_active_orders': "Aktif sipariş yok",
    'waiter.mark_attended': "İlgilenildi Olarak İşaretle",
    'waiter.ready_for_pickup': "Servise hazır",
    'waiter.call_request': "Masa Yardımı",
    'waiter.bill_request': "Hesap İstendi",
    'waiter.water_request': "Su İstendi",
    'status.active': "Aktif",
    'status.pending': "Beklemede",
    'status.preparing': "Hazırlanıyor",
    'status.ready': "Hazır",
    'status.completed': "Tamamlandı",
    'status.cancelled': "İptal edildi",
    'status.paid': "Ödendi",
    'status.unpaid': "Ödenmedi",
  },
};

export const translate = (language: Language, key: string, fallback: string): string => {
  const match = guestMenuTranslations[language]?.[key] || translations[language]?.[key];
  if (match) return match;
  if (language === 'en') return guestMenuTranslations['en']?.[key] || translations['en']?.[key] || fallback;
  return guestMenuTranslations['en']?.[key] || translations['en']?.[key] || fallback;
};

type AdminPhraseDictionary = Record<string, Record<string, string>>;

export const adminPhraseTranslations: AdminPhraseDictionary = {
  es: {
    'Save': 'Guardar',
    'Saved': 'Guardado',
    'Saving...': 'Guardando...',
    'Cancel': 'Cancelar',
    'Close': 'Cerrar',
    'Create': 'Crear',
    'Add': 'Añadir',
    'Edit': 'Editar',
    'Delete': 'Eliminar',
    'Remove': 'Quitar',
    'Update': 'Actualizar',
    'Refresh': 'Actualizar',
    'Search': 'Buscar',
    'Filter': 'Filtrar',
    'Export': 'Exportar',
    'Import': 'Importar',
    'Download': 'Descargar',
    'Upload': 'Subir',
    'Copy': 'Copiar',
    'Copied': 'Copiado',
    'Preview': 'Vista previa',
    'Open': 'Abrir',
    'View': 'Ver',
    'Manage': 'Gestionar',
    'Configure': 'Configurar',
    'Enable': 'Activar',
    'Disable': 'Desactivar',
    'Enabled': 'Activado',
    'Disabled': 'Desactivado',
    'Active': 'Activo',
    'Inactive': 'Inactivo',
    'Available': 'Disponible',
    'Unavailable': 'No disponible',
    'Visible': 'Visible',
    'Hidden': 'Oculto',
    'Required': 'Obligatorio',
    'Optional': 'Opcional',
    'Loading...': 'Cargando...',
    'No data': 'Sin datos',
    'No results found': 'No se encontraron resultados',
    'Name': 'Nombre',
    'Description': 'Descripción',
    'Price': 'Precio',
    'Status': 'Estado',
    'Actions': 'Acciones',
    'Role': 'Rol',
    'Email': 'Correo',
    'Phone': 'Teléfono',
    'Address': 'Dirección',
    'Currency': 'Moneda',
    'Language': 'Idioma',
    'Theme': 'Tema',
    'General': 'General',
    'Settings': 'Configuración',
    'Categories': 'Categorías',
    'Items': 'Artículos',
    'Menu Items': 'Artículos del menú',
    'Save Settings': 'Guardar configuración',
    'Save Changes': 'Guardar cambios',
    'Total Revenue': 'Ingresos totales',
    'Total Orders': 'Pedidos totales',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Operaciones del Chef",
    'chef.kitchen_mode': "Modo Cocina",
    'chef.exit_kitchen_mode': "Salir del modo cocina",
    'chef.start_preparing': "Comenzar preparación",
    'chef.mark_ready': "Marcar como listo",
    'chef.finalize_order': "Finalizar pedido",
    'chef.nothing_to_track': "Nada que rastrear aún",
    'chef.preparing_time': "Preparando: {time}",
    'chef.waiting_time': "En espera: {time}",
    'chef.ready_time': "Listo: {time}",
    'chef.table': "Mesa",
    'chef.dine_in': "En sala",
    'chef.delivery': "Entrega",
    'chef.pickup': "Para llevar",
    'waiter.title': "Servicio de Sala",
    'waiter.table_calls': "Llamadas de Mesa",
    'waiter.ready_pickups': "Pedidos Listos",
    'waiter.active_tickets': "Tickets Activos",
    'waiter.no_calls': "Sin llamadas de mesa pendientes",
    'waiter.nothing_pickup': "Nada listo para recoger aún",
    'waiter.no_active_orders': "Sin pedidos activos",
    'waiter.mark_attended': "Marcar como atendido",
    'waiter.ready_for_pickup': "Listo para servir",
    'waiter.call_request': "Asistencia en Mesa",
    'waiter.bill_request': "Cuenta Solicitada",
    'waiter.water_request': "Agua Solicitada",
    'status.active': "Activo",
    'status.pending': "Pendiente",
    'status.preparing': "En preparación",
    'status.ready': "Listo",
    'status.completed': "Completado",
    'status.cancelled': "Cancelado",
    'status.paid': "Pagado",
    'status.unpaid': "No pagado",
  },
  fr: {
    'Save': 'Enregistrer',
    'Saved': 'Enregistré',
    'Saving...': 'Enregistrement...',
    'Cancel': 'Annuler',
    'Close': 'Fermer',
    'Create': 'Créer',
    'Add': 'Ajouter',
    'Edit': 'Modifier',
    'Delete': 'Supprimer',
    'Remove': 'Retirer',
    'Update': 'Mettre à jour',
    'Refresh': 'Actualiser',
    'Search': 'Rechercher',
    'Filter': 'Filtrer',
    'Export': 'Exporter',
    'Import': 'Importer',
    'Download': 'Télécharger',
    'Upload': 'Importer',
    'Copy': 'Copier',
    'Copied': 'Copié',
    'Preview': 'Aperçu',
    'Open': 'Ouvrir',
    'View': 'Voir',
    'Manage': 'Gérer',
    'Configure': 'Configurer',
    'Enable': 'Activer',
    'Disable': 'Désactiver',
    'Enabled': 'Activé',
    'Disabled': 'Désactivé',
    'Active': 'Actif',
    'Inactive': 'Inactif',
    'Available': 'Disponible',
    'Unavailable': 'Indisponible',
    'Visible': 'Visible',
    'Hidden': 'Masqué',
    'Required': 'Obligatoire',
    'Optional': 'Facultatif',
    'Loading...': 'Chargement...',
    'No data': 'Aucune donnée',
    'No results found': 'Aucun résultat trouvé',
    'Name': 'Nom',
    'Description': 'Description',
    'Price': 'Prix',
    'Status': 'Statut',
    'Actions': 'Actions',
    'Role': 'Rôle',
    'Email': 'E-mail',
    'Phone': 'Téléphone',
    'Address': 'Adresse',
    'Currency': 'Devise',
    'Language': 'Langue',
    'Theme': 'Thème',
    'General': 'Général',
    'Settings': 'Paramètres',
    'Categories': 'Catégories',
    'Items': 'Articles',
    'Menu Items': 'Articles du menu',
    'Save Settings': 'Enregistrer les paramètres',
    'Save Changes': 'Enregistrer les modifications',
    'Total Revenue': 'Revenus totaux',
    'Total Orders': 'Commandes totales',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Opérations Chef",
    'chef.kitchen_mode': "Mode Cuisine",
    'chef.exit_kitchen_mode': "Quitter le mode cuisine",
    'chef.start_preparing': "Commencer la préparation",
    'chef.mark_ready': "Prêt à servir",
    'chef.finalize_order': "Finaliser la commande",
    'chef.nothing_to_track': "Aucune commande pour le moment",
    'chef.preparing_time': "En préparation : {time}",
    'chef.waiting_time': "En attente : {time}",
    'chef.ready_time': "Prêt : {time}",
    'chef.table': "Table",
    'chef.dine_in': "Sur place",
    'chef.delivery': "Livraison",
    'chef.pickup': "À emporter",
    'waiter.title': "Service en Salle",
    'waiter.table_calls': "Appels Tables",
    'waiter.ready_pickups': "Plats Prêts",
    'waiter.active_tickets': "Commandes En Cours",
    'waiter.no_calls': "Aucun appel de table en attente",
    'waiter.nothing_pickup': "Rien à récupérer pour le moment",
    'waiter.no_active_orders': "Aucune commande active",
    'waiter.mark_attended': "Marquer comme traité",
    'waiter.ready_for_pickup': "Prêt à servir",
    'waiter.call_request': "Demande de Serveur",
    'waiter.bill_request': "L'addition Demandée",
    'waiter.water_request': "Bouteille d'eau Demandée",
    'status.active': "Actif",
    'status.pending': "En attente",
    'status.preparing': "En préparation",
    'status.ready': "Prêt",
    'status.completed': "Terminé",
    'status.cancelled': "Annulé",
    'status.paid': "Payé",
    'status.unpaid': "Non payé",
  },
  ar: {
    'Save': 'حفظ',
    'Saved': 'تم الحفظ',
    'Saving...': 'جارٍ الحفظ...',
    'Cancel': 'إلغاء',
    'Close': 'إغلاق',
    'Create': 'إنشاء',
    'Add': 'إضافة',
    'Edit': 'تعديل',
    'Delete': 'حذف',
    'Remove': 'إزالة',
    'Update': 'تحديث',
    'Refresh': 'تحديث',
    'Search': 'بحث',
    'Filter': 'تصفية',
    'Export': 'تصدير',
    'Import': 'استيراد',
    'Download': 'تنزيل',
    'Upload': 'رفع',
    'Copy': 'نسخ',
    'Copied': 'تم النسخ',
    'Preview': 'معاينة',
    'Open': 'فتح',
    'View': 'عرض',
    'Manage': 'إدارة',
    'Configure': 'إعداد',
    'Enable': 'تفعيل',
    'Disable': 'تعطيل',
    'Enabled': 'مفعل',
    'Disabled': 'معطل',
    'Active': 'نشط',
    'Inactive': 'غير نشط',
    'Available': 'متوفر',
    'Unavailable': 'غير متوفر',
    'Visible': 'ظاهر',
    'Hidden': 'مخفي',
    'Required': 'مطلوب',
    'Optional': 'اختياري',
    'Loading...': 'جارٍ التحميل...',
    'No data': 'لا توجد بيانات',
    'No results found': 'لم يتم العثور على نتائج',
    'Name': 'الاسم',
    'Description': 'الوصف',
    'Price': 'السعر',
    'Status': 'الحالة',
    'Actions': 'الإجراءات',
    'Role': 'الدور',
    'Email': 'البريد الإلكتروني',
    'Phone': 'الهاتف',
    'Address': 'العنوان',
    'Currency': 'العملة',
    'Language': 'اللغة',
    'Theme': 'التصميم',
    'General': 'عام',
    'Settings': 'الإعدادات',
    'Categories': 'الفئات',
    'Items': 'العناصر',
    'Menu Items': 'عناصر القائمة',
    'Save Settings': 'حفظ الإعدادات',
    'Save Changes': 'حفظ التغييرات',
    'Total Revenue': 'إجمالي الإيرادات',
    'Total Orders': 'إجمالي الطلبات',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "عمليات المطبخ والشيف",
    'chef.kitchen_mode': "وضع المطبخ",
    'chef.exit_kitchen_mode': "الخروج من وضع المطبخ",
    'chef.start_preparing': "بدء التحضير",
    'chef.mark_ready': "جاهز للتقديم",
    'chef.finalize_order': "إنهاء الطلب",
    'chef.nothing_to_track': "لا توجد طلبات للمتابعة بعد",
    'chef.preparing_time': "قيد التحضير: {time}",
    'chef.waiting_time': "في الانتظار: {time}",
    'chef.ready_time': "جاهز: {time}",
    'chef.table': "طاولة",
    'chef.dine_in': "تناول بالداخل",
    'chef.delivery': "توصيل",
    'chef.pickup': "استلام",
    'waiter.title': "خدمة الصالة",
    'waiter.table_calls': "نداءات الطاولات",
    'waiter.ready_pickups': "أطباق جاهزة",
    'waiter.active_tickets': "الطلبات النشطة",
    'waiter.no_calls': "لا توجد نداءات طاولات معلقة",
    'waiter.nothing_pickup': "لا توجد أطباق جاهزة للاستلام بعد",
    'waiter.no_active_orders': "لا توجد طلبات نشطة",
    'waiter.mark_attended': "تمت الخدمة",
    'waiter.ready_for_pickup': "جاهز للتقديم",
    'waiter.call_request': "طلب المساعدة",
    'waiter.bill_request': "طلب الحساب / الفاتورة",
    'waiter.water_request': "طلب ماء",
    'status.active': "نشط",
    'status.pending': "قيد الانتظار",
    'status.preparing': "قيد التحضير",
    'status.ready': "جاهز",
    'status.completed': "مكتمل",
    'status.cancelled': "ملغى",
    'status.paid': "مدفوع",
    'status.unpaid': "غير مدفوع",
  },
  de: {
    'Save': 'Speichern',
    'Saved': 'Gespeichert',
    'Saving...': 'Speichern...',
    'Cancel': 'Abbrechen',
    'Close': 'Schließen',
    'Create': 'Erstellen',
    'Add': 'Hinzufügen',
    'Edit': 'Bearbeiten',
    'Delete': 'Löschen',
    'Remove': 'Entfernen',
    'Update': 'Aktualisieren',
    'Refresh': 'Aktualisieren',
    'Search': 'Suchen',
    'Filter': 'Filtern',
    'Export': 'Exportieren',
    'Import': 'Importieren',
    'Download': 'Herunterladen',
    'Upload': 'Hochladen',
    'Copy': 'Kopieren',
    'Copied': 'Kopiert',
    'Preview': 'Vorschau',
    'Open': 'Öffnen',
    'View': 'Ansehen',
    'Manage': 'Verwalten',
    'Configure': 'Konfigurieren',
    'Enable': 'Aktivieren',
    'Disable': 'Deaktivieren',
    'Enabled': 'Aktiviert',
    'Disabled': 'Deaktiviert',
    'Active': 'Aktiv',
    'Inactive': 'Inaktiv',
    'Available': 'Verfügbar',
    'Unavailable': 'Nicht verfügbar',
    'Visible': 'Sichtbar',
    'Hidden': 'Ausgeblendet',
    'Required': 'Erforderlich',
    'Optional': 'Optional',
    'Loading...': 'Laden...',
    'No data': 'Keine Daten',
    'No results found': 'Keine Ergebnisse gefunden',
    'Name': 'Name',
    'Description': 'Beschreibung',
    'Price': 'Preis',
    'Status': 'Status',
    'Actions': 'Aktionen',
    'Role': 'Rolle',
    'Email': 'E-Mail',
    'Phone': 'Telefon',
    'Address': 'Adresse',
    'Currency': 'Währung',
    'Language': 'Sprache',
    'Theme': 'Design',
    'General': 'Allgemein',
    'Settings': 'Einstellungen',
    'Categories': 'Kategorien',
    'Items': 'Artikel',
    'Menu Items': 'Menüartikel',
    'Save Settings': 'Einstellungen speichern',
    'Save Changes': 'Änderungen speichern',
    'Total Revenue': 'Gesamtumsatz',
    'Total Orders': 'Gesamte Bestellungen',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Küchenbetrieb",
    'chef.kitchen_mode': "Küchenmodus",
    'chef.exit_kitchen_mode': "Küchenmodus beenden",
    'chef.start_preparing': "Zubereitung starten",
    'chef.mark_ready': "Als fertig markieren",
    'chef.finalize_order': "Bestellung abschließen",
    'chef.nothing_to_track': "Noch nichts zu verfolgen",
    'chef.preparing_time': "In Zubereitung: {time}",
    'chef.waiting_time': "Wartend: {time}",
    'chef.ready_time': "Fertig: {time}",
    'chef.table': "Tisch",
    'chef.dine_in': "Vor Ort",
    'chef.delivery': "Lieferung",
    'chef.pickup': "Abholung",
    'waiter.title': "Saalservice",
    'waiter.table_calls': "Tischrufe",
    'waiter.ready_pickups': "Fertige Bestellungen",
    'waiter.active_tickets': "Aktive Tickets",
    'waiter.no_calls': "Keine offenen Tischrufe",
    'waiter.nothing_pickup': "Noch nichts abzuholen",
    'waiter.no_active_orders': "Keine aktiven Bestellungen",
    'waiter.mark_attended': "Als bedient markieren",
    'waiter.ready_for_pickup': "Bereit zum Servieren",
    'waiter.call_request': "Tisch-Assistent",
    'waiter.bill_request': "Rechnung angefordert",
    'waiter.water_request': "Wasser angefordert",
    'status.active': "Aktiv",
    'status.pending': "Ausstehend",
    'status.preparing': "In Zubereitung",
    'status.ready': "Fertig",
    'status.completed': "Abgeschlossen",
    'status.cancelled': "Storniert",
    'status.paid': "Bezahlt",
    'status.unpaid': "Unbezahlt",
  },
  it: {
    'Save': 'Salva',
    'Saved': 'Salvato',
    'Saving...': 'Salvataggio...',
    'Cancel': 'Annulla',
    'Close': 'Chiudi',
    'Create': 'Crea',
    'Add': 'Aggiungi',
    'Edit': 'Modifica',
    'Delete': 'Elimina',
    'Remove': 'Rimuovi',
    'Update': 'Aggiorna',
    'Refresh': 'Aggiorna',
    'Search': 'Cerca',
    'Filter': 'Filtra',
    'Export': 'Esporta',
    'Import': 'Importa',
    'Download': 'Scarica',
    'Upload': 'Carica',
    'Copy': 'Copia',
    'Copied': 'Copiato',
    'Preview': 'Anteprima',
    'Open': 'Apri',
    'View': 'Visualizza',
    'Manage': 'Gestisci',
    'Configure': 'Configura',
    'Enable': 'Abilita',
    'Disable': 'Disabilita',
    'Enabled': 'Abilitato',
    'Disabled': 'Disabilitato',
    'Active': 'Attivo',
    'Inactive': 'Inattivo',
    'Available': 'Disponibile',
    'Unavailable': 'Non disponibile',
    'Visible': 'Visibile',
    'Hidden': 'Nascosto',
    'Required': 'Obbligatorio',
    'Optional': 'Opzionale',
    'Loading...': 'Caricamento...',
    'No data': 'Nessun dato',
    'No results found': 'Nessun risultato trovato',
    'Name': 'Nome',
    'Description': 'Descrizione',
    'Price': 'Prezzo',
    'Status': 'Stato',
    'Actions': 'Azioni',
    'Role': 'Ruolo',
    'Email': 'Email',
    'Phone': 'Telefono',
    'Address': 'Indirizzo',
    'Currency': 'Valuta',
    'Language': 'Lingua',
    'Theme': 'Tema',
    'General': 'Generale',
    'Settings': 'Impostazioni',
    'Categories': 'Categorie',
    'Items': 'Voci',
    'Menu Items': 'Voci di menu',
    'Save Settings': 'Salva impostazioni',
    'Save Changes': 'Salva modifiche',
    'Total Revenue': 'Entrate totali',
    'Total Orders': 'Ordini totali',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Operazioni Chef",
    'chef.kitchen_mode': "Modalità Cucina",
    'chef.exit_kitchen_mode': "Esci dalla modalità cucina",
    'chef.start_preparing': "Inizia preparazione",
    'chef.mark_ready': "Segna come pronto",
    'chef.finalize_order': "Completa l'ordine",
    'chef.nothing_to_track': "Nessun ordine da monitorare",
    'chef.preparing_time': "In preparazione: {time}",
    'chef.waiting_time': "In attesa: {time}",
    'chef.ready_time': "Pronto: {time}",
    'chef.table': "Tavolo",
    'chef.dine_in': "Al tavolo",
    'chef.delivery': "Consegna",
    'chef.pickup': "Asporto",
    'waiter.title': "Servizio di Sala",
    'waiter.table_calls': "Chiamate Tavoli",
    'waiter.ready_pickups': "Ordini Pronti",
    'waiter.active_tickets': "Ticket Attivi",
    'waiter.no_calls': "Nessuna chiamata dal tavolo in attesa",
    'waiter.nothing_pickup': "Nessun piatto pronto per il ritiro",
    'waiter.no_active_orders': "Nessun ordine attivo",
    'waiter.mark_attended': "Segna come servito",
    'waiter.ready_for_pickup': "Pronto per il servizio",
    'waiter.call_request': "Assistenza al Tavolo",
    'waiter.bill_request': "Conto Richiesto",
    'waiter.water_request': "Acqua Richiesta",
    'status.active': "Attivo",
    'status.pending': "In attesa",
    'status.preparing': "In preparazione",
    'status.ready': "Pronto",
    'status.completed': "Completato",
    'status.cancelled': "Annullato",
    'status.paid': "Pagato",
    'status.unpaid': "Non pagato",
  },
  pt: {
    'Save': 'Guardar',
    'Saved': 'Guardado',
    'Saving...': 'A guardar...',
    'Cancel': 'Cancelar',
    'Close': 'Fechar',
    'Create': 'Criar',
    'Add': 'Adicionar',
    'Edit': 'Editar',
    'Delete': 'Eliminar',
    'Remove': 'Remover',
    'Update': 'Atualizar',
    'Refresh': 'Atualizar',
    'Search': 'Pesquisar',
    'Filter': 'Filtrar',
    'Export': 'Exportar',
    'Import': 'Importar',
    'Download': 'Transferir',
    'Upload': 'Carregar',
    'Copy': 'Copiar',
    'Copied': 'Copiado',
    'Preview': 'Pré-visualizar',
    'Open': 'Abrir',
    'View': 'Ver',
    'Manage': 'Gerir',
    'Configure': 'Configurar',
    'Enable': 'Ativar',
    'Disable': 'Desativar',
    'Enabled': 'Ativado',
    'Disabled': 'Desativado',
    'Active': 'Ativo',
    'Inactive': 'Inativo',
    'Available': 'Disponível',
    'Unavailable': 'Indisponível',
    'Visible': 'Visível',
    'Hidden': 'Oculto',
    'Required': 'Obrigatório',
    'Optional': 'Opcional',
    'Loading...': 'A carregar...',
    'No data': 'Sem dados',
    'No results found': 'Nenhum resultado encontrado',
    'Name': 'Nome',
    'Description': 'Descrição',
    'Price': 'Preço',
    'Status': 'Estado',
    'Actions': 'Ações',
    'Role': 'Função',
    'Email': 'E-mail',
    'Phone': 'Telefone',
    'Address': 'Endereço',
    'Currency': 'Moeda',
    'Language': 'Idioma',
    'Theme': 'Tema',
    'General': 'Geral',
    'Settings': 'Definições',
    'Categories': 'Categorias',
    'Items': 'Itens',
    'Menu Items': 'Itens do menu',
    'Save Settings': 'Guardar definições',
    'Save Changes': 'Guardar alterações',
    'Total Revenue': 'Receita total',
    'Total Orders': 'Total de pedidos',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Operações do Chef",
    'chef.kitchen_mode': "Modo Cozinha",
    'chef.exit_kitchen_mode': "Sair do modo cozinha",
    'chef.start_preparing': "Iniciar preparo",
    'chef.mark_ready': "Marcar como pronto",
    'chef.finalize_order': "Finalizar pedido",
    'chef.nothing_to_track': "Nada para acompanhar ainda",
    'chef.preparing_time': "Preparando: {time}",
    'chef.waiting_time': "Aguardando: {time}",
    'chef.ready_time': "Pronto: {time}",
    'chef.table': "Mesa",
    'chef.dine_in': "No local",
    'chef.delivery': "Entrega",
    'chef.pickup': "Retirada",
    'waiter.title': "Serviço de Salão",
    'waiter.table_calls': "Chamadas de Mesa",
    'waiter.ready_pickups': "Pratos Prontos",
    'waiter.active_tickets': "Pedidos Ativos",
    'waiter.no_calls': "Nenhuma chamada de mesa pendente",
    'waiter.nothing_pickup': "Nada pronto para retirada ainda",
    'waiter.no_active_orders': "Nenhum pedido ativo",
    'waiter.mark_attended': "Marcar como atendido",
    'waiter.ready_for_pickup': "Pronto para servir",
    'waiter.call_request': "Ajuda na Mesa",
    'waiter.bill_request': "Conta Solicitada",
    'waiter.water_request': "Água Solicitada",
    'status.active': "Ativo",
    'status.pending': "Pendente",
    'status.preparing': "Em preparo",
    'status.ready': "Pronto",
    'status.completed': "Concluído",
    'status.cancelled': "Cancelado",
    'status.paid': "Pago",
    'status.unpaid': "Não pago",
  },
  ru: {
    'Save': 'Сохранить',
    'Saved': 'Сохранено',
    'Saving...': 'Сохранение...',
    'Cancel': 'Отмена',
    'Close': 'Закрыть',
    'Create': 'Создать',
    'Add': 'Добавить',
    'Edit': 'Редактировать',
    'Delete': 'Удалить',
    'Remove': 'Убрать',
    'Update': 'Обновить',
    'Refresh': 'Обновить',
    'Search': 'Поиск',
    'Filter': 'Фильтр',
    'Export': 'Экспорт',
    'Import': 'Импорт',
    'Download': 'Скачать',
    'Upload': 'Загрузить',
    'Copy': 'Копировать',
    'Copied': 'Скопировано',
    'Preview': 'Просмотр',
    'Open': 'Открыть',
    'View': 'Посмотреть',
    'Manage': 'Управлять',
    'Configure': 'Настроить',
    'Enable': 'Включить',
    'Disable': 'Отключить',
    'Enabled': 'Включено',
    'Disabled': 'Отключено',
    'Active': 'Активен',
    'Inactive': 'Неактивен',
    'Available': 'Доступно',
    'Unavailable': 'Недоступно',
    'Visible': 'Видимо',
    'Hidden': 'Скрыто',
    'Required': 'Обязательно',
    'Optional': 'Необязательно',
    'Loading...': 'Загрузка...',
    'No data': 'Нет данных',
    'No results found': 'Результатов не найдено',
    'Name': 'Название',
    'Description': 'Описание',
    'Price': 'Цена',
    'Status': 'Статус',
    'Actions': 'Действия',
    'Role': 'Роль',
    'Email': 'Эл. почта',
    'Phone': 'Телефон',
    'Address': 'Адрес',
    'Currency': 'Валюта',
    'Language': 'Язык',
    'Theme': 'Тема',
    'General': 'Общие',
    'Settings': 'Настройки',
    'Categories': 'Категории',
    'Items': 'Позиции',
    'Menu Items': 'Позиции меню',
    'Save Settings': 'Сохранить настройки',
    'Save Changes': 'Сохранить изменения',
    'Total Revenue': 'Общая выручка',
    'Total Orders': 'Всего заказов',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Работа шеф-повара",
    'chef.kitchen_mode': "Режим кухни",
    'chef.exit_kitchen_mode': "Выйти из режима кухни",
    'chef.start_preparing': "Начать приготовление",
    'chef.mark_ready': "Готово к выдаче",
    'chef.finalize_order': "Завершить заказ",
    'chef.nothing_to_track': "Пока нет заказов",
    'chef.preparing_time': "Готовится: {time}",
    'chef.waiting_time': "В ожидании: {time}",
    'chef.ready_time': "Готово: {time}",
    'chef.table': "Стол",
    'chef.dine_in': "В зале",
    'chef.delivery': "Доставка",
    'chef.pickup': "Самовывоз",
    'waiter.title': "Обслуживание зала",
    'waiter.table_calls': "Вызовы со столов",
    'waiter.ready_pickups': "Готовые блюда",
    'waiter.active_tickets': "Активные заказы",
    'waiter.no_calls': "Нет ожидающих вызовов",
    'waiter.nothing_pickup': "Пока нечего забирать",
    'waiter.no_active_orders': "Нет активных заказов",
    'waiter.mark_attended': "Отметить как выполненный",
    'waiter.ready_for_pickup': "Готово к подаче",
    'waiter.call_request': "Помощь за столом",
    'waiter.bill_request': "Запрошен счет",
    'waiter.water_request': "Запрошена вода",
    'status.active': "Активный",
    'status.pending': "В ожидании",
    'status.preparing': "Готовится",
    'status.ready': "Готово",
    'status.completed': "Завершено",
    'status.cancelled': "Отменено",
    'status.paid': "Оплачено",
    'status.unpaid': "Не оплачено",
  },
  zh: {
    'Save': '保存',
    'Saved': '已保存',
    'Saving...': '正在保存...',
    'Cancel': '取消',
    'Close': '关闭',
    'Create': '创建',
    'Add': '添加',
    'Edit': '编辑',
    'Delete': '删除',
    'Remove': '移除',
    'Update': '更新',
    'Refresh': '刷新',
    'Search': '搜索',
    'Filter': '筛选',
    'Export': '导出',
    'Import': '导入',
    'Download': '下载',
    'Upload': '上传',
    'Copy': '复制',
    'Copied': '已复制',
    'Preview': '预览',
    'Open': '打开',
    'View': '查看',
    'Manage': '管理',
    'Configure': '配置',
    'Enable': '启用',
    'Disable': '禁用',
    'Enabled': '已启用',
    'Disabled': '已禁用',
    'Active': '活跃',
    'Inactive': '未激活',
    'Available': '在售',
    'Unavailable': '售罄',
    'Visible': '显示',
    'Hidden': '隐藏',
    'Required': '必填',
    'Optional': '选填',
    'Loading...': '加载中...',
    'No data': '暂无数据',
    'No results found': '未找到相关结果',
    'Name': '名称',
    'Description': '描述',
    'Price': '价格',
    'Status': '状态',
    'Actions': '操作',
    'Role': '角色',
    'Email': '邮箱',
    'Phone': '电话',
    'Address': '地址',
    'Currency': '货币',
    'Language': '语言',
    'Theme': '主题',
    'General': '常规',
    'Settings': '设置',
    'Categories': '分类',
    'Items': '商品',
    'Menu Items': '菜单商品',
    'Save Settings': '保存设置',
    'Save Changes': '保存修改',
    'Total Revenue': '总营收',
    'Total Orders': '总订单量',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "后厨运营",
    'chef.kitchen_mode': "后厨模式",
    'chef.exit_kitchen_mode': "退出后厨模式",
    'chef.start_preparing': "开始制作",
    'chef.mark_ready': "标记为完成",
    'chef.finalize_order': "完成订单",
    'chef.nothing_to_track': "暂无待处理订单",
    'chef.preparing_time': "制作中：{time}",
    'chef.waiting_time': "等待中：{time}",
    'chef.ready_time': "已就绪：{time}",
    'chef.table': "餐桌",
    'chef.dine_in': "堂食",
    'chef.delivery': "外卖配送",
    'chef.pickup': "自取",
    'waiter.title': "楼面服务",
    'waiter.table_calls': "呼叫服务",
    'waiter.ready_pickups': "出餐就绪",
    'waiter.active_tickets': "进行中订单",
    'waiter.no_calls': "暂无待处理服务呼叫",
    'waiter.nothing_pickup': "暂无出餐",
    'waiter.no_active_orders': "暂无活跃订单",
    'waiter.mark_attended': "标记为已服务",
    'waiter.ready_for_pickup': "可传菜",
    'waiter.call_request': "呼叫服务员",
    'waiter.bill_request': "请求结账",
    'waiter.water_request': "加水服务",
    'status.active': "进行中",
    'status.pending': "待处理",
    'status.preparing': "制作中",
    'status.ready': "已出餐",
    'status.completed': "已完成",
    'status.cancelled': "已取消",
    'status.paid': "已支付",
    'status.unpaid': "未支付",
  },
  ja: {
    'Save': '保存',
    'Saved': '保存済み',
    'Saving...': '保存中...',
    'Cancel': 'キャンセル',
    'Close': '閉じる',
    'Create': '作成',
    'Add': '追加',
    'Edit': '編集',
    'Delete': '削除',
    'Remove': '除外',
    'Update': '更新',
    'Refresh': '再読み込み',
    'Search': '検索',
    'Filter': '絞り込み',
    'Export': 'エクスポート',
    'Import': 'インポート',
    'Download': 'ダウンロード',
    'Upload': 'アップロード',
    'Copy': 'コピー',
    'Copied': 'コピー完了',
    'Preview': 'プレビュー',
    'Open': '開く',
    'View': '表示',
    'Manage': '管理',
    'Configure': '設定',
    'Enable': '有効化',
    'Disable': '無効化',
    'Enabled': '有効',
    'Disabled': '無効',
    'Active': 'アクティブ',
    'Inactive': '非アクティブ',
    'Available': '提供中',
    'Unavailable': '売り切れ',
    'Visible': '表示',
    'Hidden': '非表示',
    'Required': '必須',
    'Optional': '任意',
    'Loading...': '読み込み中...',
    'No data': 'データがありません',
    'No results found': '該当する結果がありません',
    'Name': '名前',
    'Description': '説明',
    'Price': '価格',
    'Status': 'ステータス',
    'Actions': '操作',
    'Role': '権限',
    'Email': 'メールアドレス',
    'Phone': '電話番号',
    'Address': '住所',
    'Currency': '通貨',
    'Language': '言語',
    'Theme': 'テーマ',
    'General': '一般',
    'Settings': '設定',
    'Categories': 'カテゴリー',
    'Items': '品目',
    'Menu Items': 'メニュー品目',
    'Save Settings': '設定を保存',
    'Save Changes': '変更を保存',
    'Total Revenue': '総売上',
    'Total Orders': '総注文数',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "調理場オペレーション",
    'chef.kitchen_mode': "キッチンモード",
    'chef.exit_kitchen_mode': "キッチンモードを終了",
    'chef.start_preparing': "調理開始",
    'chef.mark_ready': "完成にする",
    'chef.finalize_order': "注文を完了",
    'chef.nothing_to_track': "まだ注文はありません",
    'chef.preparing_time': "調理中: {time}",
    'chef.waiting_time': "待機中: {time}",
    'chef.ready_time': "準備完了: {time}",
    'chef.table': "テーブル",
    'chef.dine_in': "店内飲食",
    'chef.delivery': "配達",
    'chef.pickup': "テイクアウト",
    'waiter.title': "フロアサービス",
    'waiter.table_calls': "テーブル呼び出し",
    'waiter.ready_pickups': "配膳待ち",
    'waiter.active_tickets': "進行中の注文",
    'waiter.no_calls': "待機中の呼び出しはありません",
    'waiter.nothing_pickup': "配膳待ちの料理はありません",
    'waiter.no_active_orders': "アクティブな注文はありません",
    'waiter.mark_attended': "対応済みにする",
    'waiter.ready_for_pickup': "配膳可能",
    'waiter.call_request': "スタッフ呼び出し",
    'waiter.bill_request': "お会計のお願い",
    'waiter.water_request': "お水のお願い",
    'status.active': "アクティブ",
    'status.pending': "保留中",
    'status.preparing': "調理中",
    'status.ready': "完了",
    'status.completed': "完了済み",
    'status.cancelled': "キャンセル",
    'status.paid': "支払済み",
    'status.unpaid': "未払い",
  },
  nl: {
    'Save': 'Opslaan',
    'Saved': 'Opgeslagen',
    'Saving...': 'Bezig met opslaan...',
    'Cancel': 'Annuleren',
    'Close': 'Sluiten',
    'Create': 'Aanmaken',
    'Add': 'Toevoegen',
    'Edit': 'Bewerken',
    'Delete': 'Verwijderen',
    'Remove': 'Verwijderen',
    'Update': 'Bijwerken',
    'Refresh': 'Vernieuwen',
    'Search': 'Zoeken',
    'Filter': 'Filteren',
    'Export': 'Exporteren',
    'Import': 'Importeren',
    'Download': 'Downloaden',
    'Upload': 'Uploaden',
    'Copy': 'Kopiëren',
    'Copied': 'Gekopieerd',
    'Preview': 'Voorbeeld',
    'Open': 'Openen',
    'View': 'Bekijken',
    'Manage': 'Beheren',
    'Configure': 'Configureren',
    'Enable': 'Inschakelen',
    'Disable': 'Uitschakelen',
    'Enabled': 'Ingeschakeld',
    'Disabled': 'Uitgeschakeld',
    'Active': 'Actief',
    'Inactive': 'Inactief',
    'Available': 'Beschikbaar',
    'Unavailable': 'Niet beschikbaar',
    'Visible': 'Zichtbaar',
    'Hidden': 'Verborgen',
    'Required': 'Verplicht',
    'Optional': 'Optioneel',
    'Loading...': 'Laden...',
    'No data': 'Geen gegevens',
    'No results found': 'Geen resultaten gevonden',
    'Name': 'Naam',
    'Description': 'Beschrijving',
    'Price': 'Prijs',
    'Status': 'Status',
    'Actions': 'Acties',
    'Role': 'Rol',
    'Email': 'E-mail',
    'Phone': 'Telefoon',
    'Address': 'Adres',
    'Currency': 'Valuta',
    'Language': 'Taal',
    'Theme': 'Thema',
    'General': 'Algemeen',
    'Settings': 'Instellingen',
    'Categories': 'Categorieën',
    'Items': 'Items',
    'Menu Items': 'Menu-items',
    'Save Settings': 'Instellingen opslaan',
    'Save Changes': 'Wijzigingen opslaan',
    'Total Revenue': 'Totale omzet',
    'Total Orders': 'Totaal aantal bestellingen',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Chef Operaties",
    'chef.kitchen_mode': "Keukenmodus",
    'chef.exit_kitchen_mode': "Keukenmodus verlaten",
    'chef.start_preparing': "Start bereiding",
    'chef.mark_ready': "Markeer als gereed",
    'chef.finalize_order': "Bestelling afronden",
    'chef.nothing_to_track': "Nog niets om te volgen",
    'chef.preparing_time': "In bereiding: {time}",
    'chef.waiting_time': "Wachtend: {time}",
    'chef.ready_time': "Gereed: {time}",
    'chef.table': "Tafel",
    'chef.dine_in': "Ter plaatse",
    'chef.delivery': "Bezorging",
    'chef.pickup': "Afhalen",
    'waiter.title': "Zaalbediening",
    'waiter.table_calls': "Tafeloproepen",
    'waiter.ready_pickups': "Klaar voor Serveren",
    'waiter.active_tickets': "Actieve Tickets",
    'waiter.no_calls': "Geen openstaande tafeloproepen",
    'waiter.nothing_pickup': "Nog niets om op te halen",
    'waiter.no_active_orders': "Geen actieve bestellingen",
    'waiter.mark_attended': "Markeer als afgehandeld",
    'waiter.ready_for_pickup': "Klaar om uit te serveren",
    'waiter.call_request': "Tafelassistentie",
    'waiter.bill_request': "Rekening gevraagd",
    'waiter.water_request': "Water gevraagd",
    'status.active': "Actief",
    'status.pending': "In behandeling",
    'status.preparing': "In bereiding",
    'status.ready': "Gereed",
    'status.completed': "Afgerond",
    'status.cancelled': "Geannuleerd",
    'status.paid': "Betaald",
    'status.unpaid': "Niet betaald",
  },
  tr: {
    'Save': 'Kaydet',
    'Saved': 'Kaydedildi',
    'Saving...': 'Kaydediliyor...',
    'Cancel': 'İptal',
    'Close': 'Kapat',
    'Create': 'Oluştur',
    'Add': 'Ekle',
    'Edit': 'Düzenle',
    'Delete': 'Sil',
    'Remove': 'Kaldır',
    'Update': 'Güncelle',
    'Refresh': 'Yenile',
    'Search': 'Ara',
    'Filter': 'Filtrele',
    'Export': 'Dışa Aktar',
    'Import': 'İçe Aktar',
    'Download': 'İndir',
    'Upload': 'Yükle',
    'Copy': 'Kopyala',
    'Copied': 'Kopyalandı',
    'Preview': 'Önizleme',
    'Open': 'Aç',
    'View': 'Görüntüle',
    'Manage': 'Yönet',
    'Configure': 'Yapılandır',
    'Enable': 'Etkinleştir',
    'Disable': 'Devre Dışı Bırak',
    'Enabled': 'Etkin',
    'Disabled': 'Devre Dışı',
    'Active': 'Aktif',
    'Inactive': 'Pasif',
    'Available': 'Mevcut',
    'Unavailable': 'Mevcut Değil',
    'Visible': 'Görünür',
    'Hidden': 'Gizli',
    'Required': 'Zorunlu',
    'Optional': 'İsteğe Bağlı',
    'Loading...': 'Yükleniyor...',
    'No data': 'Veri yok',
    'No results found': 'Sonuç bulunamadı',
    'Name': 'Ad',
    'Description': 'Açıklama',
    'Price': 'Fiyat',
    'Status': 'Durum',
    'Actions': 'Eylemler',
    'Role': 'Rol',
    'Email': 'E-posta',
    'Phone': 'Telefon',
    'Address': 'Adres',
    'Currency': 'Para Birimi',
    'Language': 'Dil',
    'Theme': 'Tema',
    'General': 'Genel',
    'Settings': 'Ayarlar',
    'Categories': 'Kategoriler',
    'Items': 'Öğeler',
    'Menu Items': 'Menü Öğeleri',
    'Save Settings': 'Ayarları Kaydet',
    'Save Changes': 'Değişiklikleri Kaydet',
    'Total Revenue': 'Toplam Gelir',
    'Total Orders': 'Toplam Sipariş',
    // Kitchen (Chef/KDS) & Waiter Console Strings
    'chef.operations': "Şef Operasyonları",
    'chef.kitchen_mode': "Mutfak Modu",
    'chef.exit_kitchen_mode': "Mutfak modundan çık",
    'chef.start_preparing': "Hazırlamaya Başla",
    'chef.mark_ready': "Hazır Olarak İşaretle",
    'chef.finalize_order': "Siparişi Tamamla",
    'chef.nothing_to_track': "Henüz takip edilecek bir şey yok",
    'chef.preparing_time': "Hazırlanıyor: {time}",
    'chef.waiting_time': "Bekliyor: {time}",
    'chef.ready_time': "Hazır: {time}",
    'chef.table': "Masa",
    'chef.dine_in': "Restoranda",
    'chef.delivery': "Teslimat",
    'chef.pickup': "Gel-Al",
    'waiter.title': "Salon Servisi",
    'waiter.table_calls': "Masa Çağrıları",
    'waiter.ready_pickups': "Servise Hazır",
    'waiter.active_tickets': "Aktif Siparişler",
    'waiter.no_calls': "Bekleyen masa çağrısı yok",
    'waiter.nothing_pickup': "Henüz alınacak bir şey yok",
    'waiter.no_active_orders': "Aktif sipariş yok",
    'waiter.mark_attended': "İlgilenildi Olarak İşaretle",
    'waiter.ready_for_pickup': "Servise hazır",
    'waiter.call_request': "Masa Yardımı",
    'waiter.bill_request': "Hesap İstendi",
    'waiter.water_request': "Su İstendi",
    'status.active': "Aktif",
    'status.pending': "Beklemede",
    'status.preparing': "Hazırlanıyor",
    'status.ready': "Hazır",
    'status.completed': "Tamamlandı",
    'status.cancelled': "İptal edildi",
    'status.paid': "Ödendi",
    'status.unpaid': "Ödenmedi",
  },
};

const preserveOuterWhitespace = (original: string, translated: string) => {
  const leading = original.match(/^\s*/)?.[0] || '';
  const trailing = original.match(/\s*$/)?.[0] || '';
  return `${leading}${translated}${trailing}`;
};

import { adminPhraseTranslations as dedicatedAdminTranslations, getCanonicalEnglishPhrase } from './adminTranslations';

export const translateAdminPhrase = (language: Language, text: string): string => {
  const trimmed = text.trim();
  if (!trimmed) return text;

  // Resolve canonical English phrase in case text was previously translated
  const canonicalEn = getCanonicalEnglishPhrase(trimmed);
  if (language === 'en') {
    return preserveOuterWhitespace(text, canonicalEn);
  }

  // 1. Direct exact dictionary match
  const dictionary = { ...(adminPhraseTranslations[language] || {}), ...(dedicatedAdminTranslations[language] || {}) };
  const exact = dictionary[canonicalEn] || dictionary[trimmed] || translations[language]?.[canonicalEn] || translations[language]?.[trimmed];
  if (exact) return preserveOuterWhitespace(text, exact);

  // 2. Case-insensitive exact match
  const lower = canonicalEn.toLowerCase();
  const caseInsensitiveKey = Object.keys(dictionary).find(key => key.toLowerCase() === lower);
  if (caseInsensitiveKey && dictionary[caseInsensitiveKey]) {
    return preserveOuterWhitespace(text, dictionary[caseInsensitiveKey]);
  }

  const staticCaseInsensitiveKey = Object.keys(translations[language] || {}).find(key => key.toLowerCase() === lower);
  if (staticCaseInsensitiveKey && translations[language]?.[staticCaseInsensitiveKey]) {
    return preserveOuterWhitespace(text, translations[language][staticCaseInsensitiveKey]);
  }

  // 3. Structured separator matching (e.g. "Save / Cancel" or "Items • Categories")
  const separators = [' / ', ' | ', ' • ', ' · ', ' - '];
  for (const separator of separators) {
    if (!trimmed.includes(separator)) continue;
    const parts = trimmed.split(separator);
    const translatedParts = parts.map(part => {
      const partTrim = part.trim();
      const canonPart = getCanonicalEnglishPhrase(partTrim);
      const match = dictionary[canonPart] || dictionary[partTrim] || translations[language]?.[canonPart] || translations[language]?.[partTrim];
      if (match) return preserveOuterWhitespace(part, match);

      const prefixMatch = partTrim.match(/^([A-Za-z]+)\s+([\d:.]+(?:\s*[AP]M)?)$/i);
      if (prefixMatch) {
        const prefixWord = getCanonicalEnglishPhrase(prefixMatch[1]);
        const transPrefix = dictionary[prefixWord] || translations[language]?.[prefixWord];
        if (transPrefix) {
          return preserveOuterWhitespace(part, `${transPrefix} ${prefixMatch[2]}`);
        }
      }

      return part;
    });
    if (translatedParts.some((part, index) => part !== parts[index])) {
      return preserveOuterWhitespace(text, translatedParts.join(separator));
    }
  }

  // 4. Dynamic compound number phrases (e.g. "60 reviews", "reviews 60", "3 orders", "orders 3", "0% of orders", "of orders 0%", "10% rate")
  const numThenWord = canonicalEn.match(/^(\d+(?:\.\d+)?%?)\s+(.+)$/);
  const wordThenNum = canonicalEn.match(/^(.+)\s+(\d+(?:\.\d+)?%?)$/);
  const matched = numThenWord || wordThenNum;

  if (matched) {
    const num = numThenWord ? matched[1] : matched[2];
    const rawWord = (numThenWord ? matched[2] : matched[1]).trim();
    const canonWord = getCanonicalEnglishPhrase(rawWord);
    const transWord = dictionary[canonWord] || dictionary[rawWord] || translations[language]?.[canonWord] || translations[language]?.[rawWord];

    if (transWord) {
      const n = parseInt(num, 10);
      if (!Number.isNaN(n) && (/days?(\s+ago)?/i.test(canonWord) || /hours?(\s+ago)?/i.test(canonWord) || /minutes?(\s+ago)?/i.test(canonWord))) {
        const fullKey = `${n} ${canonWord.toLowerCase()}`;
        const exactTime = dictionary[fullKey] || dictionary[`${n} days ago`] || dictionary[`${n} hours ago`];
        if (exactTime) return preserveOuterWhitespace(text, exactTime);

        const isDay = /day/i.test(canonWord);
        const isHour = /hour/i.test(canonWord);
        const isMin = /min/i.test(canonWord);

        const timeFormatters: Record<Language, string> = {
          ar: isDay
            ? (n === 1 ? 'منذ يوم واحد' : n === 2 ? 'منذ يومين' : n <= 10 ? `منذ ${n} أيام` : `منذ ${n} يوماً`)
            : isHour
            ? (n === 1 ? 'منذ ساعة واحدة' : n === 2 ? 'منذ ساعتين' : n <= 10 ? `منذ ${n} ساعات` : `منذ ${n} ساعة`)
            : (n === 1 ? 'منذ دقيقة واحدة' : n === 2 ? 'منذ دقيقتين' : n <= 10 ? `منذ ${n} دقائق` : `منذ ${n} دقيقة`),
          fr: isDay ? `il y a ${n} jour${n > 1 ? 's' : ''}` : isHour ? `il y a ${n} heure${n > 1 ? 's' : ''}` : `il y a ${n} minute${n > 1 ? 's' : ''}`,
          es: isDay ? `hace ${n} día${n > 1 ? 's' : ''}` : isHour ? `hace ${n} hora${n > 1 ? 's' : ''}` : `hace ${n} minuto${n > 1 ? 's' : ''}`,
          de: isDay ? `vor ${n} Tag${n > 1 ? 'en' : ''}` : isHour ? `vor ${n} Stunde${n > 1 ? 'n' : ''}` : `vor ${n} Minute${n > 1 ? 'n' : ''}`,
          it: isDay ? `${n} giorn${n > 1 ? 'i' : 'o'} fa` : isHour ? `${n} ore fa` : `${n} minuti fa`,
          pt: isDay ? `há ${n} dia${n > 1 ? 's' : ''}` : isHour ? `há ${n} hora${n > 1 ? 's' : ''}` : `há ${n} minuto${n > 1 ? 's' : ''}`,
          ru: isDay ? `${n} дн. назад` : isHour ? `${n} ч. назад` : `${n} мин. назад`,
          zh: isDay ? `${n}天前` : isHour ? `${n}小时前` : `${n}分钟前`,
          ja: isDay ? `${n}日前` : isHour ? `${n}時間前` : `${n}分前`,
          nl: isDay ? `${n} dag${n > 1 ? 'en' : ''} geleden` : isHour ? `${n} uur geleden` : `${n} minuten geleden`,
          tr: isDay ? `${n} gün önce` : isHour ? `${n} saat önce` : `${n} dakika önce`,
          ko: isDay ? `${n}일 전` : isHour ? `${n}시간 전` : `${n}분 전`,
          el: isDay ? `πριν από ${n} ημέρες` : isHour ? `πριν από ${n} ώρες` : `πριν από ${n} λεπτά`,
          hi: isDay ? `${n} दिन पहले` : isHour ? `${n} घंटे पहले` : `${n} मिनट पहले`,
          th: isDay ? `${n} วันที่แล้ว` : isHour ? `${n} ชั่วโมงที่แล้ว` : `${n} นาทีที่แล้ว`,
          id: isDay ? `${n} hari yang lalu` : isHour ? `${n} jam yang lalu` : `${n} menit yang lalu`,
          pl: isDay ? `${n} dni temu` : isHour ? `${n} godzin temu` : `${n} minut temu`,
          sv: isDay ? `för ${n} dagar sedan` : isHour ? `för ${n} timmar sedan` : `för ${n} minuter sedan`,
          en: isDay ? `${n} day${n > 1 ? 's' : ''} ago` : isHour ? `${n} hour${n > 1 ? 's' : ''} ago` : `${n} minute${n > 1 ? 's' : ''} ago`,
        };

        if (timeFormatters[language]) {
          return preserveOuterWhitespace(text, timeFormatters[language]);
        }
      }
      return preserveOuterWhitespace(text, `${num} ${transWord}`);
    }
  }

  // Return original text safely if no exact match exists
  return text;
};
